/**
 * Lift2Fit — typed API client for the post-login application.
 *
 * The wire contract is docs/architecture/app-backend-contract.md, extended by the
 * client direction to make every dashboard track-aware (strength, hypertrophy,
 * conditioning, performance, wellness, general).
 *
 * RULES
 *  - Nothing in this module ever throws into the UI. Every call resolves with a
 *    `source` flag; when the API is unreachable the call falls back to the
 *    built-in demo dataset and the UI says so honestly.
 *  - The canonical unit on the wire is kilograms. Display conversion is a
 *    presentation concern (lib/units.ts).
 */
import type { UnitSystem } from '@lift2fit/shared-types';
import {
  coachPersona,
  demoAuthResponse,
  demoBlocksCurrent,
  demoBlocksHistory,
  demoCreateGoal,
  demoDashboard,
  demoDeleteGoal,
  demoExercises,
  demoInviteTrainee,
  demoLogSet,
  demoMe,
  demoNextSession,
  demoProgramme,
  demoGeneratedPlan,
  demoRecentSets,
  demoSaveSchedule,
  demoSchedule,
  demoSessions,
  demoSkipScheduleEntry,
  demoStats,
  demoStopSchedule,
  demoClearSchedule,
  demoTrainees,
  demoUpdateGoal,
  trackMeta,
} from '../app/demoData';
import {
  demoActivateProgramme,
  demoActiveSessionPayload,
  demoAddProgrammeDay,
  demoAddProgrammeExercises,
  demoAddSessionExercise,
  demoCompleteSession,
  demoCreateProgramme,
  demoCreateProgrammeWarmup,
  demoDeleteProgrammeDay,
  demoDeleteProgrammeExercise,
  demoDeleteProgrammeWarmup,
  demoDeleteSessionSet,
  demoGroupProgrammeExercises,
  demoLogSessionSet,
  demoPatchProgramme,
  demoPatchProgrammeDay,
  demoPatchProgrammeExercise,
  demoPatchSessionSet,
  demoProgrammeOverview,
  demoProgrammePlans,
  demoReorderProgramme,
  demoStartSession,
} from '../app/demoProgramme';

/* ---------------------------------------------------------------------------
   Contract types
   ------------------------------------------------------------------------ */

export type Role = 'lifter' | 'trainer' | 'coach' | 'admin';

/**
 * The fields a lifter may share with a human coach. These are the exact
 * identifiers `trainee_links.shared_fields` accepts on the wire — do not rename
 * them.
 */
export type SharedField = 'sessions' | 'sets' | 'bodyweight' | 'notes' | 'pain';

/**
 * The six training intents the product coaches for. These identifiers are
 * shared with the backend and the plan generator — do not rename them.
 */
export type TrainingTrack =
  | 'strength'
  | 'hypertrophy'
  | 'conditioning'
  | 'performance'
  | 'wellness'
  | 'general';

export interface User {
  id: string;
  email: string;
  displayName: string;
  roles: Role[]; // always contains 'lifter'
  unitSystem: UnitSystem;
  tier: 'basic' | 'premium' | 'plan_once';
  timezone: string;
  bodyweightKg: number | null;
  heightCm: number | null;
  trainingGoal: string | null;
  experience: 'beginner' | 'intermediate' | 'advanced' | null;
  injuryFlags: string[];
  weeklyTarget: number;
  /** the track this account is currently training for */
  track: TrainingTrack;
  trackLabel: string;
  /** `users.coach_plan` — the account's own CRM tier. */
  coachPlan: 'free' | 'coach' | 'gym' | 'gym_plus' | 'scale';
  /**
   * Derived entitlement from the API: the lifter interface is included for a
   * non-coach, and for a coach from the paid plan upward. A free-tier coach's
   * profile hides the lifter entry and explains the upgrade path.
   */
  lifterInterface: boolean;
  /** 0038 — this account is on the platform support team (not the bot). */
  supportStaff: boolean;
  /**
   * 0030: when this account is an ACTIVE internal coach, the gym it works for.
   * The CRM hides money surfaces when this is set and the account has no own
   * paying clients.
   */
  internalCoachOf: { coachId: string; displayName: string } | null;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
  inviteCode?: string;
  /**
   * The account the signer is deliberately creating. Omitted/`lifter` is the
   * tracker (the default, unchanged); `coach` is the CRM's front door and
   * creates a coaching business on its own free plan. The API refuses anything
   * else with 422 — there is no value that mints `admin`.
   */
  accountType?: 'lifter' | 'coach';
  /**
   * 0024 — the signup consent. Both signup forms send `true` only when the
   * unticked checkbox has been ticked; the API refuses a signup without it
   * (422 `policy_consent_required`) before creating anything.
   */
  acceptPolicy?: boolean;
  /** The policy version the form displayed; must match the API's version. */
  policyVersion?: string;
}

/** `GET /me/consents` — one recorded acceptance (0024). */
export interface PolicyConsent {
  id: string;
  policyVersion: string;
  documents: string[];
  acceptedAt: string;
  userAgent: string | null;
  ipAddress: string | null;
}

export interface PolicyConsentList {
  data: PolicyConsent[];
  /** The version the API is currently serving, so the UI can flag a stale record. */
  currentVersion: string;
  effectiveDate: string;
  documents: string[];
}

/** RFC 9457 problem+json, exactly as the contract prints it. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
}

export interface ApiResult<T> {
  data: T;
  source: 'api' | 'demo';
  problem?: Problem;
  /** A set write that never reached the API is queued for an idempotent replay. */
  queued?: boolean;
}

/* ------------------------------- dashboard ------------------------------ */

export interface WeekDay {
  date: string;
  state: 'done' | 'today' | 'rest' | 'missed' | 'upcoming';
  label: string | null;
  sessionId: string | null;
}

export interface Week {
  weekStart: string;
  target: number;
  completed: number;
  days: WeekDay[];
}

export interface StreakSummary {
  currentWeeks: number;
  longestWeeks: number;
  qualifiesThisWeek: boolean;
}

/**
 * Per-track aggregates. Every field is optional because a wellness client has
 * no tonnage and a strength client has no interval minutes — the UI renders the
 * labels from `metricsFocus` and reads whatever the track actually provides.
 */
export interface TrackMetrics {
  coachPersona: string;
  // strength / hypertrophy
  bestE1rmKg: number | null;
  topSetLabel: string | null;
  volumeKg7: number | null;
  volumeChangePct: number | null;
  sets7: number | null;
  prs7: number | null;
  setsPerMuscle: number | null;
  bodyweightKg: number | null;
  // conditioning (indoor: ergs, sleds, carries, circuits)
  intervalMinutes: number | null;
  workRestRatio: string | null;
  ergMetres: number | null;
  avgRpe: number | null;
  // performance (in-gym: jumps, throws, plyos, sled accelerations)
  powerMarkers: number | null;
  jumpHeightCm: number | null;
  readinessPct: number | null;
  // wellness & recovery
  mobilitySessions: number | null;
  painScore: number | null;
  consistencyPct: number | null;
  // shared
  sessions7: number;
  /** how the client says they feel — wellness leads with this */
  feel: string | null;
}

export interface TrendPoint {
  weekStart: string;
  volumeKg?: number | null;
  sets?: number | null;
  prs?: number | null;
  intervalMinutes?: number | null;
  mobilitySessions?: number | null;
  sessions?: number | null;
  painScore?: number | null;
}

export interface PersonalRecordEntry {
  exerciseName: string;
  weightKg: number;
  reps: number;
  estimated1rmKg: number;
  achievedAt: string;
}

/** A track-appropriate recent item (an interval block, a check-in, a marker). */
export interface Highlight {
  label: string;
  value: string;
  detail?: string | null;
  at: string;
}

export interface AlertEntry {
  code: string;
  severity: 'info' | 'warning' | 'danger';
  message: string;
}

export interface LifterDashboard {
  role: 'lifter';
  track: TrainingTrack;
  trackLabel: string;
  metricsFocus: string[];
  trackMetrics: TrackMetrics;
  greeting: { displayName: string; localDate: string };
  streak: StreakSummary;
  week: Week;
  nextSession: {
    sessionId: string;
    dayLabel: string;
    focus: string[];
    exercises: number;
    sets: number;
    lastPerformed: string | null;
  } | null;
  totals: {
    volumeKg7: number;
    volumeChangePct: number | null;
    sets7: number;
    prs7: number;
    avgRpe7: number | null;
    compliancePct7: number | null;
  };
  trend: TrendPoint[];
  personalRecords: PersonalRecordEntry[];
  /** when present, replaces the PR list with a track-appropriate one */
  highlights?: Highlight[];
  coach: {
    hasPremium: boolean;
    latestReportAt: string | null;
    verdict: string | null;
    headline: string | null;
    changes: number;
    persona: string;
  };
  alerts: AlertEntry[];
}

/* ---------------------------------------------------------------------------
   The lifter's session schedule (`GET /me/next-session`, `/me/schedule`).
   The "when" layer over the undated programme; separate from
   `LifterDashboard.nextSession`, which answers "what to train next".
   ------------------------------------------------------------------------ */

export type NextSessionKind = 'booked' | 'scheduled' | 'none';

/** Who owns the block the next session belongs to (feature 0023). */
export type NextSessionManager = 'coach' | 'self';

export interface NextSession {
  kind: NextSessionKind;
  /** Day-boundary instant for a day-only entry — always read with `hasTime`. */
  startsAt: string | null;
  hasTime: boolean;
  dayLabel: string | null;
  planName: string | null;
  source: 'booked' | 'schedule' | 'none';
  /**
   * `'coach'` when the block is coach-owned: its "when" is the coach's
   * calendar, so the card offers booking rather than a weekday setter.
   * `'self'` (or absent, for an older/demo payload) keeps the 0022 behaviour.
   */
  managedBy?: NextSessionManager | null;
  coachName?: string;
  packName?: string;
  bookingId?: string;
  scheduleId?: string;
  /** The programme day this session maps to, when the block has days. */
  planId?: string;
  planDayId?: string;
  planDayIndex?: number;
  planDayLabel?: string;
  planDayFocus?: string[];
  /**
   * True when the programme day came from the least-recently-performed
   * rotation (a booked slot). The booked date is authoritative; the day is a
   * best guess, so the card says so.
   */
  planDayDerived?: boolean;
}

export interface ScheduleEntry {
  entryId: string;
  planDayId: string;
  planId: string;
  planName: string;
  coachOwned: boolean;
  dayIndex: number;
  dayLabel: string;
  focus: string[];
  weekday: number;
  startMinute: number | null;
  repeatWeekly: boolean;
  repeatUntil: string | null;
  stoppedAt: string | null;
  nextStartsAt: string | null;
}

export interface ScheduleDay {
  planDayId: string;
  planId: string;
  planName: string;
  coachOwned: boolean;
  dayIndex: number;
  label: string;
  focus: string[];
  entryId: string | null;
  weekday: number | null;
  startMinute: number | null;
}

export interface LifterSchedule {
  timezone: string;
  active: boolean;
  repeatUntil: string | null;
  entries: ScheduleEntry[];
  days: ScheduleDay[];
}

export interface ScheduleInputDay {
  planDayId: string;
  weekday: number;
  startMinute: number | null;
}

export interface ScheduleInput {
  days: ScheduleInputDay[];
  repeatUntil: string | null;
}

export interface Trainee {
  userId: string;
  displayName: string;
  email: string;
  status: 'invited' | 'active' | 'revoked';
  linkedAt: string | null;
  lastSessionAt: string | null;
  sessionsLast7: number;
  compliancePct: number | null;
  currentStreakWeeks: number;
  flags: string[];
  sharedFields: Array<'sessions' | 'sets' | 'bodyweight' | 'notes' | 'pain'>;
  track: TrainingTrack;
  trackLabel: string;
  coachPersona: string;
  /** the trainer who owns the link — coaches see every trainee across trainers */
  trainerName?: string;
  /** a one-line, track-aware summary of what they are doing */
  focus?: string;
  /**
   * The client's active block (0016), derived from the plan's start + weeks.
   * Optional so older demo fixtures that predate the field still type-check.
   */
  blockName?: string | null;
  reviewDue?: boolean;
  blockEndsOn?: string | null;
  daysRemaining?: number | null;
  weekOf?: number | null;
  weeks?: number | null;
  /**
   * 0030: who manages this client. `self` = you; `internal` = another internal
   * coach on your team manages them; `gym` = you are an internal coach and the
   * gym owner manages them. The Clients list and client page label each row.
   */
  managedBy?: ManagedBy | null;
}

/** The manager label for a client row (0030). */
export interface ManagedBy {
  kind: 'self' | 'internal' | 'gym';
  coachId: string;
  displayName: string;
  ownerCoachId: string;
  ownerName: string;
  /** 0034: this client belongs to the reader (their own book). */
  youOwn: boolean;
  /** 0034: the reader is the one delivering this client day to day. */
  youManage: boolean;
}

export interface RosterDashboard {
  role: 'trainer' | 'coach';
  track: TrainingTrack;
  trackLabel: string;
  metricsFocus: string[];
  trackMetrics: TrackMetrics;
  greeting: { displayName: string; localDate: string };
  counts: { trainees: number; active: number; invited: number; flagged: number };
  averages: { compliancePct: number | null; sessionsPerTrainee7: number; volumeKg7: number };
  trainees: Trainee[];
  attention: Array<{ userId: string; displayName: string; reason: string; severity: 'info' | 'warning' | 'danger' }>;
  ownWeek?: Week;
  ownStreak?: StreakSummary;
}

export type DashboardPayload = LifterDashboard | RosterDashboard;

/* ------------------------------- programme ------------------------------ */

export interface PlanExercise {
  movement: string;
  variant: string | null;
  equipment: string;
  sets: number;
  repMin: number;
  repMax: number;
  perSide: boolean;
  measure: 'reps' | 'distance_m' | 'time_s' | 'amrap';
  targetRpe: number | null;
  targetWeightKg: number | null;
  percent1rm: number | null;
  tempo: string | null;
  restSeconds: number;
  supersetGroup: number | null;
  notes: string | null;
}

export interface PlanDay {
  dayIndex: number;
  label: string;
  focus: string[];
  exercises: PlanExercise[];
}

export interface GeneratedPlan {
  planId: string;
  name: string;
  goal: string;
  track: TrainingTrack;
  trackLabel: string;
  weeks: number;
  daysPerWeek: number;
  summaryMd: string;
  rationale: Array<{ rule: string; evidence: string }>;
  days: PlanDay[];
}

export interface PlanIntake {
  goal: 'strength' | 'hypertrophy' | 'fat_loss' | 'endurance' | 'general';
  track: TrainingTrack;
  experience: 'beginner' | 'intermediate' | 'advanced';
  daysPerWeek: 2 | 3 | 4 | 5 | 6;
  sessionMinutes: 30 | 45 | 60 | 75 | 90;
  heightCm: number;
  bodyweightKg: number;
  age: number;
  sex: 'female' | 'male' | 'unspecified';
  equipment: Array<'barbell' | 'dumbbell' | 'machine' | 'cable' | 'bodyweight' | 'kettlebell' | 'band'>;
  injuryFlags: string[];
  notes?: string;
}

/* -------------------------------- logging ------------------------------- */

export interface SessionSummary {
  sessionId: string;
  dayLabel: string;
  focus: string[];
  completedAt: string | null;
  volumeKg: number;
  sets: number;
  prs: number;
}

export interface LogSetInput {
  exerciseName: string;
  weightKg: number;
  reps: number;
  rpe?: number | null;
  clientSetId?: string;
}

export interface LogSetResult {
  saved: boolean;
  personalRecord: PersonalRecordEntry | null;
  sessionTotalKg: number;
  streakWeeks: number;
  coachHint: string | null;
}

export interface StatsSummary {
  track: TrainingTrack;
  sessions7: number;
  sessions28: number;
  volumeKg28: number;
  sets28: number;
  prs28: number;
  intervalMinutes28: number | null;
  mobilitySessions28: number | null;
  avgPainScore: number | null;
  consistencyPct: number;
}

/**
 * `GET /stats/summary` — the window aggregate the coach reads for a linked
 * client (`?clientId=`). Every number is set-derived, so the API only answers
 * when the link shares `sets`.
 */
export interface ClientStatsSummary {
  from: string;
  to: string;
  sessionsCompleted: number;
  volumeKg: number;
  totalReps: number;
  setsCompleted: number;
  prCount: number;
  failedSets: number;
  avgRpe: number | null;
  avgCompliancePct: number | null;
  currentStreakWeeks: number;
  longestStreakWeeks: number;
  weeklyTarget: number;
  sessionsPerWeek: number;
  unitSystem: 'metric' | 'imperial';
}

/* ------------------------------ blocks & goals -------------------------- */

/**
 * The eight metrics a block goal can track. Shared with the backend — do not
 * rename them. `pain_average` is the only lower-is-better metric: its target is
 * an average-pain ceiling and progress runs down towards it.
 */
export type GoalMetric =
  | 'sessions'
  | 'sets'
  | 'volume_kg'
  | 'bodyweight_kg'
  | 'exercise_1rm'
  | 'mobility_sessions'
  | 'interval_minutes'
  | 'pain_average';

export type GoalStatus = 'active' | 'achieved' | 'missed';

export interface Goal {
  id: string;
  title: string;
  metric: GoalMetric;
  target: number;
  current: number;
  unit: string;
  progressPct: number;
  status: GoalStatus;
  dueOn: string;
  /** only meaningful (and only sent) when the metric is `exercise_1rm` */
  exerciseId?: string | null;
}

export interface GoalInput {
  title: string;
  metric: GoalMetric;
  target: number;
  unit?: string;
  exerciseId?: string;
}

export interface GoalPatch {
  title?: string;
  target?: number;
  status?: GoalStatus;
}

/** One ISO week inside the current 4-week block (oldest first). */
export interface BlockWeek {
  weekStart: string;
  sessions: number;
  target: number;
  sets: number;
  volumeKg: number;
  adherencePct: number;
  isCurrent: boolean;
}

export interface BlockHeader {
  index: number;
  startsOn: string;
  endsOn: string;
  weekIndex: number;
  weeks: number;
}

export interface BlockTotals {
  sessions: number;
  targetSessions: number;
  sets: number;
  volumeKg: number;
  activeDays: number;
  adherencePct: number;
}

export interface CurrentBlock {
  block: BlockHeader;
  totals: BlockTotals;
  weeks: BlockWeek[];
  goals: Goal[];
}

export interface BlockHistoryEntry {
  index: number;
  startsOn: string;
  endsOn: string;
  sessions: number;
  targetSessions: number;
  sets: number;
  volumeKg: number;
  adherencePct: number;
  goalsMet: number;
  goalsTotal: number;
  /**
   * Not part of the required contract: when the API (or the demo dataset)
   * provides the week rows, the history list can expand them. Absence is not an
   * error — the row then shows its block-level recap only.
   */
  weeks?: BlockWeek[];
}

export interface BlockHistoryPayload {
  blocks: BlockHistoryEntry[];
}

/** One recently logged set, rendered in The Grid's vocabulary. */
export interface RecentSet {
  id: string;
  movement: string;
  variant: string | null;
  weightKg: number | null;
  reps: number | null;
  rpe: number | null;
  performedAt: string;
  sessionLabel: string;
}

/** Used by the `exercise_1rm` goal picker. */
export interface ExerciseOption {
  id: string;
  slug: string;
  name: string;
}

/** Wire shapes for the existing session endpoints the Grid summary reads. */
interface SessionListPageWire {
  data: Array<{ sessionId: string; label: string | null; startedAt: string }>;
}

interface LoggedSetWire {
  id: string;
  weightKg: number;
  reps: number;
  rpe: number | null;
  performedAt: string;
  isWarmup: boolean;
}

interface SessionDetailWire {
  sessionExercises: Array<{ id: string; exerciseName: string; loggedSets: LoggedSetWire[] }>;
}

/* ---------------------------------------------------------------------------
   Programme authoring — the lifter's own plan (contract 0007).

   `GET /programme` answers with a plan header plus one flattened row per
   prescribed exercise. Every mutation returns the same refreshed shape, so a
   screen never has to refetch after a write.
   ------------------------------------------------------------------------ */

export type SetMeasure = 'reps' | 'distance_m' | 'time_s' | 'amrap';

/**
 * How a cluster of prescriptions is performed. `superset` rows run back to
 * back; `drop_set` rows are the same movement with descending load. The wire
 * sends `null` for an ungrouped row.
 */
export type ExerciseGroupType = 'superset' | 'drop_set';

export interface ProgrammeWeek {
  week: number;
  isDeload: boolean;
}

/**
 * A block's derived facts: a programme is `weeks` repetitions of one training
 * week, so its end date, the current week and "review due" all follow from the
 * start date. The API derives them (`programme/block.ts`); the client only
 * renders them.
 */
export interface BlockStatus {
  startsOn: string;
  /** Inclusive last day of the block. */
  endsOn: string;
  weeks: number;
  /** 1-based week containing today, clamped to `1..weeks`. */
  weekOf: number;
  /** Whole days from today to `endsOn`; negative once the block is overdue. */
  daysRemaining: number;
  /** True inside the final week, or once the block has run past its end. */
  reviewDue: boolean;
}

export interface ProgrammePlan {
  planId: string;
  name: string;
  goal: string | null;
  track: TrainingTrack;
  trackLabel: string;
  metricsFocus: string[];
  weeks: number;
  daysPerWeek: number;
  source: string;
  isActive: boolean;
  /**
   * True when a human coach authored the block for this owner (0019). The owner
   * can view and log it but not edit, pause or delete it. Absent on older API
   * builds, where the block is always the account's own.
   */
  coachOwned?: boolean;
  /** 1-based week that is lighter, or null when the block has no deload. */
  deloadWeek: number | null;
  weekPlan: ProgrammeWeek[];
  /**
   * First day of the block (`YYYY-MM-DD`) and its derived facts. Optional so
   * demo fixtures written before 0016 still type-check; the API always sends
   * them.
   */
  startsOn?: string | null;
  block?: BlockStatus | null;
}

/** One prescribed exercise inside a day, as `v_programme_detail` flattens it. */
export interface ProgrammeRow {
  planId: string;
  planName: string;
  planGoal: string | null;
  track: TrainingTrack;
  dayId: string;
  dayIndex: number;
  dayLabel: string;
  dayFocus: string[];
  planExerciseId: string;
  orderIndex: number;
  supersetGroup: number | null;
  /** superset | drop_set; null when the row is not grouped. */
  groupType: ExerciseGroupType | null;
  exerciseId: string;
  exerciseName: string;
  movement: string | null;
  variant: string | null;
  equipment: string;
  muscleGroup: string;
  prescribedSets: number;
  repMin: number;
  repMax: number;
  measure: SetMeasure;
  perSide: boolean;
  isBodyweight: boolean;
  tempo: string | null;
  targetRpe: number | null;
  percent1rm: number | null;
  targetWeightKg: number | null;
  targetDistanceM: number | null;
  targetDurationS: number | null;
  restSeconds: number;
  progressionRule: string;
  notes: string | null;
}

/** A day in the plan, including days with no prescribed exercise yet. */
export interface ProgrammeDaySummary {
  id: string;
  dayIndex: number;
  label: string;
  focus: string[];
  exerciseCount: number;
}

/**
 * The block's one warm-up session (0019): a short ordered block of light
 * movements that runs before every day's session, defined once and shared. It is
 * never part of `days`/`rows`; the builder and the runtime read it here.
 */
export interface ProgrammeWarmup {
  dayId: string;
  label: string;
  focus: string[];
  rows: ProgrammeRow[];
}

export interface ProgrammeOverview {
  plan: ProgrammePlan | null;
  rows: ProgrammeRow[];
  /**
   * The authoritative day list. `rows` comes from a view that inner-joins
   * prescriptions, so a day with no exercise has no row; `days` is what lets a
   * freshly created day render before its first movement is added. Absent on
   * older API builds, where the screen groups `rows` instead.
   */
  days?: ProgrammeDaySummary[];
  /** The block's warm-up session, or null when it has none (0019). */
  warmup?: ProgrammeWarmup | null;
  /**
   * COACH SCOPE ONLY. Whether the client has any active block right now,
   * whoever wrote it — the bit that separates "prescribed" from "no active
   * block" without exposing the lifter's own block.
   */
  hasActiveBlock?: boolean;
}

/**
 * Most programme mutations answer with the refreshed plan+rows. Day creation is
 * the exception in practice: a day with no prescription has no row in
 * `v_programme_detail`, so the client keeps the created day id when the API
 * exposes it and can then attach the day's first movement.
 */
export interface ProgrammeMutationResult {
  overview: ProgrammeOverview;
  dayId: string | null;
}

export interface ProgrammeExerciseInput {
  exerciseId: string;
  sets: number;
  repMin: number;
  repMax: number;
  targetRpe: number | null;
  targetWeightKg?: number | null;
  restSeconds?: number;
  measure?: SetMeasure;
  perSide?: boolean;
  tempo?: string | null;
  supersetGroup?: number | null;
  groupType?: ExerciseGroupType | null;
  notes?: string | null;
}

export interface ProgrammeDayInput {
  label: string;
  /** the API stores focus as a list of short tags */
  focus?: string[];
  exercises: ProgrammeExerciseInput[];
}

export interface ProgrammeDraft {
  name: string;
  weeks: number;
  deloadWeek?: number | null;
  track?: TrainingTrack;
  /** First day of the block (`YYYY-MM-DD`); the API defaults it to today. */
  startsOn?: string;
  activate?: boolean;
  days: ProgrammeDayInput[];
}

export interface ProgrammePlanPatch {
  name?: string;
  weeks?: number;
  deloadWeek?: number | null;
  isActive?: boolean;
  /** Move the block's start day (`YYYY-MM-DD`). */
  startsOn?: string;
}

export interface ProgrammeDayPatch {
  label?: string;
  focus?: string[];
  /** reorders; the API renumbers densely */
  index?: number;
}

export interface ProgrammeExercisePatch {
  sets?: number;
  repMin?: number;
  repMax?: number;
  targetRpe?: number | null;
  targetWeightKg?: number | null;
  restSeconds?: number;
  measure?: SetMeasure;
  perSide?: boolean;
  tempo?: string | null;
  supersetGroup?: number | null;
  groupType?: ExerciseGroupType | null;
  notes?: string | null;
}

/**
 * Group (or ungroup) prescriptions of one day. `group: null` ungroups the named
 * rows; a number groups them, with `groupType` saying what kind of cluster it is
 * (`superset` is the default). A group of one is a 422 — ungroup instead.
 */
export interface GroupProgrammeInput {
  planExerciseIds: string[];
  group: number | null;
  groupType?: ExerciseGroupType | null;
}

/** A past/inactive plan for the programme history list. */
export interface PlanHistoryEntry {
  planId: string;
  name: string;
  track: TrainingTrack;
  trackLabel: string;
  weeks: number;
  daysPerWeek: number;
  isActive: boolean;
  /** True when a human coach authored the block (0019). */
  coachOwned?: boolean;
  createdAt: string | null;
  /** 0040 — when the block ran: stamped on activation, stamped again on archive. */
  activatedAt?: string | null;
  archivedAt?: string | null;
  /** 0041 — the block's first day; future + inactive = scheduled, not running. */
  startsOn?: string | null;
  archivedReason?: string | null;
  /** The block this one was copied from, when it started as a copy. */
  copiedFromPlanId?: string | null;
  dayCount?: number;
  exerciseCount?: number;
  /** What happened inside the block: completed sessions, working sets, days. */
  sessionsCompleted?: number;
  setsLogged?: number;
  daysLogged?: number;
  lastSessionAt?: string | null;
}

/* ---------------------------------------------------------------------------
   The live logging session (`GET /sessions/active`, POST sets, complete).
   ------------------------------------------------------------------------ */

export type SessionStatus = 'active' | 'completed' | 'abandoned';
export type SetStatus = 'pending' | 'done' | 'pr' | 'below' | 'failed';

export interface WorkoutSessionWire {
  id: string;
  planDayId: string | null;
  label: string | null;
  startedAt: string;
  endedAt: string | null;
  status: SessionStatus;
  perceivedExertion: number | null;
  moodScore: number | null;
  track: TrainingTrack;
}

export interface SessionSet {
  id: string;
  sessionExerciseId: string;
  exerciseId: string;
  setIndex: number;
  weightKg: number;
  reps: number;
  rpe: number | null;
  status: SetStatus;
  isPr: boolean;
  isWarmup: boolean;
  measure: SetMeasure;
  distanceM: number | null;
  durationS: number | null;
  perSide: boolean;
  tempo: string | null;
  painScore: number | null;
  performedAt: string;
  clientSetId: string | null;
}

export interface SessionExercise {
  id: string;
  sessionId: string;
  exerciseId: string;
  orderIndex: number;
  exerciseName: string;
  /** snapshot of the plan prescription at session start */
  prescription: Record<string, unknown>;
  notes: string | null;
  /** True when snapshotted from the plan's warm-up session (0019). */
  isWarmup?: boolean;
  loggedSets: SessionSet[];
}

export interface SessionTotalsWire {
  sessionId: string;
  label: string | null;
  startedAt: string;
  endedAt: string | null;
  status: SessionStatus;
  volumeKg: number;
  totalReps: number;
  setsCompleted: number;
  setsPrescribed: number | null;
  prCount: number;
  failedSets: number;
  avgRpe: number | null;
  durationMin: number;
}

export interface ActiveSessionPayload {
  session: WorkoutSessionWire | null;
  sessionExercises: SessionExercise[];
  totals: SessionTotalsWire | null;
  serverTime: string;
  softCompletionUntil: string | null;
}

/* ---------------------------------------------------------------------------
   A completed session read back as a table (`GET /sessions/:id`).

   The API computes the compliance summary and, for a coach's `?clientId=` read
   only, the anomaly flags. `flags` is deliberately optional: an own-data read
   never carries the key, which is what keeps the coach layer off the lifter's
   screen by construction rather than by convention.
   ------------------------------------------------------------------------ */

export interface SessionExerciseComplianceWire {
  sessionExerciseId: string;
  exerciseName: string;
  setsPrescribed: number;
  setsCompleted: number;
  repRangeMet: boolean;
  loadOnTarget: boolean | null;
  avgRpe: number | null;
  targetRpe: number | null;
  failedSets: number;
  prCount: number;
}

export interface SessionComplianceWire {
  setsPrescribed: number;
  setsCompleted: number;
  exercisesPrescribed: number;
  exercisesCompleted: number;
  repRangeMet: boolean;
  loadOnTarget: boolean | null;
  avgRpe: number | null;
  targetRpe: number | null;
  failedSets: number;
  prCount: number;
  summary: string;
  exercises: SessionExerciseComplianceWire[];
}

export type SessionFlagCode =
  | 'sets_skipped'
  | 'sets_added'
  | 'rep_range_missed'
  | 'load_below_prescription'
  | 'load_above_prescription'
  | 'rpe_far_from_target'
  | 'rest_too_short'
  | 'rest_too_long'
  | 'tempo_not_followed'
  | 'pain_reported'
  | 'warmup_logged_as_working'
  | 'movement_missing'
  | 'movement_substituted';

export type SessionFlagSeverity = 'info' | 'warning' | 'danger';

export interface SessionFlagWire {
  code: SessionFlagCode;
  severity: SessionFlagSeverity;
  source: 'session_log' | 'evaluation_matrix';
  evidence: string;
  evidenceData: Record<string, string | number | boolean | null>;
  suggestedAction: string;
  target: { sessionExerciseId: string; setIndex: number | null };
}

/** Which week and day of the block the session came from (derived by the API). */
export interface SessionBlockContextWire {
  planId: string;
  planName: string;
  dayLabel: string;
  dayIndex: number;
  startsOn: string | null;
  endsOn: string | null;
  weeks: number;
  weekOf: number;
  isDeload: boolean;
}

export interface SessionViewWire {
  session: WorkoutSessionWire;
  sessionExercises: SessionExercise[];
  totals: SessionTotalsWire;
  compliance: SessionComplianceWire;
  block: SessionBlockContextWire | null;
  /** Coach only. Absent on an own-data read — never an empty array there. */
  flags?: SessionFlagWire[];
}

export interface StartSessionInput {
  planDayId?: string | null;
  label?: string | null;
}

export interface LogSessionSetInput {
  sessionExerciseId: string;
  setIndex: number;
  weightKg: number;
  reps: number;
  rpe?: number | null;
  status?: Exclude<SetStatus, 'pr'>;
  measure?: SetMeasure;
  distanceM?: number | null;
  durationS?: number | null;
  perSide?: boolean;
  painScore?: number | null;
  isWarmup?: boolean;
  /** stable id so an offline retry is idempotent */
  clientSetId?: string;
  notes?: string | null;
}

export interface PersonalRecordWire {
  id: string;
  exerciseId: string;
  exerciseName: string | null;
  weightKg: number;
  reps: number;
  estimated1rmKg: number;
  previousEstimated1rmKg: number | null;
  achievedAt: string;
}

export interface SessionSetResponse {
  set: SessionSet;
  personalRecord: PersonalRecordWire | null;
  sessionTotals: SessionTotalsWire;
  coachHint: string | null;
  idempotentReplay: boolean;
  slotConflict: boolean;
}

export interface CompleteSessionResponse {
  session: WorkoutSessionWire;
  totals: SessionTotalsWire;
  coachRunInvalidated: boolean;
}

export interface AddSessionExerciseInput {
  exerciseId: string;
  orderIndex?: number;
}

/**
 * The result of asking the API to throw a session away.
 *
 * The abandon endpoint is owned by a parallel backend workstream and may not be
 * deployed yet, so this is deliberately NOT funnelled through `fallback()`:
 * discarding a session is destructive and must never be reported as successful
 * because the demo dataset said so. `supported` says whether the running API has
 * the route at all; the UI tells the truth either way.
 */
export interface AbandonSessionResult {
  abandoned: boolean;
  supported: boolean;
  detail: string;
}

/* ---------------------------------------------------------------------------
   Transport
   ------------------------------------------------------------------------ */

/**
 * Where the backend lives.
 *
 * Resolved at RUNTIME from the page's own host, so one build works everywhere:
 * on this machine (`localhost`) and on any device on the LAN (`192.168.x.x`).
 * A hard-coded `127.0.0.1` would resolve to the *viewer's* machine, which is why
 * a phone or another laptop silently fell back to demo data.
 *
 * One address is not enough, though. A site opened through a `.local` name, a
 * proxy or a tunnel can resolve for the web but not for the API, so every call
 * walks a small ordered candidate list and fails over on a *transport* failure
 * (never on an HTTP problem — that means the host answered and the request is
 * at fault). The winner is remembered and tried first next time; a failing
 * winner simply re-probes the list. Override with `VITE_API_URL` when the API
 * lives somewhere else entirely.
 */
const API_BASE_STORAGE = 'l2f.api.base';
const API_LAN_STORAGE = 'l2f.api.lan';

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function envApiBase(): string | null {
  const override = import.meta.env?.VITE_API_URL as string | undefined;
  return override ? stripTrailingSlash(override) : null;
}

function readStoredBase(key: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeStoredBase(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked — the in-memory choice still works for this tab */
  }
}

/** A real LAN address (never loopback) that the API might be reachable on. */
function isLanHost(hostname: string): boolean {
  return /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname);
}

function sameHostBase(): string | null {
  if (typeof window === 'undefined' || !window.location?.hostname) return null;
  return `${window.location.protocol}//${window.location.hostname}:8787/api/v1`;
}

/**
 * The current LAN address, if it differs from the same-host candidate. When the
 * page itself is opened over the LAN this is the page's own host; otherwise the
 * last LAN address that worked is remembered so a later `localhost` visit still
 * has something to fall back to.
 */
function lanBase(): string | null {
  if (typeof window !== 'undefined' && window.location?.hostname && isLanHost(window.location.hostname)) {
    return `${window.location.protocol}//${window.location.hostname}:8787/api/v1`;
  }
  return readStoredBase(API_LAN_STORAGE);
}

/**
 * Candidate order.
 *
 * With `VITE_API_URL` set — which is every deployed build — the override is the
 * ONLY candidate. It is an explicit statement that the API lives at that
 * address, and the same-host guess is then not merely useless but wrong: on
 * `https://lift2fit.com` it resolves to `https://lift2fit.com:8787`, a port
 * nothing serves, while the loopback candidate is a mixed-content block the
 * browser refuses before the request leaves. The old order tried both first on
 * every request after a load, so a production visit paid for two dead addresses
 * before reaching the real one.
 *
 * Without an override — local development, the LAN preview — the old order
 * stands: the page's own host first, so one build works from every device.
 */
function candidateBases(): string[] {
  const override = envApiBase();
  const list: Array<string | null> = override
    ? [override]
    : [sameHostBase(), 'http://127.0.0.1:8787/api/v1', lanBase()];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of list) {
    if (!value) continue;
    const base = stripTrailingSlash(value);
    if (base.length === 0 || seen.has(base)) continue;
    seen.add(base);
    out.push(base);
  }
  return out;
}

function hostOf(base: string): string {
  try {
    return new URL(base).hostname;
  } catch {
    return '';
  }
}

/** The URL the login screen prints: the same-host address, or the override. */
export const API_BASE: string = envApiBase() ?? sameHostBase() ?? 'http://127.0.0.1:8787/api/v1';

/** The address that last answered, remembered across reloads. */
let activeBase: string | null = readStoredBase(API_BASE_STORAGE);

/** The remembered winner first, then the rest of the candidates. */
function orderedBases(): string[] {
  const all = candidateBases();
  const remembered = activeBase;
  // A remembered address only leads when it is still a legitimate candidate. It
  // otherwise belongs to an earlier visit — a LAN address stored while testing,
  // say — and a deployed build must not walk back to it.
  if (!remembered || !all.includes(remembered)) return all;
  return [remembered, ...all.filter((base) => base !== remembered)];
}

function rememberBase(base: string): void {
  const changed = activeBase !== base;
  if (changed) {
    activeBase = base;
    writeStoredBase(API_BASE_STORAGE, base);
  }
  if (isLanHost(hostOf(base))) writeStoredBase(API_LAN_STORAGE, base);
}

/**
 * A data-heavy read (a block summary, a dashboard) can legitimately take
 * seconds; 4 s raced the deadline on a real account and aborted the request at
 * the boundary. Reads that opt into `retryOnTimeout` get one more full walk.
 */
const TIMEOUT_MS = 12000;
/** Every candidate that is still to come keeps at least this much of the budget. */
const MIN_ATTEMPT_MS = 1500;
/** A whole call — probing every candidate — never blocks the UI longer than this. */
const TOTAL_BUDGET_MS = 22000;
/** A short pause before the single retry of a timed-out read. */
const RETRY_BACKOFF_MS = 300;

let authToken: string | null = null;
let lastSource: 'api' | 'demo' = 'demo';
const sourceListeners = new Set<(source: 'api' | 'demo') => void>();

/** useSession owns the token; it pushes it here so every request carries it. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export function currentSource(): 'api' | 'demo' {
  return lastSource;
}

export function subscribeSource(listener: (source: 'api' | 'demo') => void): () => void {
  sourceListeners.add(listener);
  return () => sourceListeners.delete(listener);
}

function reportSource(source: 'api' | 'demo'): void {
  lastSource = source;
  for (const listener of sourceListeners) listener(source);
}

const DEMO_DELAY_MS = 180;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** One attempt against one candidate address. */
async function attempt<T>(base: string, path: string, init: RequestInit, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const headers = new Headers(init.headers);
    headers.set('accept', 'application/json');
    if (init.body) headers.set('content-type', 'application/json');
    if (authToken) headers.set('authorization', `Bearer ${authToken}`);
    // The demo lock (0040): one key in front of the whole API, baked into the
    // build. It is NOT a secret from anyone who can load the site — the site
    // itself is behind Cloudflare Access — it keeps the API host quiet and stops
    // everything that is not this bundle, which is the point.
    const siteKey = import.meta.env?.VITE_SITE_KEY as string | undefined;
    if (siteKey) headers.set('x-l2f-site-key', siteKey);

    const response = await fetch(`${base}${path}`, { ...init, headers, signal: controller.signal });
    if (!response.ok) {
      let problem: Problem = {
        type: 'about:blank',
        title: response.statusText || 'Request failed',
        status: response.status,
      };
      try {
        problem = { ...problem, ...((await response.json()) as Problem) };
      } catch {
        /* not a problem+json body — keep the status line */
      }
      throw new ApiError(problem);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/** A fetch aborted by the per-attempt timeout, as opposed to an HTTP problem. */
function isTimeoutError(error: unknown): boolean {
  return asRecord(error).name === 'AbortError';
}

/**
 * The transport every call goes through: walk the candidate addresses, pin the
 * one that answered, and re-probe the list when a remembered address fails. An
 * HTTP problem pins the address too — the host is reachable, so the request is
 * what is wrong — and is rethrown unchanged.
 *
 * The per-attempt timeout reserves a slice for every candidate still to come, so
 * a same-host name that blackholes (accepted but never answers) cannot consume
 * the whole budget and leave the working `127.0.0.1` candidate with nothing —
 * which is exactly how the FIRST call after opening the app used to fail while
 * the API was up.
 *
 * `retryOnTimeout` gives a data-heavy read one more full walk when the only
 * failure was the deadline; an HTTP problem or a refused connection is final.
 */
async function request<T>(
  path: string,
  init: RequestInit = {},
  options: { retryOnTimeout?: boolean } = {},
): Promise<T> {
  const maxWalks = options.retryOnTimeout === true ? 2 : 1;
  let lastError: unknown = null;
  for (let walk = 0; walk < maxWalks; walk += 1) {
    const deadline = Date.now() + TOTAL_BUDGET_MS;
    const bases = orderedBases();
    let timedOut = false;
    for (let index = 0; index < bases.length; index += 1) {
      const base = bases[index];
      if (!base) continue;
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      const reserved = (bases.length - index - 1) * MIN_ATTEMPT_MS;
      const timeoutMs = Math.min(TIMEOUT_MS, Math.max(MIN_ATTEMPT_MS, remaining - reserved));
      try {
        const data = await attempt<T>(base, path, init, timeoutMs);
        rememberBase(base);
        scheduleFlush();
        return data;
      } catch (error) {
        if (error instanceof ApiError) {
          rememberBase(base);
          throw error;
        }
        if (isTimeoutError(error)) timedOut = true;
        lastError = error;
      }
    }
    // Only a timeout earns the single retry: a refused connection means the
    // address is wrong, not busy, and repeating it just doubles the wait.
    if (!timedOut || walk + 1 >= maxWalks) break;
    await wait(RETRY_BACKOFF_MS);
  }
  throw lastError ?? new TypeError('The API could not be reached.');
}

/* ---------------------------------------------------------------------------
   The pending-set queue — a write that never reached the API is never lost.

   A failed set write (a transport failure: the API was restarting, or the
   chosen address did not answer) is stored locally, keyed by its `clientSetId`,
   and replayed automatically. The API is already idempotent on `clientSetId`,
   so a duplicate replay is safe and a set can end up in the account exactly
   once. Replays are triggered on the next successful call, on regaining focus
   or `online`, and on the next panel action — never in a retry storm.
   ------------------------------------------------------------------------ */

export interface PendingSetWrite {
  /** `clientSetId` for a POST, or `patch:<setId>` for a PATCH. */
  key: string;
  clientSetId: string | null;
  path: string;
  method: 'POST' | 'PATCH';
  body: string;
  /** short, honest description for the "waiting to sync" indicator */
  label: string;
  queuedAt: string;
}

export interface PendingWriteStatus {
  pending: number;
  lastSyncedAt: number | null;
}

const PENDING_STORAGE = 'l2f.api.pending.v1';
const MAX_PENDING = 40;
const FLUSH_DEBOUNCE_MS = 200;

function parsePending(raw: string | null): PendingSetWrite[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => asRecord(entry))
      .filter(
        (entry) =>
          typeof entry.key === 'string' &&
          typeof entry.path === 'string' &&
          typeof entry.body === 'string' &&
          (entry.method === 'POST' || entry.method === 'PATCH'),
      )
      .map((entry) => ({
        key: entry.key as string,
        clientSetId: asString(entry.clientSetId),
        path: entry.path as string,
        method: entry.method as 'POST' | 'PATCH',
        body: entry.body as string,
        label: asString(entry.label) ?? 'Set',
        queuedAt: asString(entry.queuedAt) ?? new Date().toISOString(),
      }))
      .slice(0, MAX_PENDING);
  } catch {
    return [];
  }
}

let pendingQueue: PendingSetWrite[] = parsePending(readStoredBase(PENDING_STORAGE));
let lastSyncedAt: number | null = null;
let flushing = false;
let flushTimer: number | null = null;

const pendingListeners = new Set<(status: PendingWriteStatus) => void>();

function persistPending(): void {
  writeStoredBase(PENDING_STORAGE, pendingQueue.length === 0 ? null : JSON.stringify(pendingQueue));
}

function notifyPending(): void {
  const status: PendingWriteStatus = { pending: pendingQueue.length, lastSyncedAt };
  for (const listener of pendingListeners) listener(status);
}

/** How many set writes are waiting, and when the last replay succeeded. */
export function pendingWriteStatus(): PendingWriteStatus {
  return { pending: pendingQueue.length, lastSyncedAt };
}

/**
 * Subscribe to the queue. The listener fires immediately with the current
 * status, then once per change — so an indicator can say "1 set waiting to
 * sync" and clear itself the moment the replay lands.
 */
export function subscribePendingWrites(listener: (status: PendingWriteStatus) => void): () => void {
  pendingListeners.add(listener);
  listener(pendingWriteStatus());
  return () => {
    pendingListeners.delete(listener);
  };
}

function enqueuePendingWrite(entry: PendingSetWrite): void {
  pendingQueue = [entry, ...pendingQueue.filter((existing) => existing.key !== entry.key)].slice(0, MAX_PENDING);
  persistPending();
  notifyPending();
}

function removePendingWrite(key: string): void {
  pendingQueue = pendingQueue.filter((entry) => entry.key !== key);
  persistPending();
}

/**
 * Replay everything that is waiting. Best effort and never throws into the UI:
 * a server answer (even a rejection) retires the entry — replaying the same
 * payload will not help — while a transport failure leaves the rest queued.
 */
export async function flushPendingWrites(): Promise<{ synced: number; remaining: number }> {
  if (flushing || pendingQueue.length === 0) return { synced: 0, remaining: pendingQueue.length };
  flushing = true;
  let synced = 0;
  try {
    for (const entry of [...pendingQueue]) {
      try {
        await request<unknown>(entry.path, { method: entry.method, body: entry.body });
        removePendingWrite(entry.key);
        synced += 1;
      } catch (error) {
        if (error instanceof ApiError) removePendingWrite(entry.key);
        else break;
      }
    }
  } finally {
    flushing = false;
    if (synced > 0) lastSyncedAt = Date.now();
    notifyPending();
  }
  return { synced, remaining: pendingQueue.length };
}

/** Coalesce the flush that follows a successful call so it can never storm. */
function scheduleFlush(): void {
  if (flushing || pendingQueue.length === 0 || flushTimer !== null) return;
  if (typeof window === 'undefined') return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushPendingWrites();
  }, FLUSH_DEBOUNCE_MS);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void flushPendingWrites();
  });
  window.addEventListener('focus', () => {
    void flushPendingWrites();
  });
}

export class ApiError extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
  }
}

/**
 * Try the API; on a transport failure resolve the demo dataset instead — but
 * only for the demo persona. `allowApiError` lets sign-in surface a real 401
 * rather than silently signing the user into demo mode. `surfaceForRealAccount`
 * is the same idea for a signed-in real account: an HTTP problem on a
 * write/lifecycle call is the API's own answer and must reach the UI with its
 * `detail`, never be dressed up as "the API could not be reached".
 * `surfaceStatuses` is the same idea for a narrow set of real problems a screen
 * must show (the 5-goal cap is a 409). `requireApi` is for the admin surface: it
 * is never a demo persona, so a transport failure must reach the screen as an
 * honest error instead of inventing empty admin data.
 *
 * `requireRealAccountData` is the same rule for a heavy read on a real account:
 * a transport failure must never render the built-in demo set in the user's own
 * slots. The call resolves to `empty ?? null` with a `problem` so the screen can
 * show an explicit error and a Retry; demo data stays for demo mode only.
 */
async function fallback<T>(
  run: () => Promise<T>,
  demo: () => T | Promise<T>,
  options: {
    allowApiError?: boolean;
    /**
     * A call that must not invent an answer when nothing answered it. Sign-in
     * is the one that matters: an unverified credential must never resolve to
     * the offline demo persona, or a deploy with a missing `VITE_API_URL` looks
     * like a working login and lands the visitor in "Alex"'s demo dashboard.
     */
    requireApi?: boolean;
    /** The words shown when `requireApi` and the request never arrived. */
    transportDetail?: string;
    surfaceForRealAccount?: boolean;
    requireRealAccountData?: boolean;
    empty?: T;
    surfaceStatuses?: number[];
  } = {},
): Promise<ApiResult<T>> {
  try {
    const data = await run();
    reportSource('api');
    return { data, source: 'api' };
  } catch (error) {
    if (options.requireApi === true) {
      reportSource('api');
      const problem: Problem =
        error instanceof ApiError
          ? error.problem
          : {
              type: 'about:blank',
              title: 'The API could not be reached.',
              status: 0,
              detail:
                options.transportDetail ??
                'The admin surface has no offline copy. Start the API and try again.',
            };
      return { data: (options.empty ?? null) as T, source: 'api', problem };
    }
    const surfaced =
      error instanceof ApiError &&
      (options.allowApiError === true ||
        (options.surfaceForRealAccount === true && isRealAccount()) ||
        (options.surfaceStatuses?.includes(error.problem.status) ?? false));
    if (surfaced && error instanceof ApiError) {
      reportSource('api');
      return { data: (options.empty ?? null) as T, source: 'api', problem: error.problem };
    }
    if (options.requireRealAccountData === true && isRealAccount()) {
      reportSource('api');
      const problem: Problem =
        error instanceof ApiError
          ? error.problem
          : {
              type: 'about:blank',
              title: 'The API could not be reached.',
              status: 0,
              detail: 'Your account data could not be loaded. Check the connection and try again.',
            };
      return { data: (options.empty ?? null) as T, source: 'api', problem };
    }
    reportSource('demo');
    await wait(DEMO_DELAY_MS);
    return { data: await demo(), source: 'demo' };
  }
}

/**
 * A real signed-in account, not the offline demo persona. Only a real account
 * queues a set write whose response was lost; the demo dataset answers for
 * itself and must keep working with the API absent or rejecting its fake token.
 */
function isRealAccount(): boolean {
  return typeof authToken === 'string' && authToken.length > 0 && authToken !== 'demo-access-token';
}

interface QueuedWriteOptions<T> {
  path: string;
  method: 'POST' | 'PATCH';
  body: string;
  key: string;
  clientSetId: string | null;
  label: string;
  run: () => Promise<T>;
  demo: () => T | Promise<T>;
}

/**
 * A set write with the queue behind it. An HTTP answer is surfaced with the
 * API's own reason and is never queued (the request itself is at fault); a
 * transport failure on a real account queues the exact call for an idempotent
 * replay and reports `queued: true` so the host can say so honestly.
 */
async function writeSet<T>(options: QueuedWriteOptions<T>): Promise<ApiResult<T>> {
  try {
    const data = await options.run();
    reportSource('api');
    return { data, source: 'api' };
  } catch (error) {
    if (error instanceof ApiError && isRealAccount()) {
      reportSource('api');
      return { data: null as unknown as T, source: 'api', problem: error.problem };
    }
    if (!(error instanceof ApiError) && isRealAccount()) {
      enqueuePendingWrite({
        key: options.key,
        clientSetId: options.clientSetId,
        path: options.path,
        method: options.method,
        body: options.body,
        label: options.label,
        queuedAt: new Date().toISOString(),
      });
      scheduleFlush();
      reportSource('api');
      return { data: null as unknown as T, source: 'api', queued: true };
    }
    reportSource('demo');
    await wait(DEMO_DELAY_MS);
    return { data: await options.demo(), source: 'demo' };
  }
}

/* ---------------------------------------------------------------------------
   Wire → app mapping.

   The live API payload is deliberately leaner than the demo dataset: the
   track-specific aggregates ride inside `totals` (`intervalMinutes7`,
   `avgPainScore`, `avgReadiness`, …) and there is no `trackMetrics` object at
   all. Every reader below tolerates a missing, null or wrongly-typed field and
   degrades to null (rendered as an em dash) or an empty collection, so a real
   account never blanks the app the way the demo-only code did.
   ------------------------------------------------------------------------ */

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asInt(value: unknown, fallbackValue = 0): number {
  const parsed = asNumber(value);
  return parsed === null ? fallbackValue : Math.round(parsed);
}

/** A string, or `''` — for fields that are required on the wire. */
function asStringText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

const TRAINING_TRACKS: ReadonlyArray<TrainingTrack> = [
  'strength',
  'hypertrophy',
  'conditioning',
  'performance',
  'wellness',
  'general',
];

function asTrack(value: unknown): TrainingTrack {
  const raw = asString(value);
  return TRAINING_TRACKS.includes(raw as TrainingTrack) ? (raw as TrainingTrack) : 'general';
}

const WEEK_STATES: ReadonlyArray<WeekDay['state']> = ['done', 'today', 'rest', 'missed', 'upcoming'];

/** Fill the gaps in an account payload so every screen can read `user.track`. */
export function normalizeUser(value: unknown): User {
  const raw = asRecord(value);
  const track = asTrack(raw.track);
  const roles = asArray<unknown>(raw.roles).filter(
    (role): role is Role =>
      role === 'lifter' || role === 'trainer' || role === 'coach' || role === 'admin',
  );
  const experience = raw.experience;
  return {
    id: asString(raw.id) ?? 'unknown',
    email: asString(raw.email) ?? '',
    displayName: asString(raw.displayName) ?? 'Athlete',
    roles: roles.length ? roles : ['lifter'],
    unitSystem: raw.unitSystem === 'imperial' ? 'imperial' : 'metric',
    tier: raw.tier === 'premium' || raw.tier === 'plan_once' ? raw.tier : 'basic',
    timezone: asString(raw.timezone) ?? 'UTC',
    bodyweightKg: asNumber(raw.bodyweightKg),
    heightCm: asNumber(raw.heightCm),
    trainingGoal: asString(raw.trainingGoal),
    experience:
      experience === 'beginner' || experience === 'intermediate' || experience === 'advanced' ? experience : null,
    injuryFlags: asArray<unknown>(raw.injuryFlags).filter((flag): flag is string => typeof flag === 'string'),
    weeklyTarget: asInt(raw.weeklyTarget, 4) || 4,
    track,
    trackLabel: asString(raw.trackLabel) ?? trackMeta(track).label,
    coachPlan:
      raw.coachPlan === 'coach' ||
      raw.coachPlan === 'gym' ||
      raw.coachPlan === 'gym_plus' ||
      raw.coachPlan === 'scale'
        ? raw.coachPlan
        : 'free',
    // Derived entitlement from the API. Older payloads predate the free-tier
    // gate, so they default to the permissive reading rather than locking a
    // legitimate coach out.
    lifterInterface: raw.lifterInterface !== false,
    supportStaff: raw.supportStaff === true,
    internalCoachOf: normalizeManagedCoach(raw.internalCoachOf),
  };
}

/** 0030: the `{coachId, displayName}` gym a user is an internal coach of. */
function normalizeManagedCoach(value: unknown): { coachId: string; displayName: string } | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const coachId = asString(raw.coachId);
  if (coachId === null) return null;
  return { coachId, displayName: asString(raw.displayName) ?? 'Your gym' };
}

export function normalizeAuthResponse(value: unknown): AuthResponse {
  const raw = asRecord(value);
  return {
    accessToken: asString(raw.accessToken) ?? '',
    refreshToken: asString(raw.refreshToken) ?? '',
    user: normalizeUser(raw.user),
  };
}

/** Parse the consent read-back; a malformed row is dropped rather than faked. */
export function normalizePolicyConsentList(value: unknown): PolicyConsentList {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data)
    .map((entry) => asRecord(entry))
    .filter((entry) => typeof entry.policyVersion === 'string' && typeof entry.acceptedAt === 'string')
    .map((entry) => ({
      id: asString(entry.id) ?? 'unknown',
      policyVersion: entry.policyVersion as string,
      documents: asArray<unknown>(entry.documents).filter((doc): doc is string => typeof doc === 'string'),
      acceptedAt: entry.acceptedAt as string,
      userAgent: asString(entry.userAgent),
      ipAddress: asString(entry.ipAddress),
    }));
  return {
    data,
    currentVersion: asString(raw.currentVersion) ?? '',
    effectiveDate: asString(raw.effectiveDate) ?? '',
    documents: asArray<unknown>(raw.documents).filter((doc): doc is string => typeof doc === 'string'),
  };
}

function normalizeWeek(value: unknown): Week {
  const raw = asRecord(value);
  const days: WeekDay[] = asArray<unknown>(raw.days).map((entry, index) => {
    const day = asRecord(entry);
    const state = WEEK_STATES.includes(day.state as WeekDay['state'])
      ? (day.state as WeekDay['state'])
      : 'upcoming';
    return {
      date: asString(day.date) ?? `day-${index + 1}`,
      state,
      label: asString(day.label),
      sessionId: asString(day.sessionId),
    };
  });
  return {
    weekStart: asString(raw.weekStart) ?? '',
    target: asInt(raw.target),
    completed: asInt(raw.completed),
    days,
  };
}

function normalizeTrend(value: unknown): TrendPoint[] {
  return asArray<unknown>(value).map((entry) => {
    const point = asRecord(entry);
    const headline = asNumber(point.value);
    return {
      weekStart: asString(point.weekStart) ?? '',
      // the live API names the headline series `value`; the volume UI reads `volumeKg`
      volumeKg: asNumber(point.volumeKg) ?? headline,
      sets: asNumber(point.sets),
      prs: asNumber(point.prs),
      sessions: asNumber(point.sessions),
      intervalMinutes: asNumber(point.intervalMinutes),
      mobilitySessions: asNumber(point.mobilitySessions),
      painScore: asNumber(point.painScore),
    };
  });
}

function normalizePersonalRecords(value: unknown): PersonalRecordEntry[] {
  return asArray<unknown>(value).map((entry) => {
    const record = asRecord(entry);
    return {
      exerciseName: asString(record.exerciseName) ?? 'Unknown exercise',
      weightKg: asNumber(record.weightKg) ?? 0,
      reps: asInt(record.reps),
      estimated1rmKg: asNumber(record.estimated1rmKg) ?? 0,
      achievedAt: asString(record.achievedAt) ?? new Date().toISOString(),
    };
  });
}

function normalizeAlerts(value: unknown): AlertEntry[] {
  return asArray<unknown>(value).map((entry) => {
    const alert = asRecord(entry);
    const severity = alert.severity;
    return {
      code: asString(alert.code) ?? 'note',
      severity: severity === 'warning' || severity === 'danger' ? severity : 'info',
      message: asString(alert.message) ?? '',
    };
  });
}

/**
 * Build `TrackMetrics` from wherever the API put the numbers. A backend that
 * sends `trackMetrics` is used directly; one that only sends the track-aware
 * `totals` fields is mapped from those. Anything absent stays null.
 */
function normalizeTrackMetrics(
  value: unknown,
  totalsValue: unknown,
  track: TrainingTrack,
  weekCompleted: number,
  bodyweightKg: number | null,
): TrackMetrics {
  const raw = asRecord(value);
  const totals = asRecord(totalsValue);
  const direct = (key: keyof TrackMetrics): number | null => asNumber(raw[key]);
  return {
    coachPersona: asString(raw.coachPersona) ?? coachPersona(track),
    bestE1rmKg: direct('bestE1rmKg'),
    topSetLabel: asString(raw.topSetLabel),
    volumeKg7: direct('volumeKg7') ?? asNumber(totals.volumeKg7),
    volumeChangePct: direct('volumeChangePct') ?? asNumber(totals.volumeChangePct),
    sets7: direct('sets7') ?? asNumber(totals.sets7),
    prs7: direct('prs7') ?? asNumber(totals.prs7),
    setsPerMuscle: direct('setsPerMuscle'),
    bodyweightKg: direct('bodyweightKg') ?? bodyweightKg,
    intervalMinutes: direct('intervalMinutes') ?? asNumber(totals.intervalMinutes7),
    workRestRatio: asString(raw.workRestRatio) ?? asString(totals.workRestRatio),
    ergMetres: direct('ergMetres') ?? asNumber(totals.ergMetres7),
    avgRpe: direct('avgRpe') ?? asNumber(totals.avgRpe7),
    powerMarkers: direct('powerMarkers') ?? asNumber(totals.powerMarkers7),
    jumpHeightCm: direct('jumpHeightCm') ?? asNumber(totals.jumpBestCm),
    readinessPct: direct('readinessPct') ?? asNumber(totals.avgReadiness),
    mobilitySessions: direct('mobilitySessions') ?? asNumber(totals.mobilitySessions7),
    painScore: direct('painScore') ?? asNumber(totals.avgPainScore),
    consistencyPct: direct('consistencyPct') ?? asNumber(totals.compliancePct7),
    sessions7: direct('sessions7') ?? weekCompleted,
    feel: asString(raw.feel),
  };
}

export function normalizeLifterDashboard(value: unknown): LifterDashboard {
  const raw = asRecord(value);
  const track = asTrack(raw.track);
  const meta = trackMeta(track);
  const week = normalizeWeek(raw.week);
  const totalsRaw = asRecord(raw.totals);
  const greetingRaw = asRecord(raw.greeting);
  const streakRaw = asRecord(raw.streak);
  const coachRaw = asRecord(raw.coach);
  const nextRaw = raw.nextSession === null || raw.nextSession === undefined ? null : asRecord(raw.nextSession);
  const highlightSeeds = asArray<unknown>(raw.highlights);
  const highlights: Highlight[] | undefined = highlightSeeds.length
    ? highlightSeeds.map((entry) => {
        const item = asRecord(entry);
        return {
          label: asString(item.label) ?? 'Highlight',
          value: asString(item.value) ?? '—',
          detail: asString(item.detail),
          at: asString(item.at) ?? new Date().toISOString(),
        };
      })
    : undefined;

  return {
    role: 'lifter',
    track,
    trackLabel: asString(raw.trackLabel) ?? meta.label,
    metricsFocus: asArray<unknown>(raw.metricsFocus).filter((item): item is string => typeof item === 'string'),
    trackMetrics: normalizeTrackMetrics(raw.trackMetrics, totalsRaw, track, week.completed, null),
    greeting: {
      displayName: asString(greetingRaw.displayName) ?? 'Athlete',
      localDate: asString(greetingRaw.localDate) ?? new Date().toISOString().slice(0, 10),
    },
    streak: {
      currentWeeks: asInt(streakRaw.currentWeeks),
      longestWeeks: asInt(streakRaw.longestWeeks),
      qualifiesThisWeek: streakRaw.qualifiesThisWeek === true,
    },
    week,
    nextSession: nextRaw
      ? {
          sessionId: asString(nextRaw.sessionId) ?? '',
          dayLabel: asString(nextRaw.dayLabel) ?? 'Session',
          focus: asArray<unknown>(nextRaw.focus).filter((item): item is string => typeof item === 'string'),
          exercises: asInt(nextRaw.exercises),
          sets: asInt(nextRaw.sets),
          lastPerformed: asString(nextRaw.lastPerformed),
        }
      : null,
    totals: {
      volumeKg7: asNumber(totalsRaw.volumeKg7) ?? 0,
      volumeChangePct: asNumber(totalsRaw.volumeChangePct),
      sets7: asInt(totalsRaw.sets7),
      prs7: asInt(totalsRaw.prs7),
      avgRpe7: asNumber(totalsRaw.avgRpe7),
      compliancePct7: asNumber(totalsRaw.compliancePct7),
    },
    trend: normalizeTrend(raw.trend),
    personalRecords: normalizePersonalRecords(raw.personalRecords),
    ...(highlights ? { highlights } : {}),
    coach: {
      hasPremium: coachRaw.hasPremium === true,
      latestReportAt: asString(coachRaw.latestReportAt),
      verdict: asString(coachRaw.verdict),
      headline: asString(coachRaw.headline),
      changes: asInt(coachRaw.changes),
      persona: asString(coachRaw.persona) ?? meta.persona,
    },
    alerts: normalizeAlerts(raw.alerts),
  };
}

function normalizeTrainee(value: unknown): Trainee {
  const raw = asRecord(value);
  const track = asTrack(raw.track);
  const meta = trackMeta(track);
  const status = raw.status;
  return {
    userId: asString(raw.userId) ?? asString(raw.id) ?? '',
    displayName: asString(raw.displayName) ?? 'Client',
    email: asString(raw.email) ?? '',
    status: status === 'invited' || status === 'revoked' ? status : 'active',
    linkedAt: asString(raw.linkedAt),
    lastSessionAt: asString(raw.lastSessionAt),
    sessionsLast7: asInt(raw.sessionsLast7),
    compliancePct: asNumber(raw.compliancePct),
    currentStreakWeeks: asInt(raw.currentStreakWeeks),
    flags: asArray<unknown>(raw.flags).filter((flag): flag is string => typeof flag === 'string'),
    sharedFields: asArray<unknown>(raw.sharedFields).filter(
      (field): field is Trainee['sharedFields'][number] =>
        field === 'sessions' || field === 'sets' || field === 'bodyweight' || field === 'notes' || field === 'pain',
    ),
    track,
    trackLabel: asString(raw.trackLabel) ?? meta.label,
    coachPersona: asString(raw.coachPersona) ?? meta.persona,
    trainerName: asString(raw.trainerName) ?? undefined,
    focus: asString(raw.focus) ?? undefined,
    blockName: asString(raw.blockName),
    reviewDue: raw.reviewDue === true,
    blockEndsOn: asString(raw.blockEndsOn),
    daysRemaining: asNumber(raw.daysRemaining),
    weekOf: asNumber(raw.weekOf),
    weeks: asNumber(raw.weeks),
    managedBy: normalizeManagedBy(raw.managedBy),
  };
}

/** 0030: normalise the API's `managedBy` label, or null when absent. */
export function normalizeManagedBy(value: unknown): ManagedBy | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const kind = raw.kind;
  if (kind !== 'self' && kind !== 'internal' && kind !== 'gym') return null;
  return {
    kind,
    coachId: asString(raw.coachId) ?? '',
    displayName: asString(raw.displayName) ?? 'Coach',
    ownerCoachId: asString(raw.ownerCoachId) ?? '',
    ownerName: asString(raw.ownerName) ?? 'Your gym',
    youOwn: raw.youOwn === true,
    youManage: raw.youManage === true,
  };
}

export function normalizeRosterDashboard(value: unknown, role: 'trainer' | 'coach'): RosterDashboard {
  const raw = asRecord(value);
  const track = asTrack(raw.track);
  const meta = trackMeta(track);
  const weekSeed = raw.ownWeek ?? raw.week;
  const ownWeek = weekSeed === null || weekSeed === undefined ? undefined : normalizeWeek(weekSeed);
  const streakSeed = raw.ownStreak ?? raw.streak;
  const ownStreak =
    streakSeed === null || streakSeed === undefined
      ? undefined
      : {
          currentWeeks: asInt(asRecord(streakSeed).currentWeeks),
          longestWeeks: asInt(asRecord(streakSeed).longestWeeks),
          qualifiesThisWeek: asRecord(streakSeed).qualifiesThisWeek === true,
        };
  const countsRaw = asRecord(raw.counts);
  const averagesRaw = asRecord(raw.averages);
  const greetingRaw = asRecord(raw.greeting);
  const trainees = asArray<unknown>(raw.trainees).map(normalizeTrainee);

  return {
    role,
    track,
    trackLabel: asString(raw.trackLabel) ?? meta.label,
    metricsFocus: asArray<unknown>(raw.metricsFocus).filter((item): item is string => typeof item === 'string'),
    trackMetrics: normalizeTrackMetrics(raw.trackMetrics, raw.totals, track, ownWeek?.completed ?? 0, null),
    greeting: {
      displayName: asString(greetingRaw.displayName) ?? 'Coach',
      localDate: asString(greetingRaw.localDate) ?? new Date().toISOString().slice(0, 10),
    },
    counts: {
      trainees: asInt(countsRaw.trainees, trainees.length),
      active: asInt(countsRaw.active),
      invited: asInt(countsRaw.invited),
      flagged: asInt(countsRaw.flagged),
    },
    averages: {
      compliancePct: asNumber(averagesRaw.compliancePct),
      sessionsPerTrainee7: asNumber(averagesRaw.sessionsPerTrainee7) ?? 0,
      volumeKg7: asNumber(averagesRaw.volumeKg7) ?? 0,
    },
    trainees,
    attention: asArray<unknown>(raw.attention).map((entry) => {
      const item = asRecord(entry);
      const severity = item.severity;
      return {
        userId: asString(item.userId) ?? '',
        displayName: asString(item.displayName) ?? 'Client',
        reason: asString(item.reason) ?? '',
        severity: severity === 'warning' || severity === 'danger' ? severity : 'info',
      };
    }),
    ...(ownWeek ? { ownWeek } : {}),
    ...(ownStreak ? { ownStreak } : {}),
  };
}

/* ---------------------------------------------------------------------------
   Programme + live-session normalizers.

   The live payload is close to the documented contract, but a plan built by an
   older generator carries no `deloadWeek`/`weekPlan` and a session can arrive
   without totals. Every reader below tolerates that and derives what it can, so
   a real account never blanks the screen (the crash demo mode once hid).
   ------------------------------------------------------------------------ */

const SET_MEASURES: ReadonlyArray<SetMeasure> = ['reps', 'distance_m', 'time_s', 'amrap'];

function asMeasure(value: unknown): SetMeasure {
  return SET_MEASURES.includes(value as SetMeasure) ? (value as SetMeasure) : 'reps';
}

/** A legacy row (group number, no type) is a superset — never a fabricated drop set. */
function asGroupType(value: unknown, supersetGroup: number | null): ExerciseGroupType | null {
  if (value === 'drop_set') return 'drop_set';
  if (value === 'superset') return 'superset';
  return supersetGroup === null ? null : 'superset';
}

function asWeekPlan(value: unknown, weeks: number, deloadWeek: number | null): ProgrammeWeek[] {
  const rows = asArray<unknown>(value)
    .map((entry, index) => {
      const row = asRecord(entry);
      const week = asInt(row.week, index + 1) || index + 1;
      return { week, isDeload: row.isDeload === true || week === deloadWeek };
    })
    .filter((row) => row.week >= 1);
  if (rows.length) return rows;
  const count = Math.max(0, weeks);
  return Array.from({ length: count }, (_, index) => ({
    week: index + 1,
    isDeload: deloadWeek === index + 1,
  }));
}

/** Parse the API's derived block status; `null` when the plan carries none. */
function normalizeBlockStatus(value: unknown): BlockStatus | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const startsOn = asString(raw.startsOn);
  const endsOn = asString(raw.endsOn);
  if (!startsOn || !endsOn) return null;
  return {
    startsOn,
    endsOn,
    weeks: Math.max(1, asInt(raw.weeks, 1) || 1),
    weekOf: Math.max(1, asInt(raw.weekOf, 1) || 1),
    daysRemaining: asInt(raw.daysRemaining),
    reviewDue: raw.reviewDue === true,
  };
}

export function normalizeProgrammeOverview(value: unknown): ProgrammeOverview {
  const raw = asRecord(value);
  const planRaw = raw.plan === null || raw.plan === undefined ? null : asRecord(raw.plan);
  const rows = asArray<unknown>(raw.rows).map((entry): ProgrammeRow => {
    const row = asRecord(entry);
    const track = asTrack(row.track);
    const supersetGroup = asNumber(row.supersetGroup);
    return {
      planId: asString(row.planId) ?? '',
      planName: asString(row.planName) ?? '',
      planGoal: asString(row.planGoal),
      track,
      dayId: asString(row.dayId) ?? '',
      dayIndex: asInt(row.dayIndex, 1) || 1,
      dayLabel: asString(row.dayLabel) ?? 'Day',
      dayFocus: asArray<unknown>(row.dayFocus).filter((item): item is string => typeof item === 'string'),
      planExerciseId: asString(row.planExerciseId) ?? '',
      orderIndex: asInt(row.orderIndex, 1) || 1,
      supersetGroup,
      groupType: asGroupType(row.groupType, supersetGroup),
      exerciseId: asString(row.exerciseId) ?? '',
      exerciseName: asString(row.exerciseName) ?? asString(row.movement) ?? 'Exercise',
      movement: asString(row.movement),
      variant: asString(row.variant),
      equipment: asString(row.equipment) ?? 'barbell',
      muscleGroup: asString(row.muscleGroup) ?? '',
      prescribedSets: asInt(row.prescribedSets),
      repMin: asInt(row.repMin),
      repMax: asInt(row.repMax),
      measure: asMeasure(row.measure),
      perSide: row.perSide === true,
      isBodyweight: row.isBodyweight === true,
      tempo: asString(row.tempo),
      targetRpe: asNumber(row.targetRpe),
      percent1rm: asNumber(row.percent1rm),
      targetWeightKg: asNumber(row.targetWeightKg),
      targetDistanceM: asNumber(row.targetDistanceM),
      targetDurationS: asNumber(row.targetDurationS),
      restSeconds: asInt(row.restSeconds),
      progressionRule: asString(row.progressionRule) ?? 'double_progression',
      notes: asString(row.notes),
    };
  });

  if (!planRaw)
    return {
      plan: null,
      rows,
      days: normalizeProgrammeDays(raw.days),
      hasActiveBlock: typeof raw.hasActiveBlock === 'boolean' ? raw.hasActiveBlock : undefined,
    };

  const track = asTrack(planRaw.track);
  const weeks = Math.max(1, asInt(planRaw.weeks, 1) || 1);
  const deloadRaw = asNumber(planRaw.deloadWeek);
  const deloadWeek = deloadRaw === null ? null : Math.round(deloadRaw);
  const dayCount = new Set(rows.map((row) => row.dayId)).size;

  return {
    plan: {
      planId: asString(planRaw.planId) ?? '',
      name: asString(planRaw.name) ?? 'Training plan',
      goal: asString(planRaw.goal),
      track,
      trackLabel: asString(planRaw.trackLabel) ?? trackMeta(track).label,
      metricsFocus: asArray<unknown>(planRaw.metricsFocus).filter((item): item is string => typeof item === 'string'),
      weeks,
      daysPerWeek: asInt(planRaw.daysPerWeek, dayCount) || dayCount,
      source: asString(planRaw.source) ?? 'coach',
      isActive: planRaw.isActive === true,
      coachOwned: planRaw.coachOwned === true,
      deloadWeek,
      weekPlan: asWeekPlan(planRaw.weekPlan, weeks, deloadWeek),
      startsOn: asString(planRaw.startsOn),
      block: normalizeBlockStatus(planRaw.block),
    },
    rows,
    days: normalizeProgrammeDays(raw.days),
    warmup: normalizeProgrammeWarmup(raw.warmup),
    hasActiveBlock: typeof raw.hasActiveBlock === 'boolean' ? raw.hasActiveBlock : undefined,
  };
}

/**
 * Parse the block's warm-up session. `null`/absent means the block has none —
 * the builder then renders exactly as it did before warm-ups existed.
 */
function normalizeProgrammeWarmup(value: unknown): ProgrammeWarmup | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const dayId = asString(raw.dayId);
  if (!dayId) return null;
  const rows = asArray<unknown>(raw.rows).map((entry): ProgrammeRow => {
    const row = asRecord(entry);
    const supersetGroup = asNumber(row.supersetGroup);
    const track = asTrack(row.track);
    return {
      planId: asString(row.planId) ?? '',
      planName: asString(row.planName) ?? '',
      planGoal: asString(row.planGoal),
      track,
      dayId: asString(row.dayId) ?? dayId,
      dayIndex: 0,
      dayLabel: asString(row.dayLabel) ?? 'Warm-up',
      dayFocus: asArray<unknown>(row.dayFocus).filter((item): item is string => typeof item === 'string'),
      planExerciseId: asString(row.planExerciseId) ?? '',
      orderIndex: asInt(row.orderIndex, 1) || 1,
      supersetGroup,
      groupType: asGroupType(row.groupType, supersetGroup),
      exerciseId: asString(row.exerciseId) ?? '',
      exerciseName: asString(row.exerciseName) ?? asString(row.movement) ?? 'Exercise',
      movement: asString(row.movement),
      variant: asString(row.variant),
      equipment: asString(row.equipment) ?? 'bodyweight',
      muscleGroup: asString(row.muscleGroup) ?? '',
      prescribedSets: asInt(row.prescribedSets),
      repMin: asInt(row.repMin),
      repMax: asInt(row.repMax),
      measure: asMeasure(row.measure),
      perSide: row.perSide === true,
      isBodyweight: row.isBodyweight === true,
      tempo: asString(row.tempo),
      targetRpe: asNumber(row.targetRpe),
      percent1rm: asNumber(row.percent1rm),
      targetWeightKg: asNumber(row.targetWeightKg),
      targetDistanceM: asNumber(row.targetDistanceM),
      targetDurationS: asNumber(row.targetDurationS),
      restSeconds: asInt(row.restSeconds),
      progressionRule: asString(row.progressionRule) ?? 'double_progression',
      notes: asString(row.notes),
    };
  });
  return {
    dayId,
    label: asString(raw.label) ?? 'Warm-up',
    focus: asArray<unknown>(raw.focus).filter((item): item is string => typeof item === 'string'),
    rows: rows.sort((a, b) => a.orderIndex - b.orderIndex),
  };
}

/** Parse the optional `days` envelope; absent means "group the rows instead". */
function normalizeProgrammeDays(value: unknown): ProgrammeDaySummary[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map((entry, index) => {
    const raw = asRecord(entry);
    return {
      id: asString(raw.id) ?? asString(raw.dayId) ?? `day-${index + 1}`,
      dayIndex: asInt(raw.dayIndex, index + 1) || index + 1,
      label: asString(raw.label) ?? `Day ${index + 1}`,
      focus: asArray<unknown>(raw.focus).filter((item): item is string => typeof item === 'string'),
      exerciseCount: asInt(raw.exerciseCount, asArray<unknown>(raw.exercises).length),
    };
  });
}

export function normalizePlanHistory(value: unknown): PlanHistoryEntry[] {
  const raw = asRecord(value);
  const list = Array.isArray(value) ? asArray<unknown>(value) : asArray<unknown>(raw.plans ?? raw.data);
  return list.map((entry): PlanHistoryEntry => {
    const row = asRecord(entry);
    const track = asTrack(row.track);
    return {
      planId: asString(row.planId) ?? asString(row.id) ?? '',
      name: asString(row.name) ?? 'Training plan',
      track,
      trackLabel: asString(row.trackLabel) ?? trackMeta(track).label,
      weeks: asInt(row.weeks, 1) || 1,
      daysPerWeek: asInt(row.daysPerWeek),
      isActive: row.isActive === true,
      coachOwned: row.coachOwned === true,
      createdAt: asString(row.createdAt),
      activatedAt: asString(row.activatedAt),
      archivedAt: asString(row.archivedAt),
      archivedReason: asString(row.archivedReason),
      startsOn: asString(row.startsOn),
      copiedFromPlanId: asString(row.copiedFromPlanId),
      dayCount: asInt(row.dayCount),
      exerciseCount: asInt(row.exerciseCount),
      // The block's output. Absent from an older API build, so default to 0
      // rather than inventing activity that the server did not report.
      sessionsCompleted: asInt(row.sessionsCompleted),
      setsLogged: asInt(row.setsLogged),
      daysLogged: asInt(row.daysLogged),
      lastSessionAt: asString(row.lastSessionAt),
    };
  });
}

function normalizeSessionSet(value: unknown): SessionSet {
  const raw = asRecord(value);
  const status = raw.status;
  return {
    id: asString(raw.id) ?? '',
    sessionExerciseId: asString(raw.sessionExerciseId) ?? '',
    exerciseId: asString(raw.exerciseId) ?? '',
    setIndex: asInt(raw.setIndex, 1) || 1,
    weightKg: asNumber(raw.weightKg) ?? 0,
    reps: asInt(raw.reps),
    rpe: asNumber(raw.rpe),
    status:
      status === 'done' || status === 'pr' || status === 'below' || status === 'failed' || status === 'pending'
        ? status
        : 'pending',
    isPr: raw.isPr === true,
    isWarmup: raw.isWarmup === true,
    measure: asMeasure(raw.measure),
    distanceM: asNumber(raw.distanceM),
    durationS: asNumber(raw.durationS),
    perSide: raw.perSide === true,
    tempo: asString(raw.tempo),
    painScore: asNumber(raw.painScore),
    performedAt: asString(raw.performedAt) ?? new Date().toISOString(),
    clientSetId: asString(raw.clientSetId),
  };
}

function normalizeSessionExercise(value: unknown): SessionExercise {
  const raw = asRecord(value);
  return {
    id: asString(raw.id) ?? '',
    sessionId: asString(raw.sessionId) ?? '',
    exerciseId: asString(raw.exerciseId) ?? '',
    orderIndex: asInt(raw.orderIndex, 1) || 1,
    exerciseName: asString(raw.exerciseName) ?? 'Exercise',
    prescription: asRecord(raw.prescription),
    notes: asString(raw.notes),
    isWarmup: raw.isWarmup === true,
    loggedSets: asArray<unknown>(raw.loggedSets).map(normalizeSessionSet),
  };
}

function normalizeSessionTotals(value: unknown): SessionTotalsWire | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  return {
    sessionId: asString(raw.sessionId) ?? '',
    label: asString(raw.label),
    startedAt: asString(raw.startedAt) ?? new Date().toISOString(),
    endedAt: asString(raw.endedAt),
    status: raw.status === 'completed' || raw.status === 'abandoned' ? raw.status : 'active',
    volumeKg: asNumber(raw.volumeKg) ?? 0,
    totalReps: asInt(raw.totalReps),
    setsCompleted: asInt(raw.setsCompleted),
    setsPrescribed: asNumber(raw.setsPrescribed),
    prCount: asInt(raw.prCount),
    failedSets: asInt(raw.failedSets),
    avgRpe: asNumber(raw.avgRpe),
    durationMin: asInt(raw.durationMin),
  };
}

/** Derive totals from the logged sets when the API sends none. */
function totalsFromSets(sessionId: string, exercises: SessionExercise[]): SessionTotalsWire {
  let volumeKg = 0;
  let totalReps = 0;
  let setsCompleted = 0;
  let setsPrescribed = 0;
  let prCount = 0;
  let rpeSum = 0;
  let rpeCount = 0;
  for (const exercise of exercises) {
    // Warm-up movements are prescribed light work, not working sets: their
    // prescriptions never count against the session's prescribed total.
    if (exercise.isWarmup === true) continue;
    const prescribed = asInt(asRecord(exercise.prescription).sets);
    setsPrescribed += prescribed;
    for (const set of exercise.loggedSets) {
      if (set.isWarmup) continue;
      if (set.status === 'pending') continue;
      volumeKg += set.weightKg * set.reps;
      totalReps += set.reps;
      setsCompleted += 1;
      if (set.isPr || set.status === 'pr') prCount += 1;
      if (set.rpe !== null) {
        rpeSum += set.rpe;
        rpeCount += 1;
      }
    }
  }
  return {
    sessionId,
    label: null,
    startedAt: new Date().toISOString(),
    endedAt: null,
    status: 'active',
    volumeKg: Math.round(volumeKg * 100) / 100,
    totalReps,
    setsCompleted,
    setsPrescribed,
    prCount,
    failedSets: 0,
    avgRpe: rpeCount === 0 ? null : Math.round((rpeSum / rpeCount) * 10) / 10,
    durationMin: 0,
  };
}

function normalizeWorkoutSessionWire(raw: Record<string, unknown>, fallbackTime: string): WorkoutSessionWire {
  return {
    id: asString(raw.id) ?? '',
    planDayId: asString(raw.planDayId),
    label: asString(raw.label),
    startedAt: asString(raw.startedAt) ?? fallbackTime,
    endedAt: asString(raw.endedAt),
    status: raw.status === 'completed' || raw.status === 'abandoned' ? raw.status : 'active',
    perceivedExertion: asNumber(raw.perceivedExertion),
    moodScore: asNumber(raw.moodScore),
    track: asTrack(raw.track),
  };
}

export function normalizeActiveSession(value: unknown): ActiveSessionPayload {
  const raw = asRecord(value);
  const serverTime = asString(raw.serverTime) ?? new Date().toISOString();
  const softCompletionUntil = asString(raw.softCompletionUntil);
  const sessionRaw = raw.session === null || raw.session === undefined ? null : asRecord(raw.session);
  if (!sessionRaw) {
    return { session: null, sessionExercises: [], totals: null, serverTime, softCompletionUntil };
  }
  const exercises = asArray<unknown>(raw.sessionExercises).map(normalizeSessionExercise);
  const sessionId = asString(sessionRaw.id) ?? '';
  const totalsRaw = normalizeSessionTotals(raw.totals);
  return {
    session: normalizeWorkoutSessionWire(sessionRaw, serverTime),
    sessionExercises: exercises,
    totals: totalsRaw ?? totalsFromSets(sessionId, exercises),
    serverTime,
    softCompletionUntil,
  };
}

/* -------------------- session detail (the coach/lifter table) -------------- */

const SESSION_FLAG_CODES: readonly SessionFlagCode[] = [
  'sets_skipped',
  'sets_added',
  'rep_range_missed',
  'load_below_prescription',
  'load_above_prescription',
  'rpe_far_from_target',
  'rest_too_short',
  'rest_too_long',
  'tempo_not_followed',
  'pain_reported',
  'warmup_logged_as_working',
  'movement_missing',
  'movement_substituted',
];

function isSessionFlagCode(value: unknown): value is SessionFlagCode {
  return typeof value === 'string' && (SESSION_FLAG_CODES as readonly string[]).includes(value);
}

function normalizeExerciseCompliance(value: unknown): SessionExerciseComplianceWire {
  const raw = asRecord(value);
  return {
    sessionExerciseId: asString(raw.sessionExerciseId) ?? '',
    exerciseName: asString(raw.exerciseName) ?? 'Exercise',
    setsPrescribed: asInt(raw.setsPrescribed),
    setsCompleted: asInt(raw.setsCompleted),
    repRangeMet: raw.repRangeMet === true,
    loadOnTarget: typeof raw.loadOnTarget === 'boolean' ? raw.loadOnTarget : null,
    avgRpe: asNumber(raw.avgRpe),
    targetRpe: asNumber(raw.targetRpe),
    failedSets: asInt(raw.failedSets),
    prCount: asInt(raw.prCount),
  };
}

export function normalizeSessionCompliance(value: unknown): SessionComplianceWire {
  const raw = asRecord(value);
  return {
    setsPrescribed: asInt(raw.setsPrescribed),
    setsCompleted: asInt(raw.setsCompleted),
    exercisesPrescribed: asInt(raw.exercisesPrescribed),
    exercisesCompleted: asInt(raw.exercisesCompleted),
    repRangeMet: raw.repRangeMet === true,
    loadOnTarget: typeof raw.loadOnTarget === 'boolean' ? raw.loadOnTarget : null,
    avgRpe: asNumber(raw.avgRpe),
    targetRpe: asNumber(raw.targetRpe),
    failedSets: asInt(raw.failedSets),
    prCount: asInt(raw.prCount),
    summary: asString(raw.summary) ?? '',
    exercises: asArray<unknown>(raw.exercises).map(normalizeExerciseCompliance),
  };
}

function normalizeSessionFlag(value: unknown): SessionFlagWire | null {
  const raw = asRecord(value);
  if (!isSessionFlagCode(raw.code)) return null;
  const target = asRecord(raw.target);
  const severity = raw.severity === 'danger' || raw.severity === 'info' ? raw.severity : 'warning';
  return {
    code: raw.code,
    severity,
    source: raw.source === 'evaluation_matrix' ? 'evaluation_matrix' : 'session_log',
    evidence: asString(raw.evidence) ?? '',
    evidenceData: asRecord(raw.evidenceData) as SessionFlagWire['evidenceData'],
    suggestedAction: asString(raw.suggestedAction) ?? '',
    target: {
      sessionExerciseId: asString(target.sessionExerciseId) ?? '',
      setIndex: typeof target.setIndex === 'number' ? Math.trunc(target.setIndex) : null,
    },
  };
}

function normalizeSessionBlockContext(value: unknown): SessionBlockContextWire | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const planId = asString(raw.planId);
  if (!planId) return null;
  return {
    planId,
    planName: asString(raw.planName) ?? 'Block',
    dayLabel: asString(raw.dayLabel) ?? 'Day',
    dayIndex: asInt(raw.dayIndex, 1) || 1,
    startsOn: asString(raw.startsOn),
    endsOn: asString(raw.endsOn),
    weeks: asInt(raw.weeks, 1) || 1,
    weekOf: asInt(raw.weekOf, 1) || 1,
    isDeload: raw.isDeload === true,
  };
}

export function normalizeSessionView(value: unknown): SessionViewWire | null {
  const raw = asRecord(value);
  const sessionRaw = raw.session === null || raw.session === undefined ? null : asRecord(raw.session);
  if (!sessionRaw) return null;
  const session = normalizeWorkoutSessionWire(sessionRaw, new Date().toISOString());
  if (!session.id) return null;
  const sessionExercises = asArray<unknown>(raw.sessionExercises).map(normalizeSessionExercise);
  const totals = normalizeSessionTotals(raw.totals) ?? totalsFromSets(session.id, sessionExercises);
  const flags = Array.isArray(raw.flags)
    ? asArray<unknown>(raw.flags)
        .map(normalizeSessionFlag)
        .filter((flag): flag is SessionFlagWire => flag !== null)
    : undefined;
  return {
    session,
    sessionExercises,
    totals,
    compliance: normalizeSessionCompliance(raw.compliance),
    block: normalizeSessionBlockContext(raw.block),
    ...(flags === undefined ? {} : { flags }),
  };
}

export function normalizeSessionSetResponse(value: unknown): SessionSetResponse {
  const raw = asRecord(value);
  const set = normalizeSessionSet(raw.set);
  const personalRaw = raw.personalRecord === null || raw.personalRecord === undefined ? null : asRecord(raw.personalRecord);
  return {
    set,
    personalRecord: personalRaw
      ? {
          id: asString(personalRaw.id) ?? '',
          exerciseId: asString(personalRaw.exerciseId) ?? set.exerciseId,
          exerciseName: asString(personalRaw.exerciseName),
          weightKg: asNumber(personalRaw.weightKg) ?? set.weightKg,
          reps: asInt(personalRaw.reps, set.reps) || set.reps,
          estimated1rmKg: asNumber(personalRaw.estimated1rmKg) ?? 0,
          previousEstimated1rmKg: asNumber(personalRaw.previousEstimated1rmKg),
          achievedAt: asString(personalRaw.achievedAt) ?? new Date().toISOString(),
        }
      : null,
    sessionTotals: normalizeSessionTotals(raw.sessionTotals) ?? totalsFromSets(set.id, []),
    coachHint: asString(raw.coachHint),
    idempotentReplay: raw.idempotentReplay === true,
    slotConflict: raw.slotConflict === true,
  };
}

export function normalizeCompleteSession(value: unknown): CompleteSessionResponse {
  const raw = asRecord(value);
  const sessionRaw = asRecord(raw.session);
  const totals = normalizeSessionTotals(raw.totals) ?? totalsFromSets(asString(sessionRaw.id) ?? '', []);
  return {
    session: {
      id: asString(sessionRaw.id) ?? '',
      planDayId: asString(sessionRaw.planDayId),
      label: asString(sessionRaw.label),
      startedAt: asString(sessionRaw.startedAt) ?? new Date().toISOString(),
      endedAt: asString(sessionRaw.endedAt),
      status: 'completed',
      perceivedExertion: asNumber(sessionRaw.perceivedExertion),
      moodScore: asNumber(sessionRaw.moodScore),
      track: asTrack(sessionRaw.track),
    },
    totals,
    coachRunInvalidated: raw.coachRunInvalidated === true,
  };
}

/* ---------------------------------------------------------------------------
   The coach ↔ client relationship (contract 0010, live on the running API).

   One relationship model, seen from both ends: the lifter's "connect my coach"
   and the coach's roster are the same `trainee_links` row. The client only ever
   talks to `/coaching/*`; the field names below are the wire identifiers.
   ------------------------------------------------------------------------ */

export interface CoachingParty {
  userId: string;
  displayName: string;
  email: string;
}

export type CoachLinkStatus = 'invited' | 'active' | 'revoked';
export type CoachLinkOrigin = 'lifter_invited' | 'coach_invited';

export interface CoachLink {
  linkId: string;
  status: CoachLinkStatus;
  origin: CoachLinkOrigin | null;
  sharedFields: SharedField[];
  linkedAt: string | null;
  createdAt: string;
  inviteId: string | null;
  coach: CoachingParty | null;
  trainee: CoachingParty | null;
}

export type CoachInviteDirection = 'sent' | 'received';
export type CoachInviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface CoachInvite {
  id: string;
  code: string;
  direction: CoachInviteDirection;
  inviterRole: 'lifter' | 'coach';
  status: CoachInviteStatus;
  inviteeEmail: string | null;
  inviteeName: string | null;
  sharedFields: SharedField[];
  createdAt: string;
  expiresAt: string;
  expired: boolean;
  acceptedAt: string | null;
  linkId: string | null;
  /** The answering side's note on an accept/decline (migration 0025), or null. */
  decisionMessage: string | null;
  /** When the request was accepted or declined (migration 0025). */
  decidedAt: string | null;
  /** Relative join path the API prints, e.g. `/coach/join?code=…`. */
  shareUrl: string;
  inviter: CoachingParty | null;
  coach: CoachingParty | null;
  trainee: CoachingParty | null;
  /** The pack this invite offers (migration 0020), or null. */
  pack: CoachPack | null;
}

/** `GET /coaching/requests` — the coach's lifter-request inbox + badge count. */
export interface CoachRequestList {
  data: CoachInvite[];
  pending: number;
}

/**
 * The tier of one coach↔lifter relationship (0026), derived server-side:
 * inquiry (chatting, not connected) · connected (no pack) · paid (a pack).
 */
export type ConnectionTier = 'inquiry' | 'connected' | 'paid';

/** One chatter in the coach's one-stop chat (`GET /coaching/threads`). */
export interface CoachThread {
  tier: ConnectionTier;
  linkId: string;
  userId: string;
  displayName: string;
  email: string;
  sharedFields: SharedField[];
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastMessageMine: boolean;
  unread: number;
  requestId: string | null;
  requestCode: string | null;
  /** False when the lifter is connected to another coach (chat still works). */
  connectable: boolean;
  /**
   * 0030/0034 — true when the coach OWNS this relationship; false when they
   * manage it for a gym (the gym is `ownerName`). Always true for a lifter.
   */
  owned: boolean;
  ownerName: string | null;
}

export interface CoachThreadList {
  data: CoachThread[];
  total: number;
  unread: number;
  paid: number;
  connected: number;
  inquiry: number;
  /** Coach side only: relationships managed for a gym, not owned. */
  managed: number;
}

/** One prospect in the coach's prospect surface (`GET /coaching/prospects`). */
export interface CoachProspect {
  tier: 'inquiry' | 'connected';
  userId: string;
  displayName: string;
  email: string;
  linkId: string | null;
  requestId: string | null;
  requestCode: string | null;
  sharedFields: SharedField[];
  linkedAt: string | null;
  lastMessageAt: string | null;
  unread: number;
  /** False when the lifter is connected to another coach (chat still works). */
  connectable: boolean;
  /**
   * 0030/0034 — true when the coach OWNS this relationship; false when they
   * manage it for a gym (the gym is `ownerName`). Always true for a lifter.
   */
  owned: boolean;
  ownerName: string | null;
}

export interface CoachProspectList {
  data: CoachProspect[];
  total: number;
  inquiry: number;
  connected: number;
}

export interface CoachingLinkPayload {
  link: CoachLink | null;
  pendingInvite: CoachInvite | null;
}

export interface InviteCoachInput {
  /** Optional: a coach with no account yet can still be invited by name. */
  email?: string;
  name?: string;
  /** A coach → client invite addresses the lifter instead. */
  traineeEmail?: string;
  traineeName?: string;
  sharedFields?: SharedField[];
  /** A discovered coach's id (lifter → coach) — the discovery "request to connect". */
  coachId?: string;
  /** The pack a coach→client invite offers (migration 0020). */
  packId?: string;
}

export interface AcceptCoachInviteResult {
  invite: CoachInvite;
  link: CoachLink | null;
  user: User;
  /** Present only when the code created a brand-new profile. */
  accessToken?: string;
  refreshToken?: string;
}

export interface CoachSeatUsage {
  plan: 'free' | 'coach' | 'gym' | 'gym_plus' | 'scale';
  /** null means unlimited (the Scale plan). */
  limit: number | null;
  /** PAID clients (active pack subscriptions) — what the cap compares (0026). */
  active: number;
  /** Live inquiries (a badge, never a paid seat). */
  pending: number;
  /** Connected-but-unpaid lifters (a badge, never a paid seat). */
  connected: number;
  used: number;
  remaining: number | null;
}

/** 0030: one gym invite. */
export function normalizeInternalCoachInvite(value: unknown): InternalCoachInvite {
  const raw = asRecord(value);
  const status =
    raw.status === 'accepted' || raw.status === 'expired' || raw.status === 'revoked'
      ? raw.status
      : 'pending';
  return {
    id: asString(raw.id) ?? '',
    code: asString(raw.code) ?? '',
    email: asString(raw.email) ?? '',
    label: asString(raw.label),
    status,
    createdAt: asString(raw.createdAt) ?? new Date().toISOString(),
    expiresAt: asString(raw.expiresAt) ?? new Date().toISOString(),
    expired: raw.expired === true,
    acceptedAt: asString(raw.acceptedAt),
    shareUrl: asString(raw.shareUrl) ?? '',
  };
}

/* ---------------------------------------------------------------------------
   Platform support (migration 0035)
   ------------------------------------------------------------------------- */

export type SupportAuthorKind = 'user' | 'staff' | 'bot' | 'admin';

export interface SupportMessage {
  id: string;
  threadId: string;
  body: string;
  /** True when the platform (a person or the assistant) wrote it. */
  fromAdmin: boolean;
  /** Who wrote it: the account, an agent, the assistant, or the founder. */
  authorKind: SupportAuthorKind;
  senderId: string;
  createdAt: string;
}

export interface SupportConversation {
  threadId: string;
  status: 'open' | 'closed';
  displayName: string;
  email?: string;
  unread: number;
  messages: SupportMessage[];
  /** 0038 — the current help session (queued / with a person / with the bot). */
  session: SupportSession | null;
  /** 0037 — present on the ADMIN read only: the account's review of Lift2Fit. */
  review?: PlatformReview | null;
}

/* ----------------------- the support team (0038) ------------------------ */

export type SupportSessionStatus = 'queued' | 'assigned' | 'bot' | 'closed';

export interface SupportSession {
  sessionId: string;
  status: SupportSessionStatus;
  staffId: string | null;
  staffName: string | null;
  openedAt: string;
  assignedAt: string | null;
  closedAt: string | null;
  resolution: string | null;
  /** 1-based place in the queue, when queued. */
  queuePosition: number | null;
  waitSeconds: number | null;
  escalationReason: string | null;
}

export interface SupportStaffStats {
  opened: number;
  handled: number;
  resolved: number;
  escalated: number;
  avgFirstResponseSec: number | null;
  avgResolutionSec: number | null;
  avgWaitSec: number | null;
}

export interface SupportStaffView {
  userId: string;
  displayName: string;
  email: string;
  isBot: boolean;
  active: boolean;
  onShift: boolean;
  shiftStartMinute: number;
  shiftEndMinute: number;
  shiftStart: string;
  shiftEnd: string;
  timezone: string;
  capacity: number;
  inShiftWindow: boolean;
  available: boolean;
  openChats: number;
  stats: SupportStaffStats;
}

export interface SupportTeamSummary {
  windowDays: number;
  started: number;
  served: number;
  resolved: number;
  escalated: number;
  waiting: number;
  botEnabled: boolean;
  botHandled: number;
  botResolved: number;
  staffOnline: number;
  staffTotal: number;
  availableStaff: number;
  avgWaitSec: number | null;
  avgFirstResponseSec: number | null;
  avgResolutionSec: number | null;
  peakHour: string | null;
  staffingAlert: boolean;
}

export interface SupportStaffList {
  summary: SupportTeamSummary;
  bot: SupportStaffView | null;
  data: SupportStaffView[];
  total: number;
}

export interface SupportStaffSessionRow {
  sessionId: string;
  threadId: string;
  clientName: string;
  clientEmail: string;
  status: SupportSessionStatus;
  openedAt: string;
  assignedAt: string | null;
  closedAt: string | null;
  resolution: string | null;
  waitSeconds: number | null;
  handleSeconds: number | null;
}

export interface SupportStaffDetail {
  staff: SupportStaffView;
  sessions: SupportStaffSessionRow[];
}

export interface SupportInbox {
  staff: SupportStaffView;
  summary: SupportTeamSummary;
  assigned: SupportThreadSummary[];
  queued: SupportThreadSummary[];
}

/** 0037 — one account's rating of Lift2Fit, written beside the help thread. */
export interface PlatformReview {
  reviewId: string;
  userId: string;
  rating: number;
  body: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportThreadSummary {
  threadId: string;
  userId: string;
  displayName: string;
  email: string;
  roles: string[];
  coachPlan: string | null;
  status: 'open' | 'closed';
  createdAt: string;
  lastMessageAt: string | null;
  lastMessage: string | null;
  lastFromAdmin: boolean;
  unread: number;
  totalMessages: number;
  /** 0037 — the account's rating of Lift2Fit, when it has left one. */
  rating: number | null;
  /** 0038 — the open help session, when there is one. */
  sessionId?: string;
  sessionStatus?: SupportSessionStatus;
  sessionStaffId?: string | null;
  sessionStaffName?: string | null;
  sessionOpenedAt?: string | null;
}

export interface SupportThreadList {
  data: SupportThreadSummary[];
  total: number;
  unread: number;
}

const AUTHOR_KINDS: ReadonlyArray<SupportAuthorKind> = ['user', 'staff', 'bot', 'admin'];

function asSessionStatus(value: unknown): SupportSessionStatus | undefined {
  return value === 'queued' || value === 'assigned' || value === 'bot' || value === 'closed'
    ? value
    : undefined;
}

export function normalizeSupportMessage(value: unknown): SupportMessage {
  const raw = asRecord(value);
  const fromAdmin = raw.fromAdmin === true;
  const kind = asString(raw.authorKind);
  return {
    id: asString(raw.id) ?? '',
    threadId: asString(raw.threadId) ?? '',
    body: asString(raw.body) ?? '',
    fromAdmin,
    authorKind: AUTHOR_KINDS.includes(kind as SupportAuthorKind)
      ? (kind as SupportAuthorKind)
      : fromAdmin
        ? 'admin'
        : 'user',
    senderId: asString(raw.senderId) ?? '',
    createdAt: asString(raw.createdAt) ?? new Date().toISOString(),
  };
}

export function normalizeSupportSession(value: unknown): SupportSession | null {
  if (!value) return null;
  const raw = asRecord(value);
  const status = asSessionStatus(raw.status);
  if (!status) return null;
  return {
    sessionId: asString(raw.sessionId) ?? '',
    status,
    staffId: asString(raw.staffId),
    staffName: asString(raw.staffName),
    openedAt: asString(raw.openedAt) ?? new Date().toISOString(),
    assignedAt: asString(raw.assignedAt),
    closedAt: asString(raw.closedAt),
    resolution: asString(raw.resolution),
    queuePosition: asNumber(raw.queuePosition),
    waitSeconds: asNumber(raw.waitSeconds),
    escalationReason: asString(raw.escalationReason),
  };
}

export function normalizeSupportStaff(value: unknown): SupportStaffView {
  const raw = asRecord(value);
  const statsRaw = asRecord(raw.stats);
  return {
    userId: asString(raw.userId) ?? '',
    displayName: asString(raw.displayName) ?? 'Support',
    email: asString(raw.email) ?? '',
    isBot: raw.isBot === true,
    active: raw.active !== false,
    onShift: raw.onShift === true,
    shiftStartMinute: asNumber(raw.shiftStartMinute) ?? 540,
    shiftEndMinute: asNumber(raw.shiftEndMinute) ?? 1020,
    shiftStart: asString(raw.shiftStart) ?? '09:00',
    shiftEnd: asString(raw.shiftEnd) ?? '17:00',
    timezone: asString(raw.timezone) ?? 'Europe/London',
    capacity: asNumber(raw.capacity) ?? 5,
    inShiftWindow: raw.inShiftWindow === true,
    available: raw.available === true,
    openChats: asNumber(raw.openChats) ?? 0,
    stats: {
      opened: asNumber(statsRaw.opened) ?? 0,
      handled: asNumber(statsRaw.handled) ?? 0,
      resolved: asNumber(statsRaw.resolved) ?? 0,
      escalated: asNumber(statsRaw.escalated) ?? 0,
      avgFirstResponseSec: asNumber(statsRaw.avgFirstResponseSec),
      avgResolutionSec: asNumber(statsRaw.avgResolutionSec),
      avgWaitSec: asNumber(statsRaw.avgWaitSec),
    },
  };
}

export function normalizeSupportTeamSummary(value: unknown): SupportTeamSummary {
  const raw = asRecord(value);
  return {
    windowDays: asNumber(raw.windowDays) ?? 7,
    started: asNumber(raw.started) ?? 0,
    served: asNumber(raw.served) ?? 0,
    resolved: asNumber(raw.resolved) ?? 0,
    escalated: asNumber(raw.escalated) ?? 0,
    waiting: asNumber(raw.waiting) ?? 0,
    botEnabled: raw.botEnabled === true,
    botHandled: asNumber(raw.botHandled) ?? 0,
    botResolved: asNumber(raw.botResolved) ?? 0,
    staffOnline: asNumber(raw.staffOnline) ?? 0,
    staffTotal: asNumber(raw.staffTotal) ?? 0,
    availableStaff: asNumber(raw.availableStaff) ?? 0,
    avgWaitSec: asNumber(raw.avgWaitSec),
    avgFirstResponseSec: asNumber(raw.avgFirstResponseSec),
    avgResolutionSec: asNumber(raw.avgResolutionSec),
    peakHour: asString(raw.peakHour),
    staffingAlert: raw.staffingAlert === true,
  };
}

export function normalizeSupportStaffList(value: unknown): SupportStaffList {
  const raw = asRecord(value);
  return {
    summary: normalizeSupportTeamSummary(raw.summary),
    bot: raw.bot ? normalizeSupportStaff(raw.bot) : null,
    data: asArray<unknown>(raw.data).map(normalizeSupportStaff),
    total: asNumber(raw.total) ?? 0,
  };
}

export function normalizeSupportStaffDetail(value: unknown): SupportStaffDetail {
  const raw = asRecord(value);
  return {
    staff: normalizeSupportStaff(raw.staff),
    sessions: asArray<unknown>(raw.sessions).map((entry) => {
      const row = asRecord(entry);
      return {
        sessionId: asString(row.sessionId) ?? '',
        threadId: asString(row.threadId) ?? '',
        clientName: asString(row.clientName) ?? 'Account',
        clientEmail: asString(row.clientEmail) ?? '',
        status: asSessionStatus(row.status) ?? 'closed',
        openedAt: asString(row.openedAt) ?? new Date().toISOString(),
        assignedAt: asString(row.assignedAt),
        closedAt: asString(row.closedAt),
        resolution: asString(row.resolution),
        waitSeconds: asNumber(row.waitSeconds),
        handleSeconds: asNumber(row.handleSeconds),
      };
    }),
  };
}

export function normalizeSupportInbox(value: unknown): SupportInbox {
  const raw = asRecord(value);
  const list = normalizeSupportThreadList({ data: raw.assigned ?? [] });
  const queued = normalizeSupportThreadList({ data: raw.queued ?? [] });
  return {
    staff: normalizeSupportStaff(raw.staff),
    summary: normalizeSupportTeamSummary(raw.summary),
    assigned: list.data,
    queued: queued.data,
  };
}

export function normalizePlatformReview(value: unknown): PlatformReview | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const rating = asNumber(raw.rating);
  if (rating === null || rating < 1 || rating > 5) return null;
  return {
    reviewId: asString(raw.reviewId) ?? '',
    userId: asString(raw.userId) ?? '',
    rating,
    body: asString(raw.body),
    createdAt: asString(raw.createdAt) ?? new Date().toISOString(),
    updatedAt: asString(raw.updatedAt) ?? new Date().toISOString(),
  };
}

export function normalizeSupportConversation(value: unknown): SupportConversation {
  const raw = asRecord(value);
  return {
    threadId: asString(raw.threadId) ?? '',
    status: raw.status === 'closed' ? 'closed' : 'open',
    displayName: asString(raw.displayName) ?? 'Help',
    email: asString(raw.email) ?? undefined,
    unread: asNumber(raw.unread) ?? 0,
    messages: asArray<unknown>(raw.messages).map(normalizeSupportMessage),
    session: normalizeSupportSession(raw.session),
    review: 'review' in raw ? normalizePlatformReview(raw.review) : undefined,
  };
}

export function normalizeSupportThreadList(value: unknown): SupportThreadList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => {
      const row = asRecord(entry);
      return {
        threadId: asString(row.threadId) ?? '',
        userId: asString(row.userId) ?? '',
        displayName: asString(row.displayName) ?? 'Account',
        email: asString(row.email) ?? '',
        roles: asArray<unknown>(row.roles).map((role) => String(role)),
        coachPlan: asString(row.coachPlan),
        status: row.status === 'closed' ? 'closed' : 'open',
        createdAt: asString(row.createdAt) ?? new Date().toISOString(),
        lastMessageAt: asString(row.lastMessageAt),
        lastMessage: asString(row.lastMessage),
        lastFromAdmin: row.lastFromAdmin === true,
        unread: asNumber(row.unread) ?? 0,
        totalMessages: asNumber(row.totalMessages) ?? 0,
        rating: asNumber(row.rating),
        sessionId: asString(row.sessionId) ?? undefined,
        sessionStatus: asSessionStatus(row.sessionStatus),
        sessionStaffId: asString(row.sessionStaffId),
        sessionStaffName: asString(row.sessionStaffName),
        sessionOpenedAt: asString(row.sessionOpenedAt),
      };
    }),
    total: asNumber(raw.total) ?? 0,
    unread: asNumber(raw.unread) ?? 0,
  };
}

/** 0030: the gym's internal coaches, the cap and pending invites. */
export function normalizeInternalCoachList(value: unknown): InternalCoachList {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map((entry) => {
    const row = asRecord(entry);
    const status = row.status === 'removed' ? 'removed' : 'active';
    return {
      coachId: asString(row.coachId) ?? '',
      displayName: asString(row.displayName) ?? 'Coach',
      email: asString(row.email) ?? '',
      status,
      linkedAt: asString(row.linkedAt),
      clientsManaged: asInt(row.clientsManaged),
      adherencePct: asNumber(row.adherencePct),
      sessionsLast7: asInt(row.sessionsLast7),
      unreadChats: asInt(row.unreadChats),
      inactiveClients: asInt(row.inactiveClients),
    } satisfies InternalCoach;
  });
  const limit = asNumber(raw.limit);
  return {
    data,
    total: asInt(raw.total, data.length) || data.length,
    limit: limit === null ? null : Math.round(limit),
    used: asInt(raw.used, data.length),
    remaining: asNumber(raw.remaining),
    plan: asCoachPlan(raw.plan),
    invites: asArray<unknown>(raw.invites).map(normalizeInternalCoachInvite),
  };
}

export interface CoachRoster {
  data: Trainee[];
  total: number;
  flagged: number;
  seats: CoachSeatUsage;
  /** 0030: the gym this account works for, when it is an internal coach. */
  internalCoachOf?: { coachId: string; displayName: string } | null;
}

/** One internal coach of the calling gym, with the owner's overview (0030). */
export interface InternalCoach {
  coachId: string;
  displayName: string;
  email: string;
  status: 'active' | 'removed';
  linkedAt: string | null;
  clientsManaged: number;
  adherencePct: number | null;
  sessionsLast7: number;
  unreadChats: number;
  inactiveClients: number;
}

export interface InternalCoachInvite {
  id: string;
  /** The single-use acceptance code; the share link embeds it. */
  code: string;
  email: string;
  /** 0033: the gym's own short name for the invitee, while the invitation is pending. */
  label: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  createdAt: string;
  expiresAt: string;
  expired: boolean;
  acceptedAt: string | null;
  shareUrl: string;
}

export interface InternalCoachList {
  data: InternalCoach[];
  total: number;
  limit: number | null;
  used: number;
  remaining: number | null;
  plan: CoachPlan;
  invites: InternalCoachInvite[];
}

export interface AssignmentResult {
  clientId: string;
  assignedCoachId: string | null;
  assignedCoachName: string | null;
}

export interface TraineeDetail extends Trainee {
  bodyweightKg: number | null;
  avgs: { volumeKg7: number | null; avgRpe7: number | null; avgPain14: number | null };
  recentNotes: Array<{ sessionId: string; label: string | null; notes: string; startedAt: string }>;
}

/* ------------------------------- coach chat ----------------------------- */

/** One coach↔client message (`/coaching/messages`, migration 0015). */
export interface CoachMessage {
  messageId: string;
  linkId: string;
  senderId: string;
  /** `true` when the signed-in caller wrote it. */
  mine: boolean;
  senderName: string;
  body: string;
  createdAt: string;
  readAt: string | null;
}

/** `GET /coaching/messages` — a page plus the unread badge for this caller. */
export interface CoachMessageList {
  data: CoachMessage[];
  total: number;
  unread: number;
  hasMore: boolean;
  linkId: string | null;
  coach: CoachingParty | null;
  trainee: CoachingParty | null;
}

/** `GET /coaching/messages/unread` — the lightweight badge poll. */
export interface CoachUnread {
  unread: number;
  linkId: string | null;
}

export interface CoachMessageQuery {
  /** Incremental poll cursor: only messages strictly after this instant. */
  since?: string | null;
  limit?: number | null;
  /** The coach's path: whose thread the caller holds the link to. */
  clientId?: string | null;
  /** The lifter's path (0026): which coach's thread, when they hold several. */
  coachId?: string | null;
}

const SHARED_FIELDS: ReadonlyArray<SharedField> = ['sessions', 'sets', 'bodyweight', 'notes', 'pain'];

/** The API's exact shared-field identifiers; anything else is dropped. */
export function normalizeSharedFields(value: unknown): SharedField[] {
  return asArray<unknown>(value).filter((field): field is SharedField =>
    SHARED_FIELDS.includes(field as SharedField),
  );
}

function normalizeCoachingParty(value: unknown): CoachingParty | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const userId = asString(raw.userId);
  if (!userId) return null;
  return {
    userId,
    displayName: asString(raw.displayName) ?? 'Coach',
    email: asString(raw.email) ?? '',
  };
}

export function normalizeCoachLink(value: unknown): CoachLink | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const linkId = asString(raw.linkId);
  if (!linkId) return null;
  const status = raw.status;
  const origin = raw.origin;
  return {
    linkId,
    status: status === 'active' || status === 'revoked' ? status : 'invited',
    origin: origin === 'lifter_invited' || origin === 'coach_invited' ? origin : null,
    sharedFields: normalizeSharedFields(raw.sharedFields),
    linkedAt: asString(raw.linkedAt),
    createdAt: asString(raw.createdAt) ?? new Date().toISOString(),
    inviteId: asString(raw.inviteId),
    coach: normalizeCoachingParty(raw.coach),
    trainee: normalizeCoachingParty(raw.trainee),
  };
}

export function normalizeCoachInvite(value: unknown): CoachInvite {
  const raw = asRecord(value);
  const status = raw.status;
  return {
    id: asString(raw.id) ?? '',
    code: asString(raw.code) ?? '',
    direction: raw.direction === 'received' ? 'received' : 'sent',
    inviterRole: raw.inviterRole === 'coach' ? 'coach' : 'lifter',
    status: status === 'accepted' || status === 'expired' || status === 'revoked' ? status : 'pending',
    inviteeEmail: asString(raw.inviteeEmail),
    inviteeName: asString(raw.inviteeName),
    sharedFields: normalizeSharedFields(raw.sharedFields),
    createdAt: asString(raw.createdAt) ?? new Date().toISOString(),
    expiresAt: asString(raw.expiresAt) ?? new Date().toISOString(),
    expired: raw.expired === true,
    acceptedAt: asString(raw.acceptedAt),
    linkId: asString(raw.linkId),
    decisionMessage: asString(raw.decisionMessage),
    decidedAt: asString(raw.decidedAt),
    shareUrl: asString(raw.shareUrl) ?? '',
    inviter: normalizeCoachingParty(raw.inviter),
    coach: normalizeCoachingParty(raw.coach),
    trainee: normalizeCoachingParty(raw.trainee),
    pack: raw.pack === null || raw.pack === undefined ? null : normalizeCoachPack(raw.pack),
  };
}

export function normalizeCoachingLinkPayload(value: unknown): CoachingLinkPayload {
  const raw = asRecord(value);
  return {
    link: normalizeCoachLink(raw.link),
    pendingInvite:
      raw.pendingInvite === null || raw.pendingInvite === undefined
        ? null
        : normalizeCoachInvite(raw.pendingInvite),
  };
}

function asConnectionTier(value: unknown): ConnectionTier {
  return value === 'paid' || value === 'connected' || value === 'inquiry' ? value : 'inquiry';
}

export function normalizeCoachThread(value: unknown): CoachThread {
  const raw = asRecord(value);
  return {
    tier: asConnectionTier(raw.tier),
    linkId: asString(raw.linkId) ?? '',
    userId: asString(raw.userId) ?? '',
    displayName: asString(raw.displayName) ?? 'Lifter',
    email: asString(raw.email) ?? '',
    sharedFields: normalizeSharedFields(raw.sharedFields),
    lastMessage: asString(raw.lastMessage),
    lastMessageAt: asString(raw.lastMessageAt),
    lastMessageMine: raw.lastMessageMine === true,
    unread: asInt(raw.unread),
    requestId: asString(raw.requestId),
    requestCode: asString(raw.requestCode),
    connectable: raw.connectable !== false,
    owned: raw.owned !== false,
    ownerName: asString(raw.ownerName),
  };
}

export function normalizeCoachProspect(value: unknown): CoachProspect {
  const raw = asRecord(value);
  return {
    tier: raw.tier === 'connected' ? 'connected' : 'inquiry',
    userId: asString(raw.userId) ?? '',
    displayName: asString(raw.displayName) ?? 'Lifter',
    email: asString(raw.email) ?? '',
    linkId: asString(raw.linkId),
    requestId: asString(raw.requestId),
    requestCode: asString(raw.requestCode),
    sharedFields: normalizeSharedFields(raw.sharedFields),
    linkedAt: asString(raw.linkedAt),
    lastMessageAt: asString(raw.lastMessageAt),
    unread: asInt(raw.unread),
    connectable: raw.connectable !== false,
    owned: raw.owned !== false,
    ownerName: asString(raw.ownerName),
  };
}

export function normalizeCoachSeats(value: unknown): CoachSeatUsage | null {
  if (value === null || value === undefined) return null;
  const raw = asRecord(value);
  const plan =
    raw.plan === 'coach' || raw.plan === 'gym' || raw.plan === 'gym_plus' || raw.plan === 'scale'
      ? raw.plan
      : 'free';
  const limit = asNumber(raw.limit);
  const used = asInt(raw.used);
  return {
    plan,
    limit: limit === null ? null : Math.round(limit),
    active: asInt(raw.active),
    pending: asInt(raw.pending),
    connected: asInt(raw.connected),
    used,
    remaining: asNumber(raw.remaining),
  };
}

export function normalizeCoachRoster(value: unknown): CoachRoster {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeTrainee);
  const internalRaw = asRecord(raw.internalCoachOf);
  const internalCoachOf =
    asString(internalRaw.coachId) === null
      ? null
      : {
          coachId: asString(internalRaw.coachId) ?? '',
          displayName: asString(internalRaw.displayName) ?? 'Your gym',
        };
  return {
    data,
    total: asInt(raw.total, data.length) || data.length,
    flagged: asInt(raw.flagged),
    seats: normalizeCoachSeats(raw.seats) ?? {
      plan: 'free',
      limit: null,
      active: data.length,
      pending: 0,
      connected: 0,
      used: data.length,
      remaining: null,
    },
    internalCoachOf,
  };
}

function asCoachPackKind(value: unknown): CoachPackKind {
  return value === 'in_person' || value === 'semi_coaching' ? value : 'online';
}

function asCoachPackCadence(value: unknown): CoachPackCadence {
  return value === 'one_off' || value === 'weekly' || value === 'pay_as_go' ? value : 'monthly';
}

function asStringArray(value: unknown): string[] {
  return asArray<unknown>(value).filter((entry): entry is string => typeof entry === 'string');
}

function normalizeCoachPackWindow(value: unknown): CoachPackWindow {
  const raw = asRecord(value);
  return {
    windowId: asString(raw.windowId) ?? '',
    weekday: Math.min(7, Math.max(1, asInt(raw.weekday, 1))),
    startMinute: asInt(raw.startMinute),
    endMinute: asInt(raw.endMinute),
    label: asString(raw.label) ?? '',
  };
}

function normalizeCoachPackOversubscription(value: unknown): CoachPackOversubscription {
  const raw = asRecord(value);
  return {
    subscribers: asInt(raw.subscribers),
    capacity: Math.max(1, asInt(raw.capacity, 1)),
    sessionsPerPeriod: Math.max(1, asInt(raw.sessionsPerPeriod, 1)),
    allocatedSlots: asInt(raw.allocatedSlots),
    bookablePlaces: asInt(raw.bookablePlaces),
    demand: asInt(raw.demand),
    oversubscribed: raw.oversubscribed === true,
    reason: asString(raw.reason),
  };
}

export function normalizeCoachPack(value: unknown): CoachPack {
  const raw = asRecord(value);
  return {
    packId: asString(raw.packId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    name: asString(raw.name) ?? 'Coaching pack',
    kind: asCoachPackKind(raw.kind),
    description: asString(raw.description),
    price: asNumber(raw.price) ?? 0,
    currency: asString(raw.currency) ?? 'GBP',
    cadence: asCoachPackCadence(raw.cadence),
    capacity: Math.max(1, asInt(raw.capacity, 1)),
    coachCount: Math.max(1, asInt(raw.coachCount, 1)),
    sessionMinutes: Math.max(1, asInt(raw.sessionMinutes, 60)),
    slotCapacity: Math.max(1, asInt(raw.slotCapacity, 1)),
    sessionsPerPeriod: Math.max(1, asInt(raw.sessionsPerPeriod, 1)),
    windows: asArray<unknown>(raw.windows).map(normalizeCoachPackWindow),
    summary: asString(raw.summary) ?? '',
    inclusions: asStringArray(raw.inclusions),
    location: asString(raw.location),
    archivedAt: asString(raw.archivedAt),
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

export function normalizeCoachOffer(value: unknown): CoachOffer {
  const raw = asRecord(value);
  const kind = raw.kind === 'referral' ? 'referral' : 'time_limited';
  const status = ['live', 'scheduled', 'ended', 'paused', 'exhausted'].includes(String(raw.status))
    ? (raw.status as OfferStatus)
    : 'paused';
  return {
    offerId: asStringText(raw.offerId),
    coachId: asStringText(raw.coachId),
    packId: asString(raw.packId),
    packName: asString(raw.packName),
    kind,
    name: asStringText(raw.name),
    percentOff: asInt(raw.percentOff),
    months: Math.max(1, asInt(raw.months) || 1),
    startsAt: asString(raw.startsAt),
    endsAt: asString(raw.endsAt),
    code: asString(raw.code),
    referrerPercentOff: raw.referrerPercentOff === null || raw.referrerPercentOff === undefined ? null : asInt(raw.referrerPercentOff),
    referrerMonths: raw.referrerMonths === null || raw.referrerMonths === undefined ? null : asInt(raw.referrerMonths),
    maxRedemptions: raw.maxRedemptions === null || raw.maxRedemptions === undefined ? null : asInt(raw.maxRedemptions),
    redemptions: asInt(raw.redemptions),
    active: raw.active !== false,
    status,
    summary: asStringText(raw.summary),
  };
}

export function normalizeLifterCoachCard(value: unknown): LifterCoachCard {
  const raw = asRecord(value);
  const ratingRaw = raw.rating === null || raw.rating === undefined ? null : asRecord(raw.rating);
  return {
    coachId: asStringText(raw.coachId),
    displayName: asStringText(raw.displayName) || 'Your coach',
    location: asString(raw.location),
    bio: asString(raw.bio),
    specialities: asStringArray(raw.specialities),
    rating:
      ratingRaw === null
        ? null
        : { average: Number(ratingRaw.average ?? 0), count: asInt(ratingRaw.count) },
    packs: asArray<unknown>(raw.packs).map(normalizeCoachPack),
    offers: asArray<unknown>(raw.offers).map(normalizeCoachOffer),
    myReview:
      raw.myReview === null || raw.myReview === undefined
        ? null
        : normalizeCoachReview(raw.myReview),
  };
}

export function normalizeOfferRedemption(value: unknown): OfferRedemption {
  const raw = asRecord(value);
  return {
    redemptionId: asStringText(raw.redemptionId),
    offerName: asStringText(raw.offerName),
    code: asString(raw.code),
    kind: raw.kind === 'referral' ? 'referral' : 'time_limited',
    percentOff: asInt(raw.percentOff),
    months: Math.max(1, asInt(raw.months) || 1),
    packName: asString(raw.packName),
    newClientName: asString(raw.newClientName),
    newClientEmail: asStringText(raw.newClientEmail),
    referrerName: asString(raw.referrerName),
    referrerEmail: asString(raw.referrerEmail),
    referrerPercentOff: raw.referrerPercentOff === null || raw.referrerPercentOff === undefined ? null : asInt(raw.referrerPercentOff),
    referrerRewarded: raw.referrerRewarded === true,
    createdAt: asStringText(raw.createdAt),
  };
}

export function normalizeCoachPackOwner(value: unknown): CoachPackOwner {
  const raw = asRecord(value);
  return {
    ...normalizeCoachPack(raw),
    subscriberCount: Math.max(0, asInt(raw.subscriberCount)),
    oversubscription: normalizeCoachPackOversubscription(raw.oversubscription),
  };
}

export function normalizeCoachProfile(value: unknown): CoachProfile {
  const raw = asRecord(value);
  return {
    coachId: asString(raw.coachId) ?? '',
    discoverable: raw.discoverable === true,
    displayName: asString(raw.displayName) ?? 'Coach',
    location: asString(raw.location),
    contactKind: asString(raw.contactKind),
    contactValue: asString(raw.contactValue),
    contactPublic: raw.contactPublic === true,
    bio: asString(raw.bio),
    specialities: asStringArray(raw.specialities),
    languages: asStringArray(raw.languages),
    yearsCoaching: asNumber(raw.yearsCoaching),
    reviewsPublic: raw.reviewsPublic === true,
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

export function normalizeCoachLocation(value: unknown): CoachLocation {
  const raw = asRecord(value);
  return {
    locationId: asString(raw.locationId) ?? '',
    label: asString(raw.label),
    address: asString(raw.address) ?? '',
    sortOrder: asInt(raw.sortOrder),
  };
}

export function normalizeCoachTeamMember(value: unknown): CoachTeamMember {
  const raw = asRecord(value);
  return {
    memberId: asString(raw.memberId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    displayName: asString(raw.displayName) ?? '',
    title: asString(raw.title),
    bio: asString(raw.bio),
    userId: asString(raw.userId),
    sortOrder: asInt(raw.sortOrder),
    archivedAt: asString(raw.archivedAt),
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

const RATING_KEYS = ['1', '2', '3', '4', '5'] as const;

function normalizeDistribution(value: unknown): Record<'1' | '2' | '3' | '4' | '5', number> {
  const raw = asRecord(value);
  const result = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 } as Record<
    '1' | '2' | '3' | '4' | '5',
    number
  >;
  for (const key of RATING_KEYS) result[key] = asInt(raw[key]);
  return result;
}

export function normalizeCoachReview(value: unknown): CoachReview {
  const raw = asRecord(value);
  return {
    reviewId: asString(raw.reviewId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    coachName: asString(raw.coachName) ?? 'Coach',
    lifterId: asString(raw.lifterId) ?? '',
    lifterName: asString(raw.lifterName) ?? 'A lifter',
    rating: Math.min(5, Math.max(1, asInt(raw.rating, 1))),
    comment: asString(raw.comment),
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

export function normalizeCoachReviewSummary(value: unknown): CoachReviewSummary {
  const raw = asRecord(value);
  return {
    coachId: asString(raw.coachId) ?? '',
    average: asNumber(raw.average),
    count: asInt(raw.count),
    distribution: normalizeDistribution(raw.distribution),
    recent: asArray<unknown>(raw.recent).map(normalizeCoachReview),
  };
}

function normalizePublicRating(value: unknown): CoachPublicRating | undefined {
  if (value === null || value === undefined) return undefined;
  const raw = asRecord(value);
  return {
    average: asNumber(raw.average) ?? 0,
    count: asInt(raw.count),
    distribution: normalizeDistribution(raw.distribution),
  };
}

export function normalizeCoachPublicListing(value: unknown): CoachPublicListing {
  const raw = asRecord(value);
  const contact = asRecord(raw.contact);
  const listing: CoachPublicListing = {
    coachId: asString(raw.coachId) ?? '',
    displayName: asString(raw.displayName) ?? 'Coach',
    location: asString(raw.location),
    locations: asArray<unknown>(raw.locations).map(normalizeCoachLocation),
    contact:
      raw.contact === null || raw.contact === undefined
        ? null
        : { kind: asString(contact.kind) ?? 'other', value: asString(contact.value) ?? '' },
    bio: asString(raw.bio),
    specialities: asStringArray(raw.specialities),
    languages: asStringArray(raw.languages),
    yearsCoaching: asNumber(raw.yearsCoaching),
    packs: asArray<unknown>(raw.packs).map(normalizeCoachPack),
    connectable: raw.connectable !== false,
  };
  const rating = normalizePublicRating(raw.rating);
  if (rating !== undefined) listing.rating = rating;
  return listing;
}

export function normalizeCoachDiscovery(value: unknown): CoachDiscovery {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCoachPublicListing);
  return {
    data,
    total: asInt(raw.total, data.length),
    query: asString(raw.query),
    kind: raw.kind === null || raw.kind === undefined ? null : asCoachPackKind(raw.kind),
    limit: asInt(raw.limit, data.length),
    offset: asInt(raw.offset),
  };
}

export function normalizeCoachProfileBundle(value: unknown): CoachProfileBundle {
  const raw = asRecord(value);
  return {
    profile: normalizeCoachProfile(raw.profile),
    packs: asArray<unknown>(raw.packs).map(normalizeCoachPackOwner),
  };
}

export function normalizeCoachPackSubscription(value: unknown): CoachPackSubscription {
  const raw = asRecord(value);
  const status = raw.status;
  return {
    subscriptionId: asString(raw.subscriptionId) ?? '',
    packId: asString(raw.packId) ?? '',
    packName: asString(raw.packName) ?? 'Coaching pack',
    packKind: asCoachPackKind(raw.packKind),
    coachId: asString(raw.coachId) ?? '',
    coachName: asString(raw.coachName) ?? 'Your coach',
    lifterId: asString(raw.lifterId) ?? '',
    lifterName: asString(raw.lifterName) ?? 'Client',
    status: status === 'cancelled' ? 'cancelled' : 'active',
    price: asNumber(raw.price) ?? 0,
    currency: asString(raw.currency) ?? 'GBP',
    cadence: asCoachPackCadence(raw.cadence),
    startedAt: asString(raw.startedAt) ?? '',
    cancelledAt: asString(raw.cancelledAt),
    createdAt: asString(raw.createdAt) ?? '',
  };
}

export function normalizeCoachPackSubscriptionList(value: unknown): CoachPackSubscriptionList {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCoachPackSubscription);
  return { data, total: asInt(raw.total, data.length) };
}

export function normalizeCoachPackSubscribe(value: unknown): CoachPackSubscribeResult {
  const raw = asRecord(value);
  return {
    subscription: normalizeCoachPackSubscription(raw.subscription),
    paymentId: asString(raw.paymentId),
  };
}

export function normalizeTraineeDetail(value: unknown): TraineeDetail {
  const raw = asRecord(value);
  const avgs = asRecord(raw.avgs);
  return {
    ...normalizeTrainee(raw),
    bodyweightKg: asNumber(raw.bodyweightKg),
    avgs: {
      volumeKg7: asNumber(avgs.volumeKg7),
      avgRpe7: asNumber(avgs.avgRpe7),
      avgPain14: asNumber(avgs.avgPain14),
    },
    recentNotes: asArray<unknown>(raw.recentNotes).map((entry) => {
      const note = asRecord(entry);
      return {
        sessionId: asString(note.sessionId) ?? '',
        label: asString(note.label),
        notes: asString(note.notes) ?? '',
        startedAt: asString(note.startedAt) ?? '',
      };
    }),
  };
}

export function normalizeCoachMessage(value: unknown): CoachMessage {
  const raw = asRecord(value);
  return {
    messageId: asString(raw.messageId) ?? '',
    linkId: asString(raw.linkId) ?? '',
    senderId: asString(raw.senderId) ?? '',
    mine: raw.mine === true,
    senderName: asString(raw.senderName) ?? 'Coach',
    body: asString(raw.body) ?? '',
    createdAt: asString(raw.createdAt) ?? new Date().toISOString(),
    readAt: asString(raw.readAt),
  };
}

export function normalizeCoachMessageList(value: unknown): CoachMessageList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map(normalizeCoachMessage),
    total: asInt(raw.total),
    unread: asInt(raw.unread),
    hasMore: raw.hasMore === true,
    linkId: asString(raw.linkId),
    coach: normalizeCoachingParty(raw.coach),
    trainee: normalizeCoachingParty(raw.trainee),
  };
}

export function normalizeCoachUnread(value: unknown): CoachUnread {
  const raw = asRecord(value);
  return { unread: asInt(raw.unread), linkId: asString(raw.linkId) };
}

/* ---------------------------------------------------------------------------
   The coach desk (CRM) — availability, bookings, money, the summary and the
   deterministic roster insights. These mirror `apps/api/src/types.ts` exactly;
   every reader tolerates a missing field so a leaner payload degrades to a
   dash or an empty collection rather than blanking the screen.
   ------------------------------------------------------------------------ */

/** `coach_slots.kind` — one-to-one or shared/group. */
export type CrmSlotKind = 'individual' | 'group';
export type CrmSlotStatus = 'open' | 'cancelled';
export type CrmBookingStatus = 'booked' | 'cancelled' | 'attended' | 'no_show';
export type CrmPaymentStatus = 'due' | 'paid' | 'refunded';
/** `crm_recurrence_rule` — how a slot series repeats (migration 0014). */
export type CrmRecurrenceRule = 'weekly' | 'monthly' | 'yearly';
export type CrmInsightSeverity = 'info' | 'warning' | 'danger';
export type CrmInsightSource = 'evaluation_matrix' | 'roster';
export type CrmVerdict =
  | 'insufficient_data'
  | 'overreaching'
  | 'deload_recommended'
  | 'progressing_fast'
  | 'progressing'
  | 'on_track'
  | 'stalled';

/** One client holding a place in a slot. */
export interface CrmSlotPlace {
  bookingId: string;
  clientId: string;
  /** Masked to "Former client" once the coach↔client link is no longer active. */
  clientName: string;
  clientEmail: string | null;
  status: CrmBookingStatus;
  bookedAt: string;
  /** False when the booking predates a revoked link; the place is still held. */
  linked: boolean;
}

/** One availability slot, with `booked / capacity` and who holds each place. */
export interface CrmSlot {
  slotId: string;
  coachId: string;
  startsAt: string;
  endsAt: string;
  capacity: number;
  booked: number;
  kind: CrmSlotKind;
  status: CrmSlotStatus;
  location: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  places: CrmSlotPlace[];
  /** The repeat series that generated this slot, or null for a one-off. */
  seriesId: string | null;
  recurrenceRule: CrmRecurrenceRule | null;
  seriesStopped: boolean;
  /** The pack this slot serves, or null for open availability (migration 0020). */
  packId: string | null;
  packName: string | null;
}

export interface CrmSlotList {
  data: CrmSlot[];
  from: string;
  to: string;
  total: number;
  bookedTotal: number;
}

/** One repeating rule (`POST /crm/slots/batch`, `POST /crm/slot-series/repeat`). */
export interface CrmSlotSeries {
  seriesId: string;
  coachId: string;
  rule: CrmRecurrenceRule;
  /** `YYYY-MM-DD` of the earliest pattern occurrence. */
  startsOn: string;
  until: string | null;
  occurrences: number | null;
  stoppedAt: string | null;
  createdAt: string;
  updatedAt: string;
  slotCount: number;
  /** The single pack this series carries, or null (migration 0020). */
  packId: string | null;
}

export interface CrmSlotBatch {
  data: CrmSlot[];
  series: CrmSlotSeries | null;
  created: number;
}

/** `POST /crm/slot-series/:seriesId/stop` — what stopping left alone. */
export interface CrmSlotSeriesStop {
  series: CrmSlotSeries;
  cancelled: number;
  keptBooked: number;
  kept: Array<{ slotId: string; startsAt: string; booked: number }>;
}

/** `GET /crm/coach-availability` — the lifter's coach and their open slots. */
export interface CrmCoachAvailability {
  coach: CoachingParty | null;
  data: CrmSlot[];
  from: string;
  to: string;
  total: number;
  /** The lifter's active pack subscription with this coach, or null. */
  subscription: CoachPackSubscription | null;
  /**
   * True when the lifter is linked but holds no active pack: the calendar is
   * withheld entirely. Slots appear only for the pack the lifter holds.
   */
  locked: boolean;
}

/** `GET /crm/my-bookings` — the caller's own places, with the linked coach. */
export interface CrmMyBookings {
  data: CrmBooking[];
  total: number;
  coach: CoachingParty | null;
}

export interface CrmBooking {
  bookingId: string;
  slotId: string;
  coachId: string;
  clientId: string;
  clientName: string;
  status: CrmBookingStatus;
  bookedAt: string;
  outcomeAt: string | null;
  cancelledAt: string | null;
  slot: {
    startsAt: string;
    endsAt: string;
    kind: CrmSlotKind;
    capacity: number;
    booked: number;
    status: CrmSlotStatus;
    location: string | null;
    /** The pack this slot serves, or null for open availability (migration 0020). */
    packId: string | null;
    packName: string | null;
  };
}

export interface CrmBookingList {
  data: CrmBooking[];
  total: number;
}

/** `POST /crm/slots/:slotId/book` — the booking and the slot after the write. */
export interface CrmBookSlotResult {
  booking: CrmBooking;
  slot: CrmSlot;
}

export interface CrmPayment {
  paymentId: string;
  coachId: string;
  clientId: string;
  clientName: string;
  amount: number;
  currency: string;
  description: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  status: CrmPaymentStatus;
  dueOn: string | null;
  paidAt: string | null;
  refundedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrmPaymentList {
  data: CrmPayment[];
  total: number;
  totals: { currency: string; paid: number; due: number; refunded: number };
}

/* ---------------------- coach discovery + packs (0020) ------------------- */

export type CoachPackKind = 'online' | 'in_person' | 'semi_coaching';
export type CoachPackCadence = 'one_off' | 'weekly' | 'monthly' | 'pay_as_go';
export type CoachPackSubscriptionStatus = 'active' | 'cancelled';

export const COACH_PACK_KINDS: ReadonlyArray<CoachPackKind> = ['online', 'in_person', 'semi_coaching'];
export const COACH_PACK_CADENCES: ReadonlyArray<CoachPackCadence> = [
  'one_off',
  'weekly',
  'monthly',
  'pay_as_go',
];

/** A stated booking window (ISO weekday 1..7 + coach-local minutes). */
export interface CoachPackWindow {
  windowId: string;
  weekday: number;
  startMinute: number;
  endMinute: number;
  /** "Tue 06:00-12:00". */
  label: string;
}

/** A coach's own public profile (`GET/PUT /coaching/profile`). */
export interface CoachProfile {
  coachId: string;
  discoverable: boolean;
  displayName: string;
  location: string | null;
  contactKind: string | null;
  contactValue: string | null;
  /** True only when the coach agreed to publish the contact method. */
  contactPublic: boolean;
  bio: string | null;
  specialities: string[];
  languages: string[];
  yearsCoaching: number | null;
  /** The coach's own review publication switch. Default false. */
  reviewsPublic: boolean;
  updatedAt: string;
}

/** One published place. */
export interface CoachLocation {
  locationId: string;
  label: string | null;
  address: string;
  sortOrder: number;
}

/** A named member of the coaching team. */
export interface CoachTeamMember {
  memberId: string;
  coachId: string;
  displayName: string;
  title: string | null;
  bio: string | null;
  userId: string | null;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One lifter's review of a coach. */
export interface CoachReview {
  reviewId: string;
  coachId: string;
  coachName: string;
  lifterId: string;
  lifterName: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The coach's private view of their own score. */
export interface CoachReviewSummary {
  coachId: string;
  average: number | null;
  count: number;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
  recent: CoachReview[];
}

/** The published aggregate, present only when the coach publishes reviews. */
export interface CoachPublicRating {
  average: number;
  count: number;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
}

/** Derived oversubscription warning on the coach's own pack. */
export interface CoachPackOversubscription {
  subscribers: number;
  capacity: number;
  sessionsPerPeriod: number;
  allocatedSlots: number;
  bookablePlaces: number;
  demand: number;
  oversubscribed: boolean;
  reason: string | null;
}

export interface CoachPack {
  packId: string;
  coachId: string;
  name: string;
  kind: CoachPackKind;
  description: string | null;
  price: number;
  currency: string;
  cadence: CoachPackCadence;
  capacity: number;
  coachCount: number;
  sessionMinutes: number;
  /** How many people share one session. */
  slotCapacity: number;
  sessionsPerPeriod: number;
  windows: CoachPackWindow[];
  /** The plain-words buying summary. */
  summary: string;
  inclusions: string[];
  location: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The coach's own pack, with the numbers only they may see. */
export interface CoachPackOwner extends CoachPack {
  subscriberCount: number;
  oversubscription: CoachPackOversubscription;
}

/* ---------------------------- offers (0039) ---------------------------- */

export type OfferKind = 'time_limited' | 'referral';
export type OfferStatus = 'live' | 'scheduled' | 'ended' | 'paused' | 'exhausted';

/**
 * A discount a coach attaches to a pack. `time_limited` is advertised with the
 * pack; `referral` is a code with two sides — the new client's discount and the
 * reward for the client who shared it.
 */
export interface CoachOffer {
  offerId: string;
  coachId: string;
  /** Null = every live pack this coach sells. */
  packId: string | null;
  packName: string | null;
  kind: OfferKind;
  name: string;
  percentOff: number;
  months: number;
  startsAt: string | null;
  endsAt: string | null;
  code: string | null;
  referrerPercentOff: number | null;
  referrerMonths: number | null;
  maxRedemptions: number | null;
  redemptions: number;
  active: boolean;
  status: OfferStatus;
  summary: string;
}

export interface CoachOfferInput {
  packId?: string | null;
  kind: OfferKind;
  name: string;
  percentOff: number;
  months?: number;
  startsAt?: string | null;
  endsAt?: string | null;
  code?: string;
  referrerPercentOff?: number | null;
  referrerMonths?: number | null;
  maxRedemptions?: number | null;
  active?: boolean;
}

/**
 * The lifter's own coach, as the card beside their chat: who they are, their
 * rating (when published), their packs, any live offer, and the review this
 * lifter left. `GET /coaching/my-coach`.
 */
export interface LifterCoachCard {
  coachId: string;
  displayName: string;
  location: string | null;
  bio: string | null;
  specialities: string[];
  rating: { average: number; count: number } | null;
  packs: CoachPack[];
  offers: CoachOffer[];
  myReview: CoachReview | null;
}

export interface OfferRedemption {
  redemptionId: string;
  offerName: string;
  code: string | null;
  kind: OfferKind;
  percentOff: number;
  months: number;
  packName: string | null;
  newClientName: string | null;
  newClientEmail: string;
  referrerName: string | null;
  referrerEmail: string | null;
  referrerPercentOff: number | null;
  referrerRewarded: boolean;
  createdAt: string;
}

/** What a code would give the buyer, asked before they buy. */
export interface OfferPreview {
  applies: boolean;
  reason: string | null;
  name?: string;
  kind?: OfferKind;
  code?: string | null;
  percentOff?: number;
  months?: number;
  referrerPercentOff?: number | null;
  priceBefore: number;
  priceAfter: number;
}

/** The offer that applied to a purchase (0039 extension of the subscribe result). */
export interface AppliedOffer {
  offerId: string;
  name: string;
  code: string | null;
  percentOff: number;
  months: number;
  priceBefore: number;
  priceAfter: number;
  referrerRewarded: boolean;
  note: string | null;
}

/** One public coach listing. Exactly the fields the coach chose to publish. */
export interface CoachPublicListing {
  coachId: string;
  displayName: string;
  location: string | null;
  locations: CoachLocation[];
  contact: { kind: string; value: string } | null;
  bio: string | null;
  specialities: string[];
  languages: string[];
  yearsCoaching: number | null;
  /** Absent entirely unless the coach published reviews that exist. */
  rating?: CoachPublicRating;
  packs: CoachPack[];
  /** False when the caller is connected to a different coach (chat still open). */
  connectable: boolean;
}

export interface CoachDiscovery {
  data: CoachPublicListing[];
  total: number;
  query: string | null;
  kind: CoachPackKind | null;
  limit: number;
  offset: number;
}

export interface CoachProfileBundle {
  profile: CoachProfile;
  packs: CoachPackOwner[];
}

export interface CoachPackSubscription {
  subscriptionId: string;
  packId: string;
  packName: string;
  packKind: CoachPackKind;
  coachId: string;
  coachName: string;
  lifterId: string;
  lifterName: string;
  status: CoachPackSubscriptionStatus;
  price: number;
  currency: string;
  cadence: CoachPackCadence;
  startedAt: string;
  cancelledAt: string | null;
  createdAt: string;
}

export interface CoachPackSubscriptionList {
  data: CoachPackSubscription[];
  total: number;
}

export interface CoachPackSubscribeResult {
  subscription: CoachPackSubscription;
  /** Null for a pay-as-you-go pack: its due rows are raised per booked session. */
  paymentId: string | null;
}

export interface CoachProfileInput {
  discoverable: boolean;
  displayName?: string | null;
  location?: string | null;
  contactKind?: string | null;
  contactValue?: string | null;
  contactPublic: boolean;
  bio?: string | null;
  specialities?: string[];
  languages?: string[];
  yearsCoaching?: number | null;
  reviewsPublic?: boolean;
}

export interface CoachPackInput {
  name: string;
  kind: CoachPackKind;
  description?: string | null;
  price: number;
  currency?: string;
  cadence: CoachPackCadence;
  capacity: number;
  coachCount: number;
  sessionMinutes?: number;
  slotCapacity?: number;
  sessionsPerPeriod?: number;
  windows?: Array<{ weekday: number; startMinute: number; endMinute: number }>;
  inclusions?: string[];
  location?: string | null;
}

export type CoachPackPatch = Partial<CoachPackInput>;

export interface CrmRevenuePeriod {
  /** `YYYY-MM`. */
  period: string;
  paid: number;
  due: number;
  refunded: number;
  count: number;
}

export interface CrmRevenue {
  from: string;
  to: string;
  currency: string;
  paid: { total: number; count: number };
  due: { total: number; count: number };
  refunded: { total: number; count: number };
  byPeriod: CrmRevenuePeriod[];
}

/** `GET /crm/summary` — the coach's home, aggregates only. */
export interface CrmSummary {
  generatedAt: string;
  clients: { total: number; active: number; inactive: number; missed: number; newLast28: number };
  sessions: {
    today: number;
    thisWeek: number;
    attendedLast28: number;
    missedLast28: number;
    /** Every open slot still to come; `next` is a bounded preview of these. */
    nextTotal: number;
    next: Array<{
      slotId: string;
      startsAt: string;
      endsAt: string;
      kind: CrmSlotKind;
      capacity: number;
      booked: number;
      clientNames: string[];
    }>;
  };
  reviews: {
    due: number;
    clients: Array<{
      clientId: string;
      displayName: string;
      planName: string | null;
      planStartedAt: string | null;
      planAgeDays: number | null;
      blockEndsOn: string | null;
      daysRemaining: number | null;
      weekOf: number | null;
      weeks: number | null;
      overdue: boolean;
      reason: string;
    }>;
  };
  revenue: {
    currency: string;
    paidTotal: number;
    dueTotal: number;
    refundedTotal: number;
    dueCount: number;
    paidCount: number;
    paidLast28: number;
  } | null;
  /** 0030: false for an internal coach with no money scope. */
  canSeeMoney: boolean;
}

/** One client whose block is in its final week, from `GET /crm/reviews`. */
export interface CrmReview {
  clientId: string;
  displayName: string;
  email: string;
  planId: string;
  blockName: string;
  startsOn: string;
  blockEndsOn: string;
  daysRemaining: number;
  weekOf: number;
  weeks: number;
  overdue: boolean;
}

/** The coach's review to-do, soonest block-end first. */
export interface CrmReviews {
  generatedAt: string;
  data: CrmReview[];
  total: number;
  overdue: number;
  windowDays: number;
}

/**
 * One actionable coaching task in the To-do queue, from `GET /crm/tasks`.
 * `kind` is deliberately an open string: the API owns the catalogue of kinds,
 * and the screen must render one it does not know yet rather than drop it.
 * `meta` carries the kind-specific facts, so a new kind adds no envelope field.
 */
export interface CrmTask {
  id: string;
  kind: string;
  title: string;
  clientId: string | null;
  clientName: string | null;
  dueOn: string | null;
  daysRemaining: number | null;
  overdue: boolean;
  href: string;
  meta: Record<string, string | number | boolean | null>;
}

/** The coach's To-do queue, every kind, soonest due first. */
export interface CrmTasks {
  generatedAt: string;
  data: CrmTask[];
  total: number;
  overdue: number;
  byKind: Record<string, number>;
}

/** The deterministic, evidence-bearing roster flags the coach desk shows. */
export interface CrmInsightFlag {
  code: string;
  severity: CrmInsightSeverity;
  source: CrmInsightSource;
  evidence: string;
  evidenceData: Record<string, string | number | boolean | null>;
  suggestedAction: string;
}

export interface CrmClientInsight {
  clientId: string;
  displayName: string;
  email: string;
  status: 'active' | 'invited' | 'revoked';
  verdict: CrmVerdict;
  headline: string;
  metrics: {
    totalVolumeKg: number;
    volumeChangePct: number;
    avgRpe: number;
    compliancePct: number;
    sessionsAnalyzed: number;
    prCount: number;
    acuteChronicRatio: number;
    weeksAnalyzed: number;
  };
  verdictReasons: string[];
  dataQuality: string[];
  flags: CrmInsightFlag[];
}

export interface CrmInsights {
  generatedAt: string;
  windowDays: number;
  data: CrmClientInsight[];
  totals: { clients: number; flagged: number; byCode: Record<string, number> };
}

function asCrmBookingStatus(value: unknown): CrmBookingStatus {
  return value === 'cancelled' || value === 'attended' || value === 'no_show' ? value : 'booked';
}

function asCrmSlotKind(value: unknown): CrmSlotKind {
  return value === 'group' ? 'group' : 'individual';
}

function asCrmSlotStatus(value: unknown): CrmSlotStatus {
  return value === 'cancelled' ? 'cancelled' : 'open';
}

function asCrmRecurrenceRule(value: unknown): CrmRecurrenceRule | null {
  return value === 'weekly' || value === 'monthly' || value === 'yearly' ? value : null;
}

function asCrmPaymentStatus(value: unknown): CrmPaymentStatus {
  return value === 'paid' || value === 'refunded' ? value : 'due';
}

function asCrmSeverity(value: unknown): CrmInsightSeverity {
  return value === 'danger' || value === 'warning' ? value : 'info';
}

function asCrmSource(value: unknown): CrmInsightSource {
  return value === 'evaluation_matrix' ? 'evaluation_matrix' : 'roster';
}

const CRM_VERDICTS: ReadonlyArray<CrmVerdict> = [
  'insufficient_data',
  'overreaching',
  'deload_recommended',
  'progressing_fast',
  'progressing',
  'on_track',
  'stalled',
];

function asCrmVerdict(value: unknown): CrmVerdict {
  return CRM_VERDICTS.includes(value as CrmVerdict) ? (value as CrmVerdict) : 'insufficient_data';
}

function normalizeCrmSlotPlace(value: unknown): CrmSlotPlace {
  const raw = asRecord(value);
  return {
    bookingId: asString(raw.bookingId) ?? '',
    clientId: asString(raw.clientId) ?? '',
    clientName: asString(raw.clientName) ?? 'Client',
    clientEmail: asString(raw.clientEmail),
    status: asCrmBookingStatus(raw.status),
    bookedAt: asString(raw.bookedAt) ?? '',
    linked: raw.linked !== false,
  };
}

export function normalizeCrmSlot(value: unknown): CrmSlot {
  const raw = asRecord(value);
  const capacity = asInt(raw.capacity, 1);
  return {
    slotId: asString(raw.slotId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    startsAt: asString(raw.startsAt) ?? '',
    endsAt: asString(raw.endsAt) ?? '',
    capacity: capacity > 0 ? capacity : 1,
    booked: Math.max(0, asInt(raw.booked)),
    kind: asCrmSlotKind(raw.kind),
    status: asCrmSlotStatus(raw.status),
    location: asString(raw.location),
    notes: asString(raw.notes),
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
    places: asArray<unknown>(raw.places).map(normalizeCrmSlotPlace),
    seriesId: asString(raw.seriesId),
    recurrenceRule: asCrmRecurrenceRule(raw.recurrenceRule),
    seriesStopped: raw.seriesStopped === true,
    packId: asString(raw.packId),
    packName: asString(raw.packName),
  };
}

function normalizeCrmSlotSeries(value: unknown): CrmSlotSeries {
  const raw = asRecord(value);
  return {
    seriesId: asString(raw.seriesId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    rule: asCrmRecurrenceRule(raw.rule) ?? 'weekly',
    startsOn: asString(raw.startsOn) ?? '',
    until: asString(raw.until),
    occurrences: asNumber(raw.occurrences),
    stoppedAt: asString(raw.stoppedAt),
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
    slotCount: asInt(raw.slotCount),
    packId: asString(raw.packId),
  };
}

export function normalizeCrmSlotBatch(value: unknown): CrmSlotBatch {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCrmSlot);
  const series = raw.series === null || raw.series === undefined ? null : normalizeCrmSlotSeries(raw.series);
  return { data, series, created: asInt(raw.created, data.length) || data.length };
}

export function normalizeCrmSlotSeriesStop(value: unknown): CrmSlotSeriesStop {
  const raw = asRecord(value);
  return {
    series: normalizeCrmSlotSeries(raw.series),
    cancelled: asInt(raw.cancelled),
    keptBooked: asInt(raw.keptBooked),
    kept: asArray<unknown>(raw.kept).map((entry) => {
      const kept = asRecord(entry);
      return {
        slotId: asString(kept.slotId) ?? '',
        startsAt: asString(kept.startsAt) ?? '',
        booked: asInt(kept.booked),
      };
    }),
  };
}

export function normalizeCrmCoachAvailability(value: unknown): CrmCoachAvailability {
  const raw = asRecord(value);
  return {
    coach: normalizeCoachingParty(raw.coach),
    data: asArray<unknown>(raw.data).map(normalizeCrmSlot),
    from: asString(raw.from) ?? '',
    to: asString(raw.to) ?? '',
    total: asInt(raw.total, asArray<unknown>(raw.data).length),
    subscription:
      raw.subscription === null || raw.subscription === undefined
        ? null
        : normalizeCoachPackSubscription(raw.subscription),
    locked: raw.locked === true,
  };
}

export function normalizeCrmMyBookings(value: unknown): CrmMyBookings {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCrmBooking);
  return { data, total: asInt(raw.total, data.length), coach: normalizeCoachingParty(raw.coach) };
}

export function normalizeCrmSlotList(value: unknown): CrmSlotList {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCrmSlot);
  return {
    data,
    from: asString(raw.from) ?? '',
    to: asString(raw.to) ?? '',
    total: asInt(raw.total, data.length) || data.length,
    bookedTotal: asInt(raw.bookedTotal) || data.reduce((sum, slot) => sum + slot.booked, 0),
  };
}

export function normalizeCrmBooking(value: unknown): CrmBooking {
  const raw = asRecord(value);
  const slot = asRecord(raw.slot);
  return {
    bookingId: asString(raw.bookingId) ?? '',
    slotId: asString(raw.slotId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    clientId: asString(raw.clientId) ?? '',
    clientName: asString(raw.clientName) ?? 'Client',
    status: asCrmBookingStatus(raw.status),
    bookedAt: asString(raw.bookedAt) ?? '',
    outcomeAt: asString(raw.outcomeAt),
    cancelledAt: asString(raw.cancelledAt),
    slot: {
      startsAt: asString(slot.startsAt) ?? '',
      endsAt: asString(slot.endsAt) ?? '',
      kind: asCrmSlotKind(slot.kind),
      capacity: Math.max(1, asInt(slot.capacity, 1)),
      booked: Math.max(0, asInt(slot.booked)),
      status: asCrmSlotStatus(slot.status),
      location: asString(slot.location),
      packId: asString(slot.packId),
      packName: asString(slot.packName),
    },
  };
}

export function normalizeCrmBookingList(value: unknown): CrmBookingList {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCrmBooking);
  return { data, total: asInt(raw.total, data.length) || data.length };
}

export function normalizeCrmBookSlot(value: unknown): CrmBookSlotResult {
  const raw = asRecord(value);
  return {
    booking: normalizeCrmBooking(raw.booking),
    slot: normalizeCrmSlot(raw.slot),
  };
}

/* ------------------------- the lifter's schedule ------------------------- */

/** `kind: 'none'` is forced to a clean empty — never a half-filled reminder. */
export function normalizeNextSession(value: unknown): NextSession {
  const raw = asRecord(value);
  const kindRaw = asString(raw.kind);
  const managerRaw = asString(raw.managedBy);
  const managedBy: NextSessionManager | null =
    managerRaw === 'coach' || managerRaw === 'self' ? managerRaw : null;
  if (kindRaw !== 'booked' && kindRaw !== 'scheduled') {
    return {
      kind: 'none',
      startsAt: null,
      hasTime: false,
      dayLabel: null,
      planName: null,
      source: 'none',
      managedBy,
    };
  }
  const sourceRaw = asString(raw.source);
  const coachName = asString(raw.coachName);
  const packName = asString(raw.packName);
  const bookingId = asString(raw.bookingId);
  const scheduleId = asString(raw.scheduleId);
  const planId = asString(raw.planId);
  const planDayId = asString(raw.planDayId);
  const planDayIndexRaw = asNumber(raw.planDayIndex);
  const planDayIndex = planDayIndexRaw === null ? null : Math.round(planDayIndexRaw);
  const planDayLabel = asString(raw.planDayLabel);
  const planDayFocus = Array.isArray(raw.planDayFocus)
    ? raw.planDayFocus.map((entry) => asString(entry) ?? '').filter((entry) => entry !== '')
    : [];
  // A payload older than the ownership field: a booking is always the coach's,
  // a self-set pattern is always the lifter's.
  const resolvedManager: NextSessionManager =
    managedBy ?? (kindRaw === 'booked' ? 'coach' : 'self');
  return {
    kind: kindRaw,
    startsAt: asString(raw.startsAt),
    // A booked slot always has a clock time; a scheduled entry says so itself.
    hasTime: kindRaw === 'booked' ? true : raw.hasTime === true,
    dayLabel: asString(raw.dayLabel),
    planName: asString(raw.planName),
    source: sourceRaw === 'booked' ? 'booked' : 'schedule',
    managedBy: resolvedManager,
    ...(coachName === null ? {} : { coachName }),
    ...(packName === null ? {} : { packName }),
    ...(bookingId === null ? {} : { bookingId }),
    ...(scheduleId === null ? {} : { scheduleId }),
    ...(planId === null ? {} : { planId }),
    ...(planDayId === null ? {} : { planDayId }),
    ...(planDayIndex === null ? {} : { planDayIndex }),
    ...(planDayLabel === null ? {} : { planDayLabel }),
    ...(planDayFocus.length === 0 ? {} : { planDayFocus }),
    ...(raw.planDayDerived === true ? { planDayDerived: true } : {}),
  };
}

function asStartMinute(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = asNumber(value);
  return parsed === null ? null : parsed;
}

export function normalizeScheduleEntry(value: unknown): ScheduleEntry {
  const raw = asRecord(value);
  return {
    entryId: asString(raw.entryId) ?? '',
    planDayId: asString(raw.planDayId) ?? '',
    planId: asString(raw.planId) ?? '',
    planName: asString(raw.planName) ?? 'Your programme',
    coachOwned: raw.coachOwned === true,
    dayIndex: asInt(raw.dayIndex, 1),
    dayLabel: asString(raw.dayLabel) ?? 'Session',
    focus: asStringArray(raw.focus),
    weekday: asInt(raw.weekday, 1),
    startMinute: asStartMinute(raw.startMinute),
    repeatWeekly: raw.repeatWeekly === true,
    repeatUntil: asString(raw.repeatUntil),
    stoppedAt: asString(raw.stoppedAt),
    nextStartsAt: asString(raw.nextStartsAt),
  };
}

export function normalizeScheduleDay(value: unknown): ScheduleDay {
  const raw = asRecord(value);
  return {
    planDayId: asString(raw.planDayId) ?? '',
    planId: asString(raw.planId) ?? '',
    planName: asString(raw.planName) ?? 'Your programme',
    coachOwned: raw.coachOwned === true,
    dayIndex: asInt(raw.dayIndex, 1),
    label: asString(raw.label) ?? 'Session',
    focus: asStringArray(raw.focus),
    entryId: asString(raw.entryId),
    weekday: raw.weekday === null || raw.weekday === undefined ? null : asInt(raw.weekday, 1),
    startMinute: asStartMinute(raw.startMinute),
  };
}

export function normalizeLifterSchedule(value: unknown): LifterSchedule {
  const raw = asRecord(value);
  return {
    timezone: asString(raw.timezone) ?? 'UTC',
    active: raw.active === true,
    repeatUntil: asString(raw.repeatUntil),
    entries: asArray<unknown>(raw.entries).map(normalizeScheduleEntry),
    days: asArray<unknown>(raw.days).map(normalizeScheduleDay),
  };
}

export function normalizeCrmPayment(value: unknown): CrmPayment {
  const raw = asRecord(value);
  return {
    paymentId: asString(raw.paymentId) ?? '',
    coachId: asString(raw.coachId) ?? '',
    clientId: asString(raw.clientId) ?? '',
    clientName: asString(raw.clientName) ?? 'Client',
    amount: asNumber(raw.amount) ?? 0,
    currency: asString(raw.currency) ?? 'GBP',
    description: asString(raw.description),
    periodStart: asString(raw.periodStart),
    periodEnd: asString(raw.periodEnd),
    status: asCrmPaymentStatus(raw.status),
    dueOn: asString(raw.dueOn),
    paidAt: asString(raw.paidAt),
    refundedAt: asString(raw.refundedAt),
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

export function normalizeCrmPaymentList(value: unknown): CrmPaymentList {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map(normalizeCrmPayment);
  const totals = asRecord(raw.totals);
  return {
    data,
    total: asInt(raw.total, data.length) || data.length,
    totals: {
      currency: asString(totals.currency) ?? data[0]?.currency ?? 'GBP',
      paid: asNumber(totals.paid) ?? 0,
      due: asNumber(totals.due) ?? 0,
      refunded: asNumber(totals.refunded) ?? 0,
    },
  };
}

function normalizeCrmMoney(value: unknown): { total: number; count: number } {
  const raw = asRecord(value);
  return { total: asNumber(raw.total) ?? 0, count: asInt(raw.count) };
}

export function normalizeCrmRevenue(value: unknown): CrmRevenue {
  const raw = asRecord(value);
  return {
    from: asString(raw.from) ?? '',
    to: asString(raw.to) ?? '',
    currency: asString(raw.currency) ?? 'GBP',
    paid: normalizeCrmMoney(raw.paid),
    due: normalizeCrmMoney(raw.due),
    refunded: normalizeCrmMoney(raw.refunded),
    byPeriod: asArray<unknown>(raw.byPeriod).map((entry) => {
      const period = asRecord(entry);
      return {
        period: asString(period.period) ?? '',
        paid: asNumber(period.paid) ?? 0,
        due: asNumber(period.due) ?? 0,
        refunded: asNumber(period.refunded) ?? 0,
        count: asInt(period.count),
      };
    }),
  };
}

export function normalizeCrmSummary(value: unknown): CrmSummary {
  const raw = asRecord(value);
  const clients = asRecord(raw.clients);
  const sessions = asRecord(raw.sessions);
  const reviews = asRecord(raw.reviews);
  const revenue = asRecord(raw.revenue);
  return {
    generatedAt: asString(raw.generatedAt) ?? new Date().toISOString(),
    clients: {
      total: asInt(clients.total),
      active: asInt(clients.active),
      inactive: asInt(clients.inactive),
      missed: asInt(clients.missed),
      newLast28: asInt(clients.newLast28),
    },
    sessions: {
      today: asInt(sessions.today),
      thisWeek: asInt(sessions.thisWeek),
      attendedLast28: asInt(sessions.attendedLast28),
      missedLast28: asInt(sessions.missedLast28),
      nextTotal: asInt(sessions.nextTotal, asArray<unknown>(sessions.next).length),
      next: asArray<unknown>(sessions.next).map((entry) => {
        const slot = asRecord(entry);
        return {
          slotId: asString(slot.slotId) ?? '',
          startsAt: asString(slot.startsAt) ?? '',
          endsAt: asString(slot.endsAt) ?? '',
          kind: asCrmSlotKind(slot.kind),
          capacity: Math.max(1, asInt(slot.capacity, 1)),
          booked: Math.max(0, asInt(slot.booked)),
          clientNames: asArray<unknown>(slot.clientNames)
            .map((name) => asString(name))
            .filter((name): name is string => name !== null),
        };
      }),
    },
    reviews: {
      due: asInt(reviews.due),
      clients: asArray<unknown>(reviews.clients).map((entry) => {
        const review = asRecord(entry);
        return {
          clientId: asString(review.clientId) ?? '',
          displayName: asString(review.displayName) ?? 'Client',
          planName: asString(review.planName),
          planStartedAt: asString(review.planStartedAt),
          planAgeDays: asNumber(review.planAgeDays),
          blockEndsOn: asString(review.blockEndsOn),
          daysRemaining: asNumber(review.daysRemaining),
          weekOf: asNumber(review.weekOf),
          weeks: asNumber(review.weeks),
          overdue: review.overdue === true,
          reason: asString(review.reason) ?? 'Review due.',
        };
      }),
    },
    revenue:
      raw.revenue === null || raw.revenue === undefined
        ? null
        : {
            currency: asString(revenue.currency) ?? 'GBP',
            paidTotal: asNumber(revenue.paidTotal) ?? 0,
            dueTotal: asNumber(revenue.dueTotal) ?? 0,
            refundedTotal: asNumber(revenue.refundedTotal) ?? 0,
            dueCount: asInt(revenue.dueCount),
            paidCount: asInt(revenue.paidCount),
            paidLast28: asNumber(revenue.paidLast28) ?? 0,
          },
    // Older payloads predate the money gate, so default to the permissive read
    // unless the API explicitly sent a null revenue block.
    canSeeMoney: raw.canSeeMoney !== false && raw.revenue !== null,
  };
}

/** Normalise the API's `/stats/summary` window aggregate for a client. */
export function normalizeClientStatsSummary(value: unknown): ClientStatsSummary {
  const raw = asRecord(value);
  return {
    from: asString(raw.from) ?? '',
    to: asString(raw.to) ?? '',
    sessionsCompleted: asInt(raw.sessionsCompleted),
    volumeKg: asNumber(raw.volumeKg) ?? 0,
    totalReps: asInt(raw.totalReps),
    setsCompleted: asInt(raw.setsCompleted),
    prCount: asInt(raw.prCount),
    failedSets: asInt(raw.failedSets),
    avgRpe: asNumber(raw.avgRpe),
    avgCompliancePct: asNumber(raw.avgCompliancePct),
    currentStreakWeeks: asInt(raw.currentStreakWeeks),
    longestStreakWeeks: asInt(raw.longestStreakWeeks),
    weeklyTarget: asInt(raw.weeklyTarget, 4) || 4,
    sessionsPerWeek: asNumber(raw.sessionsPerWeek) ?? 0,
    unitSystem: raw.unitSystem === 'imperial' ? 'imperial' : 'metric',
  };
}

export function normalizeCrmReviews(value: unknown): CrmReviews {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map((entry): CrmReview => {
    const review = asRecord(entry);
    return {
      clientId: asString(review.clientId) ?? '',
      displayName: asString(review.displayName) ?? 'Client',
      email: asString(review.email) ?? '',
      planId: asString(review.planId) ?? '',
      blockName: asString(review.blockName) ?? 'Active block',
      startsOn: asString(review.startsOn) ?? '',
      blockEndsOn: asString(review.blockEndsOn) ?? '',
      daysRemaining: asInt(review.daysRemaining),
      weekOf: Math.max(1, asInt(review.weekOf, 1) || 1),
      weeks: Math.max(1, asInt(review.weeks, 1) || 1),
      overdue: review.overdue === true,
    };
  });
  return {
    generatedAt: asString(raw.generatedAt) ?? new Date().toISOString(),
    data,
    total: asInt(raw.total, data.length),
    overdue: asInt(raw.overdue, data.filter((review) => review.overdue).length),
    windowDays: Math.max(1, asInt(raw.windowDays, 7) || 7),
  };
}

/** Keep only the JSON scalars `meta` is allowed to carry, dropping anything else. */
function normalizeCrmTaskMeta(value: unknown): Record<string, string | number | boolean | null> {
  const meta: Record<string, string | number | boolean | null> = {};
  for (const [key, entry] of Object.entries(asRecord(value))) {
    if (entry === null || typeof entry === 'string' || typeof entry === 'boolean') meta[key] = entry;
    else if (typeof entry === 'number' && Number.isFinite(entry)) meta[key] = entry;
  }
  return meta;
}

export function normalizeCrmTasks(value: unknown): CrmTasks {
  const raw = asRecord(value);
  const data = asArray<unknown>(raw.data).map((entry): CrmTask => {
    const task = asRecord(entry);
    const remaining = asNumber(task.daysRemaining);
    return {
      id: asString(task.id) ?? '',
      kind: asString(task.kind) ?? 'unknown',
      title: asString(task.title) ?? 'Coaching task',
      clientId: asString(task.clientId),
      clientName: asString(task.clientName),
      dueOn: asString(task.dueOn),
      daysRemaining: remaining === null ? null : Math.round(remaining),
      overdue: task.overdue === true,
      href: asString(task.href) ?? '/crm',
      meta: normalizeCrmTaskMeta(task.meta),
    };
  });
  const byKind: Record<string, number> = {};
  for (const [kind, count] of Object.entries(asRecord(raw.byKind))) byKind[kind] = asInt(count);
  return {
    generatedAt: asString(raw.generatedAt) ?? new Date().toISOString(),
    data,
    total: asInt(raw.total, data.length),
    overdue: asInt(raw.overdue, data.filter((task) => task.overdue).length),
    byKind,
  };
}

export function normalizeCrmInsights(value: unknown): CrmInsights {
  const raw = asRecord(value);
  const totals = asRecord(raw.totals);
  const byCode: Record<string, number> = {};
  for (const [code, count] of Object.entries(asRecord(totals.byCode))) {
    byCode[code] = asInt(count);
  }
  return {
    generatedAt: asString(raw.generatedAt) ?? new Date().toISOString(),
    windowDays: asInt(raw.windowDays, 28) || 28,
    data: asArray<unknown>(raw.data).map((entry) => {
      const client = asRecord(entry);
      const metrics = asRecord(client.metrics);
      return {
        clientId: asString(client.clientId) ?? '',
        displayName: asString(client.displayName) ?? 'Client',
        email: asString(client.email) ?? '',
        status:
          client.status === 'invited' || client.status === 'revoked' ? client.status : 'active',
        verdict: asCrmVerdict(client.verdict),
        headline: asString(client.headline) ?? 'No evaluation yet.',
        metrics: {
          totalVolumeKg: asNumber(metrics.totalVolumeKg) ?? 0,
          volumeChangePct: asNumber(metrics.volumeChangePct) ?? 0,
          avgRpe: asNumber(metrics.avgRpe) ?? 0,
          compliancePct: asNumber(metrics.compliancePct) ?? 0,
          sessionsAnalyzed: asInt(metrics.sessionsAnalyzed),
          prCount: asInt(metrics.prCount),
          acuteChronicRatio: asNumber(metrics.acuteChronicRatio) ?? 0,
          weeksAnalyzed: asInt(metrics.weeksAnalyzed),
        },
        verdictReasons: asArray<unknown>(client.verdictReasons)
          .map((reason) => asString(reason))
          .filter((reason): reason is string => reason !== null),
        dataQuality: asArray<unknown>(client.dataQuality)
          .map((note) => asString(note))
          .filter((note): note is string => note !== null),
        flags: asArray<unknown>(client.flags).map((flag) => {
          const rawFlag = asRecord(flag);
          const evidenceData: Record<string, string | number | boolean | null> = {};
          for (const [key, item] of Object.entries(asRecord(rawFlag.evidenceData))) {
            if (
              item === null ||
              typeof item === 'string' ||
              typeof item === 'number' ||
              typeof item === 'boolean'
            ) {
              evidenceData[key] = item;
            }
          }
          return {
            code: asString(rawFlag.code) ?? 'flag',
            severity: asCrmSeverity(rawFlag.severity),
            source: asCrmSource(rawFlag.source),
            evidence: asString(rawFlag.evidence) ?? '',
            evidenceData,
            suggestedAction: asString(rawFlag.suggestedAction) ?? '',
          };
        }),
      };
    }),
    totals: {
      clients: asInt(totals.clients),
      flagged: asInt(totals.flagged),
      byCode,
    },
  };
}

export interface CrmSlotInput {
  startsAt: string;
  endsAt: string;
  capacity: number;
  kind: CrmSlotKind;
  location?: string | null;
  notes?: string | null;
  /** The pack this slot serves, or null for open availability (migration 0020). */
  packId?: string | null;
}

export interface CrmSlotPatch {
  startsAt?: string;
  endsAt?: string;
  capacity?: number;
  kind?: CrmSlotKind;
  status?: CrmSlotStatus;
  location?: string | null;
  notes?: string | null;
  packId?: string | null;
}

/** A repeat rule plus its end condition; `occurrences` counts the pattern itself. */
export interface CrmRecurrenceInput {
  rule: CrmRecurrenceRule;
  occurrences?: number | null;
  until?: string | null;
}

/** A batch of slot patterns plus, optionally, one repeat rule. */
export interface CrmSlotBatchInput {
  slots: CrmSlotInput[];
  recurrence?: CrmRecurrenceInput | null;
}

/* ---------------------------------------------------------------------------
   Admin — the founder's platform surface (migration 0012)

   The wire mirror of `apps/api/src/types.ts`'s admin block. Every payload here
   is aggregate-only: identity, plans, seats, dates and money. There is no type
   for a set, a session body or a client roster because the admin API never
   returns one.
   ------------------------------------------------------------------------ */

export type CoachPlan = 'free' | 'coach' | 'gym' | 'gym_plus' | 'scale';

/**
 * RENDER HINT ONLY: does this plan include internal coaches?
 *
 * The enforcement is `public.l2f_coach_internal_limit()` in the database
 * (free 0 · coach 0 · gym 10 · gym_plus 20 · scale unlimited), read through
 * `GET /coaching/internal-coaches`. This mirrors it so a nav tab is not drawn
 * for a plan whose page could only say "not included" — if the two ever
 * disagree, the API wins and the page shows the honest panel.
 */
export function planIncludesTeam(plan: CoachPlan): boolean {
  return plan === 'gym' || plan === 'gym_plus' || plan === 'scale';
}

export interface AdminAuditEntry {
  auditId: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  targetKind: 'account' | 'plan' | 'offer' | 'invitation';
  targetUserId: string | null;
  targetEmail: string | null;
  /** Human key of a non-account target: plan key, offer code or invitation code. */
  targetRef: string | null;
  summary: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
}

/** The active subscriptions behind MRR, grouped by catalog plan. */
export interface AdminSubscriptionView {
  lifterPremium: number;
  coachPaid: number;
  customContracts: number;
  /** Premium accounts whose window has closed — a failed/lapsed trial. */
  lapsedPremium: number;
  activeTotal: number;
  byPlan: Array<{
    audience: PlanAudience;
    planKey: string;
    label: string;
    count: number;
    priceMonthly: number;
    mrr: number;
  }>;
}

export interface AdminSummary {
  generatedAt: string;
  accounts: { total: number; lifters: number; coaches: number; admins: number };
  activity: { active7: number; active28: number };
  sessions: { completed: number; completed28: number };
  sets: { logged: number; logged28: number };
  blocks: { completed: number };
  businessesByPlan: Array<{
    plan: CoachPlan;
    businesses: number;
    seatsUsed: number;
    seatsLimit: number | null;
  }>;
  seats: { used: number; capacity: number | null; unlimitedBusinesses: number };
  revenue: {
    currency: string;
    paid: number;
    paidCount: number;
    due: number;
    dueCount: number;
    refunded: number;
    refundedCount: number;
    /** Monthly recurring revenue, valued at the catalog list price. */
    mrr: number;
    /** @deprecated Phase-5 alias for `mrr`; kept on the wire. */
    mrrEquivalent: number;
    activeSubscriptions: number;
    customContracts: number;
    subscriptions: AdminSubscriptionView;
  };
}

export interface AdminAccountView {
  userId: string;
  email: string;
  displayName: string;
  roles: Role[];
  tier: 'basic' | 'premium' | 'plan_once';
  premiumUntil: string | null;
  coachPlan: CoachPlan;
  createdAt: string;
  lastActivityAt: string | null;
  isPremium: boolean;
  hasCoachLink: boolean;
  seats: CoachSeatUsage | null;
}

export interface AdminAccountDetail extends AdminAccountView {
  sessionsCompleted: number;
  sessionsLast28: number;
  setsLogged: number;
  clients: number | null;
  openSlots: number | null;
  bookings: number | null;
  revenue: { currency: string; paid: number; due: number; refunded: number } | null;
}

export interface AdminAccountList {
  data: AdminAccountView[];
  total: number;
  limit: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AdminBusiness {
  userId: string;
  email: string;
  displayName: string;
  plan: CoachPlan;
  seatsUsed: number;
  seatsLimit: number | null;
  clients: number;
  pendingInvites: number;
  openSlots: number;
  bookings: number;
  attended: number;
  currency: string;
  paid: number;
  due: number;
  refunded: number;
  createdAt: string;
  lastActivityAt: string | null;
}

export interface AdminBusinessList {
  data: AdminBusiness[];
  total: number;
}

export interface AdminRevenuePeriod {
  period: string;
  paid: number;
  due: number;
  refunded: number;
  count: number;
}

export interface AdminRevenue {
  from: string;
  to: string;
  currency: string;
  paid: { total: number; count: number };
  due: { total: number; count: number };
  refunded: { total: number; count: number };
  byPeriod: AdminRevenuePeriod[];
  byBusiness: Array<{
    coachId: string;
    displayName: string;
    currency: string;
    paid: number;
    due: number;
    refunded: number;
  }>;
}

export interface AdminAuditList {
  data: AdminAuditEntry[];
  total: number;
  limit: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AdminAccountPatch {
  coachPlan?: CoachPlan;
  tier?: 'basic' | 'premium';
  premiumUntil?: string | null;
  grantRole?: 'coach' | 'admin';
  revokeRole?: 'coach' | 'admin';
}

export interface AdminUpdateAccountResult {
  account: AdminAccountDetail;
  audit: AdminAuditEntry | null;
}

/* -------- phase 6: the business console (catalog, offers, invitations) ---- */

export type PlanAudience = 'lifter' | 'coach';

export interface PlanCatalogEntry {
  planId: string;
  audience: PlanAudience;
  planKey: string;
  displayName: string;
  priceMonthly: number;
  currency: string;
  seatLimit: number | null;
  negotiated: boolean;
  active: boolean;
  sortOrder: number;
  accountValue: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlanCatalogList {
  data: PlanCatalogEntry[];
  total: number;
}

export interface AdminPlanCreate {
  audience: PlanAudience;
  planKey: string;
  displayName: string;
  priceMonthly: number;
  currency: string;
  seatLimit: number | null;
  negotiated: boolean;
  active: boolean;
  sortOrder: number;
  accountValue: string;
}

export type AdminPlanPatch = Partial<Omit<AdminPlanCreate, 'audience' | 'planKey'>>;

export interface AdminPlanWriteResult {
  plan: PlanCatalogEntry;
  audit: AdminAuditEntry | null;
}

export type OfferDiscountKind = 'percent' | 'amount';

export interface OfferView {
  offerId: string;
  code: string;
  description: string;
  discountKind: OfferDiscountKind;
  discountValue: number;
  planId: string | null;
  planKey: string | null;
  planName: string | null;
  audience: PlanAudience | null;
  startsAt: string | null;
  endsAt: string | null;
  usageCap: number | null;
  usageCount: number;
  active: boolean;
  redeemable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OfferList {
  data: OfferView[];
  total: number;
}

export interface AdminOfferCreate {
  code: string;
  description: string;
  discountKind: OfferDiscountKind;
  discountValue: number;
  planId: string | null;
  startsAt: string | null;
  endsAt: string | null;
  usageCap: number | null;
  active: boolean;
}

export type AdminOfferPatch = Partial<AdminOfferCreate>;

export interface AdminOfferWriteResult {
  offer: OfferView;
  audit: AdminAuditEntry | null;
}

export type InvitationProfileKind = 'lifter' | 'coach';
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface AdminInvitationView {
  invitationId: string;
  code: string;
  email: string | null;
  note: string;
  groupLabel: string | null;
  profileKind: InvitationProfileKind;
  planId: string | null;
  planKey: string | null;
  planName: string | null;
  audience: PlanAudience | null;
  grantDays: number | null;
  grantSeats: number | null;
  status: InvitationStatus;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedUserId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface AdminInvitationList {
  data: AdminInvitationView[];
  total: number;
}

export interface AdminInvitationCreate {
  profileKind: InvitationProfileKind;
  email: string | null;
  note: string;
  groupLabel: string | null;
  planId: string | null;
  grantDays: number | null;
  grantSeats: number | null;
  expiresInDays: number;
}

export interface AdminInvitationCreateResult {
  invitation: AdminInvitationView;
  /** The link the admin can send; opens the public `#/join/:code` screen. */
  shareUrl: string;
  audit: AdminAuditEntry | null;
}

export interface AdminInvitationRevokeResult {
  invitation: AdminInvitationView;
  audit: AdminAuditEntry | null;
}

export interface InvitationAcceptResult {
  invitation: AdminInvitationView;
  user: User;
  createdAccount: boolean;
  accessToken?: string;
  refreshToken?: string;
}

export interface AdminSystemTable {
  name: string;
  rows: number;
}

export interface AdminSystemMigration {
  filename: string;
  appliedAt: string;
}

export interface AdminSystemHealth {
  accountsNoActivity: number;
  sessionsActive: number;
  sessionsAbandoned: number;
  invitationsPending: number;
  invitationsExpired: number;
  offersActive: number;
  paymentsDue: number;
}

export interface AdminSystem {
  generatedAt: string;
  api: {
    name: string;
    version: string;
    nodeEnv: string;
    uptimeSeconds: number;
    serverTime: string;
  };
  database: { engine: string; sizeBytes: number | null };
  tables: AdminSystemTable[];
  migrations: AdminSystemMigration[];
  health: AdminSystemHealth;
}

const ADMIN_COACH_PLANS: ReadonlyArray<CoachPlan> = ['free', 'coach', 'gym', 'gym_plus', 'scale'];
const ADMIN_ROLES: ReadonlyArray<Role> = ['lifter', 'trainer', 'coach', 'admin'];

function asCoachPlan(value: unknown): CoachPlan {
  return ADMIN_COACH_PLANS.includes(value as CoachPlan) ? (value as CoachPlan) : 'free';
}

function asAdminRole(value: unknown): Role | null {
  return ADMIN_ROLES.includes(value as Role) ? (value as Role) : null;
}

function asAdminTier(value: unknown): AdminAccountView['tier'] {
  return value === 'premium' || value === 'plan_once' ? value : 'basic';
}

function asSeatLimit(value: unknown): number | null {
  return asNumber(value);
}

function normalizeAdminSeats(raw: unknown): CoachSeatUsage | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') return null;
  const seats = asRecord(raw);
  return {
    plan: asCoachPlan(seats.plan),
    limit: asSeatLimit(seats.limit),
    active: asInt(seats.active),
    pending: asInt(seats.pending),
    connected: asInt(seats.connected),
    used: asInt(seats.used),
    remaining: asSeatLimit(seats.remaining),
  };
}

function normalizeAdminAccount(raw: unknown): AdminAccountView {
  const account = asRecord(raw);
  const roles = asArray<unknown>(account.roles)
    .map((role) => asAdminRole(role))
    .filter((role): role is Role => role !== null);
  return {
    userId: asString(account.userId) ?? '',
    email: asString(account.email) ?? '',
    displayName: asString(account.displayName) ?? 'Account',
    roles: roles.length > 0 ? roles : ['lifter'],
    tier: asAdminTier(account.tier),
    premiumUntil: asString(account.premiumUntil),
    coachPlan: asCoachPlan(account.coachPlan),
    createdAt: asString(account.createdAt) ?? '',
    lastActivityAt: asString(account.lastActivityAt),
    isPremium: account.isPremium === true,
    hasCoachLink: account.hasCoachLink === true,
    seats: normalizeAdminSeats(account.seats),
  };
}

export function normalizeAdminSummary(value: unknown): AdminSummary {
  const raw = asRecord(value);
  const accounts = asRecord(raw.accounts);
  const activity = asRecord(raw.activity);
  const sessions = asRecord(raw.sessions);
  const sets = asRecord(raw.sets);
  const blocks = asRecord(raw.blocks);
  const seats = asRecord(raw.seats);
  const revenue = asRecord(raw.revenue);
  return {
    generatedAt: asString(raw.generatedAt) ?? '',
    accounts: {
      total: asInt(accounts.total),
      lifters: asInt(accounts.lifters),
      coaches: asInt(accounts.coaches),
      admins: asInt(accounts.admins),
    },
    activity: { active7: asInt(activity.active7), active28: asInt(activity.active28) },
    sessions: { completed: asInt(sessions.completed), completed28: asInt(sessions.completed28) },
    sets: { logged: asInt(sets.logged), logged28: asInt(sets.logged28) },
    blocks: { completed: asInt(blocks.completed) },
    businessesByPlan: asArray<unknown>(raw.businessesByPlan).map((entry) => {
      const plan = asRecord(entry);
      return {
        plan: asCoachPlan(plan.plan),
        businesses: asInt(plan.businesses),
        seatsUsed: asInt(plan.seatsUsed),
        seatsLimit: asSeatLimit(plan.seatsLimit),
      };
    }),
    seats: {
      used: asInt(seats.used),
      capacity: asSeatLimit(seats.capacity),
      unlimitedBusinesses: asInt(seats.unlimitedBusinesses),
    },
    revenue: {
      currency: asString(revenue.currency) ?? 'GBP',
      paid: asNumber(revenue.paid) ?? 0,
      paidCount: asInt(revenue.paidCount),
      due: asNumber(revenue.due) ?? 0,
      dueCount: asInt(revenue.dueCount),
      refunded: asNumber(revenue.refunded) ?? 0,
      refundedCount: asInt(revenue.refundedCount),
      mrr: asNumber(revenue.mrr) ?? asNumber(revenue.mrrEquivalent) ?? 0,
      mrrEquivalent: asNumber(revenue.mrrEquivalent) ?? asNumber(revenue.mrr) ?? 0,
      activeSubscriptions: asInt(revenue.activeSubscriptions),
      customContracts: asInt(revenue.customContracts),
      subscriptions: normalizeAdminSubscriptions(revenue.subscriptions),
    },
  };
}

function normalizeAdminSubscriptions(value: unknown): AdminSubscriptionView {
  const raw = asRecord(value);
  return {
    lifterPremium: asInt(raw.lifterPremium),
    coachPaid: asInt(raw.coachPaid),
    customContracts: asInt(raw.customContracts),
    lapsedPremium: asInt(raw.lapsedPremium),
    activeTotal: asInt(raw.activeTotal),
    byPlan: asArray<unknown>(raw.byPlan).map((entry) => {
      const plan = asRecord(entry);
      return {
        audience: plan.audience === 'coach' ? 'coach' : 'lifter',
        planKey: asString(plan.planKey) ?? '',
        label: asString(plan.label) ?? 'Plan',
        count: asInt(plan.count),
        priceMonthly: asNumber(plan.priceMonthly) ?? 0,
        mrr: asNumber(plan.mrr) ?? 0,
      };
    }),
  };
}

export function normalizeAdminAccountList(value: unknown): AdminAccountList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => normalizeAdminAccount(entry)),
    total: asInt(raw.total),
    limit: asInt(raw.limit, 25),
    nextCursor: asString(raw.nextCursor),
    hasMore: raw.hasMore === true,
  };
}

export function normalizeAdminAccountDetail(value: unknown): AdminAccountDetail {
  const base = normalizeAdminAccount(value);
  const raw = asRecord(value);
  const revenue = asRecord(raw.revenue);
  return {
    ...base,
    sessionsCompleted: asInt(raw.sessionsCompleted),
    sessionsLast28: asInt(raw.sessionsLast28),
    setsLogged: asInt(raw.setsLogged),
    clients: asNumber(raw.clients),
    openSlots: asNumber(raw.openSlots),
    bookings: asNumber(raw.bookings),
    revenue:
      raw.revenue === null || raw.revenue === undefined
        ? null
        : {
            currency: asString(revenue.currency) ?? 'GBP',
            paid: asNumber(revenue.paid) ?? 0,
            due: asNumber(revenue.due) ?? 0,
            refunded: asNumber(revenue.refunded) ?? 0,
          },
  };
}

export function normalizeAdminBusinessList(value: unknown): AdminBusinessList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => {
      const business = asRecord(entry);
      return {
        userId: asString(business.userId) ?? '',
        email: asString(business.email) ?? '',
        displayName: asString(business.displayName) ?? 'Business',
        plan: asCoachPlan(business.plan),
        seatsUsed: asInt(business.seatsUsed),
        seatsLimit: asSeatLimit(business.seatsLimit),
        clients: asInt(business.clients),
        pendingInvites: asInt(business.pendingInvites),
        openSlots: asInt(business.openSlots),
        bookings: asInt(business.bookings),
        attended: asInt(business.attended),
        currency: asString(business.currency) ?? 'GBP',
        paid: asNumber(business.paid) ?? 0,
        due: asNumber(business.due) ?? 0,
        refunded: asNumber(business.refunded) ?? 0,
        createdAt: asString(business.createdAt) ?? '',
        lastActivityAt: asString(business.lastActivityAt),
      };
    }),
    total: asInt(raw.total),
  };
}

export function normalizeAdminRevenue(value: unknown): AdminRevenue {
  const raw = asRecord(value);
  const paid = asRecord(raw.paid);
  const due = asRecord(raw.due);
  const refunded = asRecord(raw.refunded);
  return {
    from: asString(raw.from) ?? '',
    to: asString(raw.to) ?? '',
    currency: asString(raw.currency) ?? 'GBP',
    paid: { total: asNumber(paid.total) ?? 0, count: asInt(paid.count) },
    due: { total: asNumber(due.total) ?? 0, count: asInt(due.count) },
    refunded: { total: asNumber(refunded.total) ?? 0, count: asInt(refunded.count) },
    byPeriod: asArray<unknown>(raw.byPeriod).map((entry) => {
      const period = asRecord(entry);
      return {
        period: asString(period.period) ?? '',
        paid: asNumber(period.paid) ?? 0,
        due: asNumber(period.due) ?? 0,
        refunded: asNumber(period.refunded) ?? 0,
        count: asInt(period.count),
      };
    }),
    byBusiness: asArray<unknown>(raw.byBusiness).map((entry) => {
      const business = asRecord(entry);
      return {
        coachId: asString(business.coachId) ?? '',
        displayName: asString(business.displayName) ?? 'Coach',
        currency: asString(business.currency) ?? 'GBP',
        paid: asNumber(business.paid) ?? 0,
        due: asNumber(business.due) ?? 0,
        refunded: asNumber(business.refunded) ?? 0,
      };
    }),
  };
}

function asAuditTargetKind(value: unknown): AdminAuditEntry['targetKind'] {
  return value === 'plan' || value === 'offer' || value === 'invitation' ? value : 'account';
}

function normalizeAuditEntry(value: unknown): AdminAuditEntry {
  const raw = asRecord(value);
  const before = asRecord(raw.before);
  const after = asRecord(raw.after);
  return {
    auditId: asString(raw.auditId) ?? '',
    actorUserId: asString(raw.actorUserId),
    actorEmail: asString(raw.actorEmail),
    action: asString(raw.action) ?? 'account.update',
    targetKind: asAuditTargetKind(raw.targetKind),
    targetUserId: asString(raw.targetUserId),
    targetEmail: asString(raw.targetEmail),
    targetRef: asString(raw.targetRef),
    summary: asString(raw.summary) ?? '',
    before: raw.before === null || raw.before === undefined ? null : before,
    after: raw.after === null || raw.after === undefined ? null : after,
    createdAt: asString(raw.createdAt) ?? '',
  };
}

export function normalizeAdminAuditList(value: unknown): AdminAuditList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => normalizeAuditEntry(entry)),
    total: asInt(raw.total),
    limit: asInt(raw.limit, 50),
    nextCursor: asString(raw.nextCursor),
    hasMore: raw.hasMore === true,
  };
}

export function normalizeAdminUpdateResult(value: unknown): AdminUpdateAccountResult {
  const raw = asRecord(value);
  return {
    account: normalizeAdminAccountDetail(raw.account),
    audit:
      raw.audit === null || raw.audit === undefined ? null : normalizeAuditEntry(raw.audit),
  };
}

function asPlanAudience(value: unknown): PlanAudience {
  return value === 'coach' ? 'coach' : 'lifter';
}

function normalizePlanCatalogEntry(value: unknown): PlanCatalogEntry {
  const raw = asRecord(value);
  return {
    planId: asString(raw.planId) ?? '',
    audience: asPlanAudience(raw.audience),
    planKey: asString(raw.planKey) ?? '',
    displayName: asString(raw.displayName) ?? 'Plan',
    priceMonthly: asNumber(raw.priceMonthly) ?? 0,
    currency: asString(raw.currency) ?? 'GBP',
    seatLimit: asNumber(raw.seatLimit),
    negotiated: raw.negotiated === true,
    active: raw.active !== false,
    sortOrder: asInt(raw.sortOrder),
    accountValue: asString(raw.accountValue) ?? '',
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

export function normalizePlanCatalogList(value: unknown): PlanCatalogList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => normalizePlanCatalogEntry(entry)),
    total: asInt(raw.total),
  };
}

export function normalizePlanWriteResult(value: unknown): AdminPlanWriteResult {
  const raw = asRecord(value);
  return {
    plan: normalizePlanCatalogEntry(raw.plan),
    audit: raw.audit === null || raw.audit === undefined ? null : normalizeAuditEntry(raw.audit),
  };
}

function normalizeOfferView(value: unknown): OfferView {
  const raw = asRecord(value);
  return {
    offerId: asString(raw.offerId) ?? '',
    code: asString(raw.code) ?? '',
    description: asString(raw.description) ?? '',
    discountKind: raw.discountKind === 'amount' ? 'amount' : 'percent',
    discountValue: asNumber(raw.discountValue) ?? 0,
    planId: asString(raw.planId),
    planKey: asString(raw.planKey),
    planName: asString(raw.planName),
    audience: raw.audience === 'coach' ? 'coach' : raw.audience === 'lifter' ? 'lifter' : null,
    startsAt: asString(raw.startsAt),
    endsAt: asString(raw.endsAt),
    usageCap: asNumber(raw.usageCap),
    usageCount: asInt(raw.usageCount),
    active: raw.active !== false,
    redeemable: raw.redeemable === true,
    createdAt: asString(raw.createdAt) ?? '',
    updatedAt: asString(raw.updatedAt) ?? '',
  };
}

export function normalizeOfferList(value: unknown): OfferList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => normalizeOfferView(entry)),
    total: asInt(raw.total),
  };
}

export function normalizeOfferWriteResult(value: unknown): AdminOfferWriteResult {
  const raw = asRecord(value);
  return {
    offer: normalizeOfferView(raw.offer),
    audit: raw.audit === null || raw.audit === undefined ? null : normalizeAuditEntry(raw.audit),
  };
}

function normalizeInvitationStatus(value: unknown): InvitationStatus {
  return value === 'accepted' || value === 'expired' || value === 'revoked' ? value : 'pending';
}

function normalizeAdminInvitation(value: unknown): AdminInvitationView {
  const raw = asRecord(value);
  return {
    invitationId: asString(raw.invitationId) ?? '',
    code: asString(raw.code) ?? '',
    email: asString(raw.email),
    note: asString(raw.note) ?? '',
    groupLabel: asString(raw.groupLabel),
    profileKind: raw.profileKind === 'coach' ? 'coach' : 'lifter',
    planId: asString(raw.planId),
    planKey: asString(raw.planKey),
    planName: asString(raw.planName),
    audience: raw.audience === 'coach' ? 'coach' : raw.audience === 'lifter' ? 'lifter' : null,
    grantDays: asNumber(raw.grantDays),
    grantSeats: asNumber(raw.grantSeats),
    status: normalizeInvitationStatus(raw.status),
    expiresAt: asString(raw.expiresAt) ?? '',
    acceptedAt: asString(raw.acceptedAt),
    acceptedUserId: asString(raw.acceptedUserId),
    createdBy: asString(raw.createdBy),
    createdAt: asString(raw.createdAt) ?? '',
  };
}

export function normalizeInvitationList(value: unknown): AdminInvitationList {
  const raw = asRecord(value);
  return {
    data: asArray<unknown>(raw.data).map((entry) => normalizeAdminInvitation(entry)),
    total: asInt(raw.total),
  };
}

export function normalizeInvitationCreateResult(value: unknown): AdminInvitationCreateResult {
  const raw = asRecord(value);
  return {
    invitation: normalizeAdminInvitation(raw.invitation),
    shareUrl: asString(raw.shareUrl) ?? '',
    audit: raw.audit === null || raw.audit === undefined ? null : normalizeAuditEntry(raw.audit),
  };
}

export function normalizeInvitationRevokeResult(value: unknown): AdminInvitationRevokeResult {
  const raw = asRecord(value);
  return {
    invitation: normalizeAdminInvitation(raw.invitation),
    audit: raw.audit === null || raw.audit === undefined ? null : normalizeAuditEntry(raw.audit),
  };
}

export function normalizeInvitationAcceptResult(value: unknown): InvitationAcceptResult {
  const raw = asRecord(value);
  const result: InvitationAcceptResult = {
    invitation: normalizeAdminInvitation(raw.invitation),
    user: normalizeUser(raw.user),
    createdAccount: raw.createdAccount === true,
  };
  const accessToken = asString(raw.accessToken);
  const refreshToken = asString(raw.refreshToken);
  if (accessToken !== null) result.accessToken = accessToken;
  if (refreshToken !== null) result.refreshToken = refreshToken;
  return result;
}

export function normalizeAdminSystem(value: unknown): AdminSystem {
  const raw = asRecord(value);
  const api = asRecord(raw.api);
  const database = asRecord(raw.database);
  const health = asRecord(raw.health);
  return {
    generatedAt: asString(raw.generatedAt) ?? '',
    api: {
      name: asString(api.name) ?? 'Lift2Fit API',
      version: asString(api.version) ?? '',
      nodeEnv: asString(api.nodeEnv) ?? '',
      uptimeSeconds: asInt(api.uptimeSeconds),
      serverTime: asString(api.serverTime) ?? '',
    },
    database: { engine: asString(database.engine) ?? 'unknown', sizeBytes: asNumber(database.sizeBytes) },
    tables: asArray<unknown>(raw.tables).map((entry) => {
      const table = asRecord(entry);
      return { name: asString(table.name) ?? '', rows: asInt(table.rows) };
    }),
    migrations: asArray<unknown>(raw.migrations).map((entry) => {
      const migration = asRecord(entry);
      return {
        filename: asString(migration.filename) ?? '',
        appliedAt: asString(migration.appliedAt) ?? '',
      };
    }),
    health: {
      accountsNoActivity: asInt(health.accountsNoActivity),
      sessionsActive: asInt(health.sessionsActive),
      sessionsAbandoned: asInt(health.sessionsAbandoned),
      invitationsPending: asInt(health.invitationsPending),
      invitationsExpired: asInt(health.invitationsExpired),
      offersActive: asInt(health.offersActive),
      paymentsDue: asInt(health.paymentsDue),
    },
  };
}

export interface AdminAccountQuery {
  role?: Role | null;
  q?: string | null;
  premium?: boolean | null;
  linked?: boolean | null;
  limit?: number | null;
  cursor?: string | null;
}

export interface CrmPaymentInput {
  clientId: string;
  amount: number;
  currency?: string;
  description?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  status?: CrmPaymentStatus;
  dueOn?: string | null;
}

export interface CrmPaymentPatch {
  amount?: number;
  currency?: string;
  description?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  status?: CrmPaymentStatus;
  dueOn?: string | null;
}

export interface CrmBookingQuery {
  clientId?: string | null;
  from?: string | null;
  to?: string | null;
}

export interface CrmPaymentQuery {
  clientId?: string | null;
  status?: CrmPaymentStatus | null;
  from?: string | null;
  to?: string | null;
}

/** Build a query string, dropping empty values. */
function crmQuery(params: Record<string, string | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, value);
  }
  const query = search.toString();
  return query === '' ? '' : `?${query}`;
}

/** Append `clientId` to a programme/builder path, preserving an existing query. */
function withClientId(path: string, clientId?: string | null): string {
  if (!clientId) return path;
  return `${path}${path.includes('?') ? '&' : '?'}clientId=${encodeURIComponent(clientId)}`;
}

/* ---------------------------------------------------------------------------
   Demo answers for the coach desk. The demo persona has no coach-of-record
   data, so every read resolves to an honest empty shape (and the CRM screens
   show their empty states) rather than inventing clients or money.
   ------------------------------------------------------------------------ */

function demoCrmSummary(): CrmSummary {
  return {
    generatedAt: new Date().toISOString(),
    clients: { total: 0, active: 0, inactive: 0, missed: 0, newLast28: 0 },
    sessions: { today: 0, thisWeek: 0, attendedLast28: 0, missedLast28: 0, nextTotal: 0, next: [] },
    reviews: { due: 0, clients: [] },
    revenue: {
      currency: 'GBP',
      paidTotal: 0,
      dueTotal: 0,
      refundedTotal: 0,
      dueCount: 0,
      paidCount: 0,
      paidLast28: 0,
    },
    canSeeMoney: true,
  };
}

function emptyCrmSlotList(): CrmSlotList {
  return { data: [], from: '', to: '', total: 0, bookedTotal: 0 };
}

function emptyCrmSlotBatch(): CrmSlotBatch {
  return { data: [], series: null, created: 0 };
}

function emptyCrmCoachAvailability(from: string, to: string): CrmCoachAvailability {
  return { coach: null, data: [], from, to, total: 0, subscription: null, locked: false };
}

function emptyCrmMyBookings(): CrmMyBookings {
  return { data: [], total: 0, coach: null };
}

function emptyLifterSchedule(): LifterSchedule {
  return { timezone: 'UTC', active: false, repeatUntil: null, entries: [], days: [] };
}

function emptyCrmBookingList(): CrmBookingList {
  return { data: [], total: 0 };
}

function emptyCrmPaymentList(): CrmPaymentList {
  return { data: [], total: 0, totals: { currency: 'GBP', paid: 0, due: 0, refunded: 0 } };
}

function emptyCrmRevenue(from: string, to: string): CrmRevenue {
  return {
    from,
    to,
    currency: 'GBP',
    paid: { total: 0, count: 0 },
    due: { total: 0, count: 0 },
    refunded: { total: 0, count: 0 },
    byPeriod: [],
  };
}

function emptyCrmInsights(): CrmInsights {
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 28,
    data: [],
    totals: { clients: 0, flagged: 0, byCode: {} },
  };
}

/* ---------------------------------------------------------------------------
   The client
   ------------------------------------------------------------------------ */

export const api = {
  login(email: string, password: string): Promise<ApiResult<AuthResponse | null>> {
    return fallback(
      async () =>
        normalizeAuthResponse(
          await request<unknown>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
        ),
      () => demoAuthResponse(email, null),
      {
        allowApiError: true,
        // Honest failure beats a convincing lie: if nothing answered, say so.
        // The demo persona is still one click away (`session.loginDemo`), which
        // is a deliberate choice rather than a credential that silently "worked".
        requireApi: true,
        transportDetail:
          'We could not reach Lift2Fit, so you were not signed in. If the site was just set up, give it a minute and try again.',
        empty: null,
      },
    );
  },

  register(input: RegisterInput): Promise<ApiResult<AuthResponse | null>> {
    return fallback(
      async () =>
        normalizeAuthResponse(await request<unknown>('/auth/register', { method: 'POST', body: JSON.stringify(input) })),
      // The offline review must land where the real account would: a coaching
      // business gets a coach demo identity, a lifter the default one.
      () => demoAuthResponse(input.email, input.displayName, input.accountType ?? 'lifter'),
      {
        allowApiError: true,
        requireApi: true,
        transportDetail:
          'We could not reach Lift2Fit, so no account was created. Check the connection and try again.',
        empty: null,
      },
    );
  },

  me(): Promise<ApiResult<User>> {
    return fallback(
      async () => normalizeUser(await request<unknown>('/me')),
      () => demoMe(),
    );
  },

  /**
   * 0024 — the caller's recorded policy acceptances, for the profile read-back.
   * A real account must never be shown an invented consent record, so a
   * transport failure resolves to an empty list with the problem attached; the
   * demo persona has no server record and gets the same honest empty list.
   */
  consents(): Promise<ApiResult<PolicyConsentList>> {
    const empty: PolicyConsentList = { data: [], currentVersion: '', effectiveDate: '', documents: [] };
    return fallback(
      async () => normalizePolicyConsentList(await request<unknown>('/me/consents')),
      () => empty,
      { requireRealAccountData: true, empty },
    );
  },

  /** Persist an account-level setting (display name, units, timezone). */
  updateMe(patch: {
    displayName?: string;
    unitSystem?: 'metric' | 'imperial';
    timezone?: string;
  }): Promise<ApiResult<User | null>> {
    return fallback(
      async () =>
        normalizeUser(
          await request<unknown>('/me', { method: 'PATCH', body: JSON.stringify(patch) }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  dashboard(role: Role): Promise<ApiResult<DashboardPayload>> {
    // An admin is a lifter first: their own training view is the lifter one.
    const viewRole: 'lifter' | 'trainer' | 'coach' = role === 'admin' ? 'lifter' : role;
    return fallback(
      async () => {
        const raw = await request<unknown>(`/dashboard?role=${viewRole}`, {}, { retryOnTimeout: true });
        return viewRole === 'lifter' ? normalizeLifterDashboard(raw) : normalizeRosterDashboard(raw, viewRole);
      },
      () => demoDashboard(viewRole),
      { requireRealAccountData: true },
    );
  },

  /**
   * The one next session the dashboard reminds the lifter of: a booked slot
   * wins, then the lifter's own weekly schedule, then an honest "not set". A
   * real account must never be shown the offline demo reminder, so an absent
   * API surfaces an empty state with the problem attached.
   */
  nextSession(): Promise<ApiResult<NextSession>> {
    const empty: NextSession = { kind: 'none', startsAt: null, hasTime: false, dayLabel: null, planName: null, source: 'none' };
    return fallback(
      async () => normalizeNextSession(await request<unknown>('/me/next-session')),
      () => demoNextSession(),
      { requireRealAccountData: true, empty },
    );
  },

  /** The whole schedule: the lifter's week, the scheduleable days, the timezone. */
  schedule(): Promise<ApiResult<LifterSchedule>> {
    return fallback(
      async () => normalizeLifterSchedule(await request<unknown>('/me/schedule')),
      () => demoSchedule(),
      { requireRealAccountData: true, empty: emptyLifterSchedule() },
    );
  },

  /** Set the whole week in one action; the API replaces the pattern atomically. */
  saveSchedule(input: ScheduleInput): Promise<ApiResult<LifterSchedule | null>> {
    return fallback(
      async () =>
        normalizeLifterSchedule(
          await request<unknown>('/me/schedule', { method: 'PUT', body: JSON.stringify(input) }),
        ),
      () => demoSaveSchedule(input),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Stop repeating: future occurrences leave the reminder, the past is untouched. */
  stopSchedule(): Promise<ApiResult<LifterSchedule | null>> {
    return fallback(
      async () =>
        normalizeLifterSchedule(
          await request<unknown>('/me/schedule/stop', { method: 'POST' }),
        ),
      () => demoStopSchedule(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Clear back to "not set". */
  clearSchedule(): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>('/me/schedule', { method: 'DELETE' });
        return true;
      },
      () => {
        demoClearSchedule();
        return true;
      },
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** Skip one occurrence of one entry; the following week's becomes next. */
  skipScheduleEntry(entryId: string): Promise<ApiResult<LifterSchedule | null>> {
    return fallback(
      async () =>
        normalizeLifterSchedule(
          await request<unknown>(`/me/schedule/${encodeURIComponent(entryId)}/skip`, { method: 'POST' }),
        ),
      () => demoSkipScheduleEntry(entryId),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  trainees(): Promise<ApiResult<Trainee[]>> {
    return fallback(
      () => request<Trainee[]>('/trainees'),
      () => demoTrainees(),
    );
  },
  inviteTrainee(email: string): Promise<ApiResult<Trainee>> {
    return fallback(
      () => request<Trainee>('/trainees/invite', { method: 'POST', body: JSON.stringify({ email }) }),
      () => demoInviteTrainee(email),
    );
  },

  /* --------------------------- coach ↔ client --------------------------- */

  /**
   * The lifter's own coach relationship plus their latest pending invite to a
   * coach. A real account must see the API's own answer (a stale token is a
   * 401, not a demo link), so every problem is surfaced with its `detail`.
   */
  coachingLink(): Promise<ApiResult<CoachingLinkPayload>> {
    const empty: CoachingLinkPayload = { link: null, pendingInvite: null };
    return fallback(
      async () => normalizeCoachingLinkPayload(await request<unknown>('/coaching/link')),
      () => empty,
      { surfaceForRealAccount: true, empty },
    );
  },

  /** Sent and received invites, pending first. */
  coachingInvites(): Promise<ApiResult<CoachInvite[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/invites')).data).map(
          normalizeCoachInvite,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /**
   * A lifter invites a coach. The 409s here are product rules, not transport
   * noise: already linked to a coach, or the coach has no free seat. Both carry
   * the API's own `detail`, which the form renders verbatim.
   */
  inviteCoach(input: InviteCoachInput): Promise<ApiResult<CoachInvite | null>> {
    return fallback(
      async () =>
        normalizeCoachInvite(
          await request<unknown>('/coaching/invite', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * Redeem an invite code. Called with the Bearer token when the caller is
   * signed in, and anonymously with a new profile when they are not. The
   * "sign in to accept" 409 and the "already used" 409 are surfaced as-is.
   */
  acceptCoachInvite(
    code: string,
    profile?: { email: string; displayName: string; password: string },
    message?: string | null,
  ): Promise<ApiResult<AcceptCoachInviteResult | null>> {
    return fallback(
      async () => {
        const payload: Record<string, unknown> = { ...(profile ?? {}) };
        if (message !== undefined && message !== null && message.trim() !== '') {
          payload.message = message.trim();
        }
        const raw = asRecord(
          await request<unknown>(`/coaching/invites/${encodeURIComponent(code)}/accept`, {
            method: 'POST',
            body: JSON.stringify(payload),
          }),
        );
        return {
          invite: normalizeCoachInvite(raw.invite),
          link: normalizeCoachLink(raw.link),
          user: normalizeUser(raw.user),
          accessToken: asString(raw.accessToken) ?? undefined,
          refreshToken: asString(raw.refreshToken) ?? undefined,
        };
      },
      () => null,
      // The redeem path is the one call an anonymous visitor makes, so these
      // product 409s/404s must reach the screen even with no token set.
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [401, 404, 409, 422] },
    );
  },

  /** The invited party declines, optionally with a note the other side reads. */
  declineCoachInvite(code: string, message?: string | null): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        const body =
          message !== undefined && message !== null && message.trim() !== ''
            ? JSON.stringify({ message: message.trim() })
            : '{}';
        await request<void>(`/coaching/invites/${encodeURIComponent(code)}/decline`, {
          method: 'POST',
          body,
        });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /**
   * The coach's inbox of lifter-initiated connect requests. Pending requests
   * come first; `pending` is the badge count.
   */
  coachRequests(): Promise<ApiResult<CoachRequestList>> {
    return fallback(
      async () => {
        const raw = asRecord(await request<unknown>('/coaching/requests'));
        return {
          data: asArray<unknown>(raw.data).map((entry) => normalizeCoachInvite(entry)),
          pending: asInt(raw.pending),
        };
      },
      () => ({ data: [], pending: 0 }),
      { surfaceForRealAccount: true, empty: { data: [], pending: 0 } },
    );
  },

  /**
   * The coach's one-stop chat: every live relationship (paid, connected,
   * inquiry) with its tier, last message and unread count. Paid sorts first.
   */
  coachThreads(side?: 'coach' | 'lifter'): Promise<ApiResult<CoachThreadList>> {
    const empty: CoachThreadList = {
      data: [],
      total: 0,
      unread: 0,
      paid: 0,
      connected: 0,
      inquiry: 0,
      managed: 0,
    };
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>(`/coaching/threads${side ? `?side=${side}` : ''}`),
        );
        return {
          data: asArray<unknown>(raw.data).map((entry) => normalizeCoachThread(entry)),
          total: asInt(raw.total),
          unread: asInt(raw.unread),
          paid: asInt(raw.paid),
          connected: asInt(raw.connected),
          inquiry: asInt(raw.inquiry),
          managed: asInt(raw.managed),
        };
      },
      () => empty,
      { surfaceForRealAccount: true, empty },
    );
  },

  /** The coach's prospect surface: inquiries + connected-but-unpaid lifters. */
  coachProspects(): Promise<ApiResult<CoachProspectList>> {
    const empty: CoachProspectList = { data: [], total: 0, inquiry: 0, connected: 0 };
    return fallback(
      async () => {
        const raw = asRecord(await request<unknown>('/coaching/prospects'));
        return {
          data: asArray<unknown>(raw.data).map((entry) => normalizeCoachProspect(entry)),
          total: asInt(raw.total),
          inquiry: asInt(raw.inquiry),
          connected: asInt(raw.connected),
        };
      },
      () => empty,
      { surfaceForRealAccount: true, empty },
    );
  },

  /** Every coach relationship the lifter holds (0026: many at once). */
  coachingLinks(): Promise<ApiResult<CoachLink[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/links')).data)
          .map((entry) => normalizeCoachLink(entry))
          .filter((link): link is CoachLink => link !== null),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /** The inviter cancels an outgoing invite. */
  cancelCoachInvite(code: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/invites/${encodeURIComponent(code)}`, { method: 'DELETE' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** The lifter rewrites what the coach may see. Saves take effect immediately. */
  updateCoachSharedFields(
    sharedFields: SharedField[],
    coachId?: string | null,
  ): Promise<ApiResult<CoachLink | null>> {
    return fallback(
      async () =>
        normalizeCoachLink(
          await request<unknown>(`/coaching/link${crmQuery({ coachId })}`, {
            method: 'PATCH',
            body: JSON.stringify({ sharedFields }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The lifter revokes one coach (0026), or every link when no id is given. */
  revokeCoachLink(coachId?: string | null): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/link${crmQuery({ coachId })}`, { method: 'DELETE' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /* ------------------- coach discovery + packs (0020) ------------------- */

  /**
   * The code-holder's read of an invite before accepting. Public on purpose:
   * the invitee may have no account yet, and it is how they see the pack.
   */
  invitePreview(code: string): Promise<ApiResult<CoachInvite | null>> {
    return fallback(
      async () =>
        normalizeCoachInvite(await request<unknown>(`/coaching/invites/${encodeURIComponent(code)}`)),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [404] },
    );
  },

  /** The coach's own public profile plus their packs. */
  coachProfileBundle(): Promise<ApiResult<CoachProfileBundle | null>> {
    return fallback(
      async () => normalizeCoachProfileBundle(await request<unknown>('/coaching/profile')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Save the coach's profile (including the discoverable opt-in). */
  saveCoachProfile(input: CoachProfileInput): Promise<ApiResult<CoachProfile | null>> {
    return fallback(
      async () =>
        normalizeCoachProfile(
          await request<unknown>('/coaching/profile', { method: 'PUT', body: JSON.stringify(input) }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The coach's own packs, archived included. */
  coachPacks(): Promise<ApiResult<CoachPackOwner[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/packs')).data).map(
          normalizeCoachPackOwner,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  createCoachPack(input: CoachPackInput): Promise<ApiResult<CoachPackOwner | null>> {
    return fallback(
      async () =>
        normalizeCoachPackOwner(
          await request<unknown>('/coaching/packs', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  updateCoachPack(packId: string, patch: CoachPackPatch): Promise<ApiResult<CoachPackOwner | null>> {
    return fallback(
      async () =>
        normalizeCoachPackOwner(
          await request<unknown>(`/coaching/packs/${encodeURIComponent(packId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Archive: hidden from discovery, closed to new subscribers, subscribers kept. */
  archiveCoachPack(packId: string): Promise<ApiResult<CoachPackOwner | null>> {
    return fallback(
      async () =>
        normalizeCoachPackOwner(
          await request<unknown>(`/coaching/packs/${encodeURIComponent(packId)}/archive`, {
            method: 'POST',
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  restoreCoachPack(packId: string): Promise<ApiResult<CoachPackOwner | null>> {
    return fallback(
      async () =>
        normalizeCoachPackOwner(
          await request<unknown>(`/coaching/packs/${encodeURIComponent(packId)}/restore`, {
            method: 'POST',
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Hard delete — the API refuses when subscribers or slots depend on it. */
  deleteCoachPack(packId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/packs/${encodeURIComponent(packId)}`, { method: 'DELETE' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** The coach's published places (a coach may have several). */
  coachLocations(): Promise<ApiResult<CoachLocation[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/locations')).data).map(
          normalizeCoachLocation,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /** Replace the coach's published places in one write. */
  saveCoachLocations(
    locations: Array<{ label?: string | null; address: string; sortOrder?: number }>,
  ): Promise<ApiResult<CoachLocation[]>> {
    return fallback(
      async () =>
        asArray<unknown>(
          asRecord(
            await request<unknown>('/coaching/locations', {
              method: 'PUT',
              body: JSON.stringify({ locations }),
            }),
          ).data,
        ).map(normalizeCoachLocation),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /** The named coaches who work at this business. */
  coachTeam(): Promise<ApiResult<CoachTeamMember[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/team')).data).map(
          normalizeCoachTeamMember,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  createCoachTeamMember(input: {
    displayName: string;
    title?: string | null;
    bio?: string | null;
    userId?: string | null;
    sortOrder?: number;
  }): Promise<ApiResult<CoachTeamMember | null>> {
    return fallback(
      async () =>
        normalizeCoachTeamMember(
          await request<unknown>('/coaching/team', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  updateCoachTeamMember(
    memberId: string,
    input: {
      displayName: string;
      title?: string | null;
      bio?: string | null;
      userId?: string | null;
      sortOrder?: number;
    },
  ): Promise<ApiResult<CoachTeamMember | null>> {
    return fallback(
      async () =>
        normalizeCoachTeamMember(
          await request<unknown>(`/coaching/team/${encodeURIComponent(memberId)}`, {
            method: 'PATCH',
            body: JSON.stringify(input),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  deleteCoachTeamMember(memberId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/team/${encodeURIComponent(memberId)}`, { method: 'DELETE' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /* ------------------------------ support (0035) ------------------------ */

  /**
   * The caller's own support thread, created on first read. Every account has
   * one, so the chat interface can show "Help" by default.
   */
  support(): Promise<ApiResult<SupportConversation | null>> {
    return fallback(
      async () => normalizeSupportConversation(await request<unknown>('/support')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  sendSupportMessage(body: string): Promise<ApiResult<SupportMessage | null>> {
    return fallback(
      async () =>
        normalizeSupportMessage(
          await request<unknown>('/support/messages', {
            method: 'POST',
            body: JSON.stringify({ body }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  supportUnread(): Promise<ApiResult<number>> {
    return fallback(
      async () => {
        const raw = asRecord(await request<unknown>('/support/unread'));
        return asNumber(raw.unread) ?? 0;
      },
      () => 0,
      { surfaceForRealAccount: true, empty: 0 },
    );
  },

  /* ---------------------- platform reviews (0037) ------------------------ */

  /** The caller's own review of Lift2Fit, or null before they rate it. */
  myPlatformReview(): Promise<ApiResult<PlatformReview | null>> {
    return fallback(
      async () => normalizePlatformReview(asRecord(await request<unknown>('/support/review')).review),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * Write or replace the caller's review. One row per account: rating again is
   * an edit, not a second vote.
   */
  savePlatformReview(input: { rating: number; body?: string | null }): Promise<ApiResult<PlatformReview | null>> {
    return fallback(
      async () =>
        normalizePlatformReview(
          asRecord(
            await request<unknown>('/support/review', {
              method: 'PUT',
              body: JSON.stringify({ rating: input.rating, body: input.body ?? null }),
            }),
          ).review,
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /* ---------------------- the support team (0038) ----------------------- */

  /** The Staff page: the team summary, the bot row and every agent. */
  adminStaff(): Promise<ApiResult<SupportStaffList | null>> {
    return fallback(
      async () => normalizeSupportStaffList(await request<unknown>('/admin/staff')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  adminStaffMember(userId: string): Promise<ApiResult<SupportStaffDetail | null>> {
    return fallback(
      async () =>
        normalizeSupportStaffDetail(
          await request<unknown>(`/admin/staff/${encodeURIComponent(userId)}`),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Add an existing account to the team, or update it. `isBot` is admin-only. */
  addSupportStaff(input: {
    email: string;
    shiftStartMinute?: number;
    shiftEndMinute?: number;
    timezone?: string;
    capacity?: number;
    active?: boolean;
    onShift?: boolean;
    isBot?: boolean;
  }): Promise<ApiResult<SupportStaffView | null>> {
    return fallback(
      async () =>
        normalizeSupportStaff(
          await request<unknown>('/admin/staff', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  updateSupportStaff(
    userId: string,
    patch: {
      active?: boolean;
      onShift?: boolean;
      shiftStartMinute?: number;
      shiftEndMinute?: number;
      timezone?: string;
      capacity?: number;
    },
  ): Promise<ApiResult<SupportStaffView | null>> {
    return fallback(
      async () =>
        normalizeSupportStaff(
          await request<unknown>(`/admin/staff/${encodeURIComponent(userId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  removeSupportStaff(userId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/admin/staff/${encodeURIComponent(userId)}`, { method: 'DELETE' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** The admin steps into a waiting/assigned chat. */
  adminTakeoverThread(threadId: string): Promise<ApiResult<SupportConversation | null>> {
    return fallback(
      async () =>
        normalizeSupportConversation(
          await request<unknown>(
            `/admin/support/threads/${encodeURIComponent(threadId)}/takeover`,
            { method: 'POST' },
          ),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /* ----------------------- the staff workspace -------------------------- */

  staffInbox(): Promise<ApiResult<SupportInbox | null>> {
    return fallback(
      async () => normalizeSupportInbox(await request<unknown>('/staff/inbox')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  setStaffShift(onShift: boolean): Promise<ApiResult<SupportStaffView | null>> {
    return fallback(
      async () =>
        normalizeSupportStaff(
          await request<unknown>('/staff/shift', {
            method: 'POST',
            body: JSON.stringify({ onShift }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  staffTakeSession(sessionId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/staff/sessions/${encodeURIComponent(sessionId)}/take`, {
          method: 'POST',
        });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  staffSession(sessionId: string): Promise<ApiResult<SupportConversation | null>> {
    return fallback(
      async () =>
        normalizeSupportConversation(
          await request<unknown>(`/staff/sessions/${encodeURIComponent(sessionId)}`),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  staffReplySession(sessionId: string, body: string): Promise<ApiResult<SupportMessage | null>> {
    return fallback(
      async () =>
        normalizeSupportMessage(
          await request<unknown>(`/staff/sessions/${encodeURIComponent(sessionId)}/reply`, {
            method: 'POST',
            body: JSON.stringify({ body }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  staffCloseSession(
    sessionId: string,
    resolution: 'solved' | 'escalated' | 'abandoned' = 'solved',
  ): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/staff/sessions/${encodeURIComponent(sessionId)}/close`, {
          method: 'POST',
          body: JSON.stringify({ resolution }),
        });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** The account says the current question is answered. */
  closeSupportSession(): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>('/support/session/close', { method: 'POST' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** The chat button's badge: messages from accounts the platform has not read. */
  adminSupportUnread(): Promise<ApiResult<number>> {
    return fallback(
      async () => {
        const raw = asRecord(await request<unknown>('/admin/support/unread'));
        return asNumber(raw.unread) ?? 0;
      },
      () => 0,
      { surfaceForRealAccount: true, empty: 0 },
    );
  },

  /** The admin queue: every support thread, the account's unread first. */
  adminSupportThreads(): Promise<ApiResult<SupportThreadList | null>> {
    return fallback(
      async () => normalizeSupportThreadList(await request<unknown>('/admin/support/threads')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  adminSupportThread(threadId: string): Promise<ApiResult<SupportConversation | null>> {
    return fallback(
      async () =>
        normalizeSupportConversation(
          await request<unknown>(`/admin/support/threads/${encodeURIComponent(threadId)}`),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  adminSupportReply(threadId: string, body: string): Promise<ApiResult<SupportMessage | null>> {
    return fallback(
      async () =>
        normalizeSupportMessage(
          await request<unknown>(`/admin/support/threads/${encodeURIComponent(threadId)}/messages`, {
            method: 'POST',
            body: JSON.stringify({ body }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /* ------------------------- internal coaches (0030) -------------------- */

  /** The gym's internal coaches, the plan cap and pending invites. */
  internalCoaches(): Promise<ApiResult<InternalCoachList | null>> {
    return fallback(
      async () => normalizeInternalCoachList(await request<unknown>('/coaching/internal-coaches')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Invite an email to join the gym's team. The cap is enforced by the API. */
  inviteInternalCoach(
    email: string,
    label?: string | null,
  ): Promise<ApiResult<InternalCoachInvite | null>> {
    return fallback(
      async () =>
        normalizeInternalCoachInvite(
          await request<unknown>('/coaching/internal-coaches/invites', {
            method: 'POST',
            body: JSON.stringify({ email, label: label ?? null }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * The PUBLIC peek behind an internal-coach invitation link (0033): which gym
   * asked, the email it is bound to, and the gym's own nickname for the invitee.
   * Readable signed out, because the invitee may not have an account yet.
   */
  internalCoachInvitePreview(code: string): Promise<ApiResult<{
    email: string;
    label: string | null;
    status: string;
    expired: boolean;
    gymName: string;
  } | null>> {
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>(
            `/coaching/internal-coaches/invites/${encodeURIComponent(code)}`,
          ),
        );
        return {
          email: asString(raw.email) ?? '',
          label: asString(raw.label),
          status: asString(raw.status) ?? 'pending',
          expired: raw.expired === true,
          gymName: asString(raw.gymName) ?? 'A gym',
        };
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The invitee declines with their own login. */
  declineInternalCoachInvite(code: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(
          `/coaching/internal-coaches/invites/${encodeURIComponent(code)}/decline`,
          { method: 'POST' },
        );
        return true;
      },
      () => false,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** The invitee accepts with their own login (an existing coach account). */
  acceptInternalCoachInvite(
    code: string,
  ): Promise<ApiResult<{ parentCoachId: string; parentName: string } | null>> {
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>(
            `/coaching/internal-coaches/invites/${encodeURIComponent(code)}/accept`,
            { method: 'POST' },
          ),
        );
        return {
          parentCoachId: asString(raw.parentCoachId) ?? '',
          parentName: asString(raw.parentName) ?? 'Your gym',
        };
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The gym withdraws a pending invitation. */
  revokeInternalCoachInvite(inviteId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/internal-coaches/invites/${encodeURIComponent(inviteId)}`, {
          method: 'DELETE',
        });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** Remove an internal coach; their clients return to the owner. */
  removeInternalCoach(coachId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/internal-coaches/${encodeURIComponent(coachId)}`, {
          method: 'DELETE',
        });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** Assign or reassign a client to an internal coach (null returns them). */
  assignClient(
    clientId: string,
    internalCoachId: string | null,
  ): Promise<ApiResult<AssignmentResult | null>> {
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>(`/coaching/clients/${encodeURIComponent(clientId)}/assign`, {
            method: 'POST',
            body: JSON.stringify({ internalCoachId }),
          }),
        );
        return {
          clientId: asString(raw.clientId) ?? clientId,
          assignedCoachId: asString(raw.assignedCoachId),
          assignedCoachName: asString(raw.assignedCoachName),
        };
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The coach's own score, distribution and recent comments. Always visible. */
  coachReceivedReviews(): Promise<ApiResult<CoachReviewSummary | null>> {
    return fallback(
      async () =>
        normalizeCoachReviewSummary(await request<unknown>('/coaching/reviews/received')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The reviews the caller (as a lifter) has written. */
  lifterReviews(): Promise<ApiResult<CoachReview[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/reviews/mine')).data).map(
          normalizeCoachReview,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /** Leave or edit the caller's review of one coach (one per coach). */
  saveReview(
    coachId: string,
    input: { rating: number; comment?: string | null },
  ): Promise<ApiResult<CoachReview | null>> {
    return fallback(
      async () =>
        normalizeCoachReview(
          await request<unknown>(`/coaching/reviews/${encodeURIComponent(coachId)}`, {
            method: 'PUT',
            body: JSON.stringify(input),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Search published coaches by name, location or pack; optional kind filter. */
  discoverCoaches(query: {
    q?: string;
    kind?: CoachPackKind | null;
    limit?: number;
    offset?: number;
  } = {}): Promise<ApiResult<CoachDiscovery | null>> {
    const params = new URLSearchParams();
    if (query.q?.trim()) params.set('q', query.q.trim());
    if (query.kind) params.set('kind', query.kind);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.offset !== undefined) params.set('offset', String(query.offset));
    const suffix = params.toString();
    return fallback(
      async () =>
        normalizeCoachDiscovery(
          await request<unknown>(`/coaching/discover${suffix ? `?${suffix}` : ''}`),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** One published coach's public listing. */
  discoverCoach(coachId: string): Promise<ApiResult<CoachPublicListing | null>> {
    return fallback(
      async () =>
        normalizeCoachPublicListing(
          await request<unknown>(`/coaching/discover/${encodeURIComponent(coachId)}`),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [404] },
    );
  },

  /** The caller's own pack subscriptions (active first). */
  packSubscriptions(): Promise<ApiResult<CoachPackSubscriptionList | null>> {
    return fallback(
      async () =>
        normalizeCoachPackSubscriptionList(await request<unknown>('/coaching/pack-subscriptions')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** A coach reads one client's pack subscription. */
  clientPackSubscriptions(clientId: string): Promise<ApiResult<CoachPackSubscriptionList | null>> {
    return fallback(
      async () =>
        normalizeCoachPackSubscriptionList(
          await request<unknown>(
            `/coaching/pack-subscriptions?clientId=${encodeURIComponent(clientId)}`,
          ),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * Subscribe to a pack. An unlinked lifter gets the API's 409 naming the reason.
   * `offer` claims a code and the share behind it; a code that does not apply
   * never blocks the sale — the result says so in `offerNote`.
   */
  subscribePack(
    packId: string,
    offer?: { code?: string; referrerId?: string },
  ): Promise<ApiResult<CoachPackSubscribeResult | null>> {
    const payload: Record<string, unknown> = { packId };
    if (offer?.code) payload.code = offer.code;
    if (offer?.referrerId) payload.referrerId = offer.referrerId;
    return fallback(
      async () =>
        normalizeCoachPackSubscribe(
          await request<unknown>('/coaching/pack-subscriptions', {
            method: 'POST',
            body: JSON.stringify(payload),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /* --------------------------- offers (0039) --------------------------- */

  /** The coach's offers, with how many times each has been used. */
  coachOffers(): Promise<ApiResult<CoachOffer[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/offers')).data).map(
          normalizeCoachOffer,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  createCoachOffer(input: CoachOfferInput): Promise<ApiResult<CoachOffer | null>> {
    return fallback(
      async () =>
        normalizeCoachOffer(
          await request<unknown>('/coaching/offers', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  updateCoachOffer(input: {
    offerId: string;
    percentOff?: number;
    months?: number;
    code?: string | null;
    referrerPercentOff?: number | null;
    referrerMonths?: number | null;
    endsAt?: string | null;
    active?: boolean;
  }): Promise<ApiResult<CoachOffer | null>> {
    const { offerId, ...patch } = input;
    return fallback(
      async () =>
        normalizeCoachOffer(
          await request<unknown>(`/coaching/offers/${encodeURIComponent(offerId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  deleteCoachOffer(offerId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/coaching/offers/${encodeURIComponent(offerId)}`, { method: 'DELETE' });
        return true;
      },
      () => true,
      { surfaceForRealAccount: true, empty: false },
    );
  },

  /** Who used which code, and what both sides actually received. */
  offerRedemptions(): Promise<ApiResult<OfferRedemption[]>> {
    return fallback(
      async () =>
        asArray<unknown>(asRecord(await request<unknown>('/coaching/offers/redemptions')).data).map(
          normalizeOfferRedemption,
        ),
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /** The caller's own coach: identity, rating, packs, live offers, my review. */
  myCoachCard(): Promise<ApiResult<LifterCoachCard | null>> {
    return fallback(
      async () => normalizeLifterCoachCard(await request<unknown>('/coaching/my-coach')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** 0040/0042 — copy a block into a new inactive one, with fresh day/exercise ids. */
  copyProgrammePlan(
    planId: string,
    name: string | null,
    clientId?: string,
    when?: { startNow?: boolean; startOn?: string | null },
  ): Promise<ApiResult<{ planId: string } | null>> {
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>(`/programme/plans/${encodeURIComponent(planId)}/copy`, {
            method: 'POST',
            body: JSON.stringify({
              name,
              ...(clientId ? { clientId } : {}),
              ...(when?.startNow ? { startNow: true } : {}),
              ...(when?.startOn ? { startOn: when.startOn } : {}),
            }),
          }),
        );
        return { planId: asStringText(raw.planId) };
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * 0042 — New Block in one call: name + start-from + when. `copyFromPlanId`
   * null starts empty (the editor adds the days); otherwise the source is
   * deep-copied. `startNow` activates, `startOn` schedules, neither just saves.
   */
  createProgrammePlan(input: {
    name: string;
    copyFromPlanId: string | null;
    clientId?: string;
    startNow?: boolean;
    startOn?: string | null;
  }): Promise<ApiResult<{ planId: string } | null>> {
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>('/programme/plans', {
            method: 'POST',
            body: JSON.stringify({
              name: input.name.trim(),
              copyFromPlanId: input.copyFromPlanId,
              ...(input.clientId ? { clientId: input.clientId } : {}),
              ...(input.startNow ? { startNow: true } : {}),
              ...(input.startNow || !input.startOn ? {} : { startOn: input.startOn }),
            }),
          }),
        );
        return { planId: asStringText(raw.planId) };
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Ask what a code would give this buyer, before they buy. */
  resolveOffer(code: string, packId: string): Promise<ApiResult<OfferPreview | null>> {
    return fallback(
      async () => {
        const raw = asRecord(
          await request<unknown>(
            `/coaching/offers/resolve?code=${encodeURIComponent(code)}&packId=${encodeURIComponent(packId)}`,
          ),
        );
        return {
          applies: raw.applies === true,
          reason: asString(raw.reason),
          name: asString(raw.name) ?? undefined,
          kind: raw.kind === 'referral' ? ('referral' as const) : raw.kind === 'time_limited' ? ('time_limited' as const) : undefined,
          code: asString(raw.code),
          percentOff: raw.percentOff === undefined ? undefined : asInt(raw.percentOff),
          months: raw.months === undefined ? undefined : Math.max(1, asInt(raw.months)),
          referrerPercentOff:
            raw.referrerPercentOff === null || raw.referrerPercentOff === undefined
              ? null
              : asInt(raw.referrerPercentOff),
          priceBefore: Number(raw.priceBefore ?? 0),
          priceAfter: Number(raw.priceAfter ?? 0),
        };
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** End a subscription. The ledger and existing bookings are untouched. */
  cancelPackSubscription(
    subscriptionId: string,
  ): Promise<ApiResult<CoachPackSubscription | null>> {
    return fallback(
      async () =>
        normalizeCoachPackSubscription(
          await request<unknown>(
            `/coaching/pack-subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
            { method: 'POST' },
          ),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The coach's roster plus the seat gauge, from `GET /coaching/clients`. */
  coachClients(): Promise<ApiResult<CoachRoster | null>> {
    return fallback(
      async () => normalizeCoachRoster(await request<unknown>('/coaching/clients')),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** One client's shared detail (session summary and notes), never their log fields. */
  coachTrainee(userId: string): Promise<ApiResult<TraineeDetail | null>> {
    return fallback(
      async () => normalizeTraineeDetail(await request<unknown>(`/trainees/${encodeURIComponent(userId)}`)),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * The coach chat thread. A lifter reads their own; a coach passes `clientId`
   * for a client whose link they hold. Reading marks the other side's messages
   * read, so the API's `unread` is the badge as it stood on open.
   */
  coachingMessages(query: CoachMessageQuery = {}): Promise<ApiResult<CoachMessageList>> {
    const empty: CoachMessageList = {
      data: [],
      total: 0,
      unread: 0,
      hasMore: false,
      linkId: null,
      coach: null,
      trainee: null,
    };
    return fallback(
      async () =>
        normalizeCoachMessageList(
          await request<unknown>(
            `/coaching/messages${crmQuery({
              since: query.since,
              limit: query.limit === null || query.limit === undefined ? null : String(query.limit),
              clientId: query.clientId,
              coachId: query.coachId,
            })}`,
          ),
        ),
      () => empty,
      { surfaceForRealAccount: true, empty },
    );
  },

  /** The badge poll — the unread count without fetching the thread. */
  coachingUnread(
    query: { clientId?: string | null; coachId?: string | null } = {},
  ): Promise<ApiResult<CoachUnread>> {
    const empty: CoachUnread = { unread: 0, linkId: null };
    return fallback(
      async () =>
        normalizeCoachUnread(
          await request<unknown>(
            `/coaching/messages/unread${crmQuery({ clientId: query.clientId, coachId: query.coachId })}`,
          ),
        ),
      () => empty,
      { surfaceForRealAccount: true, empty },
    );
  },

  /**
   * Send one message. A 404 means there is no link at all, a 409 means the link
   * is no longer live; both carry the API's own `detail`, which the composer
   * renders verbatim rather than dropping the text. The coach passes `clientId`;
   * a lifter with several coaches passes `coachId`.
   */
  sendCoachMessage(
    body: string,
    clientId?: string | null,
    coachId?: string | null,
  ): Promise<ApiResult<CoachMessage | null>> {
    const payload: Record<string, string> = { body };
    if (clientId) payload.clientId = clientId;
    if (coachId) payload.coachId = coachId;
    return fallback<CoachMessage | null>(
      async () =>
        normalizeCoachMessage(
          await request<unknown>('/coaching/messages', {
            method: 'POST',
            body: JSON.stringify(payload),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /* ---------------------------- the coach desk (CRM) -------------------- */

  /**
   * The coach's home. A real coach must see the API's own answer, including a
   * 403 when the account does not hold the role, so the problem is surfaced
   * rather than hidden behind demo zeros.
   */
  crmSummary(): Promise<ApiResult<CrmSummary | null>> {
    return fallback<CrmSummary | null>(
      async () => normalizeCrmSummary(await request<unknown>('/crm/summary')),
      () => demoCrmSummary(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * The coach's review to-do: clients whose active block is in its final week or
   * already overdue, soonest block-end first (`GET /crm/reviews`).
   */
  crmReviews(): Promise<ApiResult<CrmReviews>> {
    const empty: CrmReviews = {
      generatedAt: new Date().toISOString(),
      data: [],
      total: 0,
      overdue: 0,
      windowDays: 7,
    };
    return fallback(
      async () => normalizeCrmReviews(await request<unknown>('/crm/reviews')),
      () => empty,
      { surfaceForRealAccount: true, empty },
    );
  },

  /**
   * The coach's To-do queue: every actionable coaching task, kind-tagged,
   * soonest due first (`GET /crm/tasks`). Reviews are the first kind — the
   * screen renders the envelope, so a new kind needs no screen change.
   */
  crmTasks(): Promise<ApiResult<CrmTasks | null>> {
    const empty: CrmTasks = {
      generatedAt: new Date().toISOString(),
      data: [],
      total: 0,
      overdue: 0,
      byKind: {},
    };
    return fallback<CrmTasks | null>(
      async () => normalizeCrmTasks(await request<unknown>('/crm/tasks')),
      () => empty,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * A linked client's completed sessions, read-only and `sessions`-gated by the
   * API. A coach never edits these; set-derived fields are withheld unless the
   * link also shares `sets`.
   */
  clientSessions(clientId: string, limit = 50): Promise<ApiResult<SessionTotalsWire[]>> {
    return fallback(
      async () => {
        const page = await request<unknown>(
          `/sessions${crmQuery({ limit: String(limit), clientId })}`,
        );
        const list = Array.isArray(page) ? page : asArray<unknown>(asRecord(page).data);
        return list
          .map((entry) => normalizeSessionTotals(entry))
          .filter((entry): entry is SessionTotalsWire => entry !== null);
      },
      () => [],
      { surfaceForRealAccount: true, empty: [] },
    );
  },

  /** A linked client's set-derived summary (volume, RPE, compliance, streak). */
  clientStats(clientId: string): Promise<ApiResult<ClientStatsSummary | null>> {
    return fallback<ClientStatsSummary | null>(
      async () =>
        normalizeClientStatsSummary(await request<unknown>(`/stats/summary${crmQuery({ clientId })}`)),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The coach's schedule for a range, `booked / capacity` and who holds each place. */
  crmSlots(from?: string | null, to?: string | null): Promise<ApiResult<CrmSlotList | null>> {
    return fallback<CrmSlotList | null>(
      async () =>
        normalizeCrmSlotList(
          await request<unknown>(`/crm/slots${crmQuery({ from, to })}`),
        ),
      () => emptyCrmSlotList(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Create availability. Capacity is enforced by the API, never here. */
  crmCreateSlot(input: CrmSlotInput): Promise<ApiResult<CrmSlot | null>> {
    return fallback<CrmSlot | null>(
      async () =>
        normalizeCrmSlot(await request<unknown>('/crm/slots', { method: 'POST', body: JSON.stringify(input) })),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 409, 422] },
    );
  },

  /**
   * Create a batch of slots — optionally as one recurring series — in a single
   * request. The server expands the rule in one transaction; capacity is still
   * the API's, never this client's.
   */
  crmCreateSlotBatch(input: CrmSlotBatchInput): Promise<ApiResult<CrmSlotBatch | null>> {
    return fallback<CrmSlotBatch | null>(
      async () =>
        normalizeCrmSlotBatch(
          await request<unknown>('/crm/slots/batch', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => emptyCrmSlotBatch(),
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 409, 422] },
    );
  },

  /** Repeat slots already on the calendar forward as one series, in one request. */
  crmRepeatSlots(
    slotIds: string[],
    recurrence: CrmRecurrenceInput,
  ): Promise<ApiResult<CrmSlotBatch | null>> {
    return fallback<CrmSlotBatch | null>(
      async () =>
        normalizeCrmSlotBatch(
          await request<unknown>('/crm/slot-series/repeat', {
            method: 'POST',
            body: JSON.stringify({ slotIds, recurrence }),
          }),
        ),
      () => emptyCrmSlotBatch(),
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /**
   * Stop a series: future unbooked occurrences cancel, past ones stay, and a
   * booked future occurrence is kept and reported — never silently destroyed.
   */
  crmStopSlotSeries(seriesId: string): Promise<ApiResult<CrmSlotSeriesStop | null>> {
    return fallback<CrmSlotSeriesStop | null>(
      async () =>
        normalizeCrmSlotSeriesStop(
          await request<unknown>(`/crm/slot-series/${encodeURIComponent(seriesId)}/stop`, {
            method: 'POST',
            body: '{}',
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /** Move, resize, re-kind or cancel a slot. A refusal carries the API's `detail`. */
  crmUpdateSlot(slotId: string, patch: CrmSlotPatch): Promise<ApiResult<CrmSlot | null>> {
    return fallback<CrmSlot | null>(
      async () =>
        normalizeCrmSlot(
          await request<unknown>(`/crm/slots/${encodeURIComponent(slotId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /** Delete an empty slot. A slot that still holds places is refused by the API. */
  crmDeleteSlot(slotId: string): Promise<ApiResult<boolean>> {
    return fallback<boolean>(
      async () => {
        await request<void>(`/crm/slots/${encodeURIComponent(slotId)}`, { method: 'DELETE' });
        return true;
      },
      () => false,
      { surfaceForRealAccount: true, empty: false, surfaceStatuses: [400, 404, 409] },
    );
  },

  /**
   * Book a client into a slot. The 409 that names a full slot (or a duplicate
   * place) is a product rule: it is surfaced verbatim, never rounded away.
   */
  crmBookSlot(slotId: string, clientId: string): Promise<ApiResult<CrmBookSlotResult | null>> {
    return fallback<CrmBookSlotResult | null>(
      async () =>
        normalizeCrmBookSlot(
          await request<unknown>(`/crm/slots/${encodeURIComponent(slotId)}/book`, {
            method: 'POST',
            body: JSON.stringify({ clientId }),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /** Bookings for the coach, optionally scoped to a client and a slot range. */
  crmBookings(query: CrmBookingQuery = {}): Promise<ApiResult<CrmBookingList | null>> {
    return fallback<CrmBookingList | null>(
      async () =>
        normalizeCrmBookingList(
          await request<unknown>(
            `/crm/bookings${crmQuery({ clientId: query.clientId, from: query.from, to: query.to })}`,
          ),
        ),
      () => emptyCrmBookingList(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * A lifter books themselves into their own coach's slot. The body is empty —
   * the server resolves the caller as the client through the active link, so
   * this can never book somebody else.
   */
  crmBookMyPlace(slotId: string): Promise<ApiResult<CrmBookSlotResult | null>> {
    return fallback<CrmBookSlotResult | null>(
      async () =>
        normalizeCrmBookSlot(
          await request<unknown>(`/crm/slots/${encodeURIComponent(slotId)}/book`, {
            method: 'POST',
            body: '{}',
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /** The lifter's own coach and that coach's upcoming open slots. */
  crmCoachAvailability(
    from?: string | null,
    to?: string | null,
    coachId?: string | null,
  ): Promise<ApiResult<CrmCoachAvailability | null>> {
    return fallback<CrmCoachAvailability | null>(
      async () =>
        normalizeCrmCoachAvailability(
          await request<unknown>(`/crm/coach-availability${crmQuery({ from, to, coachId })}`),
        ),
      () => emptyCrmCoachAvailability(from ?? '', to ?? ''),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** The caller's own places, whichever product role they hold. */
  crmMyBookings(from?: string | null, to?: string | null): Promise<ApiResult<CrmMyBookings | null>> {
    return fallback<CrmMyBookings | null>(
      async () =>
        normalizeCrmMyBookings(await request<unknown>(`/crm/my-bookings${crmQuery({ from, to })}`)),
      () => emptyCrmMyBookings(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Cancel a booking. Cancelling frees the place immediately, per the API. */
  crmCancelBooking(bookingId: string): Promise<ApiResult<CrmBooking | null>> {
    return fallback<CrmBooking | null>(
      async () =>
        normalizeCrmBooking(
          await request<unknown>(`/crm/bookings/${encodeURIComponent(bookingId)}/cancel`, {
            method: 'POST',
            body: '{}',
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409] },
    );
  },

  /** Mark a booking attended or no-show. */
  crmMarkBooking(
    bookingId: string,
    outcome: 'attend' | 'no-show',
  ): Promise<ApiResult<CrmBooking | null>> {
    return fallback<CrmBooking | null>(
      async () =>
        normalizeCrmBooking(
          await request<unknown>(`/crm/bookings/${encodeURIComponent(bookingId)}/${outcome}`, {
            method: 'POST',
            body: '{}',
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409] },
    );
  },

  /** The payments ledger, filtered by client and/or status. */
  crmPayments(query: CrmPaymentQuery = {}): Promise<ApiResult<CrmPaymentList | null>> {
    return fallback<CrmPaymentList | null>(
      async () =>
        normalizeCrmPaymentList(
          await request<unknown>(
            `/crm/payments${crmQuery({
              clientId: query.clientId,
              status: query.status,
              from: query.from,
              to: query.to,
            })}`,
          ),
        ),
      () => emptyCrmPaymentList(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /** Create a due payment. Money is a ledger row, never an invoice. */
  crmCreatePayment(input: CrmPaymentInput): Promise<ApiResult<CrmPayment | null>> {
    return fallback<CrmPayment | null>(
      async () =>
        normalizeCrmPayment(
          await request<unknown>('/crm/payments', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /** Mark a payment paid or refunded (or adjust it). */
  crmUpdatePayment(paymentId: string, patch: CrmPaymentPatch): Promise<ApiResult<CrmPayment | null>> {
    return fallback<CrmPayment | null>(
      async () =>
        normalizeCrmPayment(
          await request<unknown>(`/crm/payments/${encodeURIComponent(paymentId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null, surfaceStatuses: [400, 404, 409, 422] },
    );
  },

  /**
   * Delete a payment logged in error. Refunding is the normal path; this is for
   * a mistyped row. The 204 is the whole answer.
   */
  crmDeletePayment(paymentId: string): Promise<ApiResult<boolean>> {
    return fallback<boolean>(
      async () => {
        await request<void>(`/crm/payments/${encodeURIComponent(paymentId)}`, { method: 'DELETE' });
        return true;
      },
      () => false,
      { surfaceForRealAccount: true, empty: false, surfaceStatuses: [400, 404, 409] },
    );
  },

  /** Paid / due / refunded totals plus a per-month series. */
  crmRevenue(from?: string | null, to?: string | null): Promise<ApiResult<CrmRevenue | null>> {
    const fallbackFrom = from ?? '';
    const fallbackTo = to ?? '';
    return fallback<CrmRevenue | null>(
      async () =>
        normalizeCrmRevenue(await request<unknown>(`/crm/revenue${crmQuery({ from, to })}`)),
      () => emptyCrmRevenue(fallbackFrom, fallbackTo),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * The premium layer: deterministic, evidence-bearing roster flags from the
   * evaluation matrix and the roster aggregates. No LLM is called on the client.
   */
  crmInsights(): Promise<ApiResult<CrmInsights | null>> {
    return fallback<CrmInsights | null>(
      async () => normalizeCrmInsights(await request<unknown>('/crm/insights')),
      () => emptyCrmInsights(),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /* ---------------------- the founder's admin surface ------------------- */

  /**
   * The platform at a glance. `requireApi` is deliberate: an admin screen has
   * no demo copy, so a transport failure is an honest error, never zeros.
   */
  adminSummary(): Promise<ApiResult<AdminSummary | null>> {
    return fallback<AdminSummary | null>(
      async () => normalizeAdminSummary(await request<unknown>('/admin/summary')),
      () => null,
      { requireApi: true, empty: null },
    );
  },

  /** Search and paginate accounts. 403 for a non-admin, 422 for a bad cursor. */
  adminAccounts(query: AdminAccountQuery = {}): Promise<ApiResult<AdminAccountList | null>> {
    return fallback<AdminAccountList | null>(
      async () =>
        normalizeAdminAccountList(
          await request<unknown>(
            `/admin/accounts${crmQuery({
              role: query.role ?? null,
              q: query.q ?? null,
              premium:
                query.premium === null || query.premium === undefined
                  ? null
                  : query.premium
                    ? 'true'
                    : 'false',
              linked:
                query.linked === null || query.linked === undefined
                  ? null
                  : query.linked
                    ? 'true'
                    : 'false',
              limit: query.limit === null || query.limit === undefined ? null : String(query.limit),
              cursor: query.cursor ?? null,
            })}`,
          ),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 422] },
    );
  },

  /** One account's aggregate detail — counts, never contents. */
  adminAccount(userId: string): Promise<ApiResult<AdminAccountDetail | null>> {
    return fallback<AdminAccountDetail | null>(
      async () =>
        normalizeAdminAccountDetail(
          await request<unknown>(`/admin/accounts/${encodeURIComponent(userId)}`),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404] },
    );
  },

  /** The CRM side of the platform: plan, seats, clients, slots, money. */
  adminBusinesses(): Promise<ApiResult<AdminBusinessList | null>> {
    return fallback<AdminBusinessList | null>(
      async () => normalizeAdminBusinessList(await request<unknown>('/admin/businesses')),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [403] },
    );
  },

  /** The platform ledger story, by period. */
  adminRevenue(from?: string | null, to?: string | null): Promise<ApiResult<AdminRevenue | null>> {
    return fallback<AdminRevenue | null>(
      async () =>
        normalizeAdminRevenue(await request<unknown>(`/admin/revenue${crmQuery({ from, to })}`)),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 422] },
    );
  },

  /**
   * The one deliberate write. The API validates every change, refuses the last
   * admin with 409 and records the before/after in the audit trail.
   */
  adminPatchAccount(
    userId: string,
    patch: AdminAccountPatch,
  ): Promise<ApiResult<AdminUpdateAccountResult | null>> {
    return fallback<AdminUpdateAccountResult | null>(
      async () =>
        normalizeAdminUpdateResult(
          await request<unknown>(`/admin/accounts/${encodeURIComponent(userId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  /** The append-only audit trail, newest first. */
  adminAudit(limit = 50, cursor?: string | null): Promise<ApiResult<AdminAuditList | null>> {
    return fallback<AdminAuditList | null>(
      async () =>
        normalizeAdminAuditList(
          await request<unknown>(
            `/admin/audit${crmQuery({ limit: String(limit), cursor: cursor ?? null })}`,
          ),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 422] },
    );
  },

  /* ---- phase 6: pricing as data, offers, invitations, system health ---- */

  /** The whole plan catalog, retired rows included. */
  adminPlans(): Promise<ApiResult<PlanCatalogList | null>> {
    return fallback<PlanCatalogList | null>(
      async () => normalizePlanCatalogList(await request<unknown>('/admin/plans')),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [403] },
    );
  },

  /** Create a plan. 409 on a duplicate key, 422 on a bad body. */
  adminCreatePlan(input: AdminPlanCreate): Promise<ApiResult<AdminPlanWriteResult | null>> {
    return fallback<AdminPlanWriteResult | null>(
      async () =>
        normalizePlanWriteResult(
          await request<unknown>('/admin/plans', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  /** Edit a plan's price, seats, name or active state — every change audited. */
  adminUpdatePlan(
    planId: string,
    patch: AdminPlanPatch,
  ): Promise<ApiResult<AdminPlanWriteResult | null>> {
    return fallback<AdminPlanWriteResult | null>(
      async () =>
        normalizePlanWriteResult(
          await request<unknown>(`/admin/plans/${encodeURIComponent(planId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  adminOffers(): Promise<ApiResult<OfferList | null>> {
    return fallback<OfferList | null>(
      async () => normalizeOfferList(await request<unknown>('/admin/offers')),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [403] },
    );
  },

  adminCreateOffer(input: AdminOfferCreate): Promise<ApiResult<AdminOfferWriteResult | null>> {
    return fallback<AdminOfferWriteResult | null>(
      async () =>
        normalizeOfferWriteResult(
          await request<unknown>('/admin/offers', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  adminUpdateOffer(
    offerId: string,
    patch: AdminOfferPatch,
  ): Promise<ApiResult<AdminOfferWriteResult | null>> {
    return fallback<AdminOfferWriteResult | null>(
      async () =>
        normalizeOfferWriteResult(
          await request<unknown>(`/admin/offers/${encodeURIComponent(offerId)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  adminInvitations(): Promise<ApiResult<AdminInvitationList | null>> {
    return fallback<AdminInvitationList | null>(
      async () => normalizeInvitationList(await request<unknown>('/admin/invitations')),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [403] },
    );
  },

  /** Mint an invitation and get back the shareable link. */
  adminCreateInvitation(
    input: AdminInvitationCreate,
  ): Promise<ApiResult<AdminInvitationCreateResult | null>> {
    return fallback<AdminInvitationCreateResult | null>(
      async () =>
        normalizeInvitationCreateResult(
          await request<unknown>('/admin/invitations', {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  /** Revoke a pending invitation. 409 when it is already accepted/revoked. */
  adminRevokeInvitation(
    invitationId: string,
  ): Promise<ApiResult<AdminInvitationRevokeResult | null>> {
    return fallback<AdminInvitationRevokeResult | null>(
      async () =>
        normalizeInvitationRevokeResult(
          await request<unknown>(`/admin/invitations/${encodeURIComponent(invitationId)}`, {
            method: 'DELETE',
          }),
        ),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [400, 403, 404, 409, 422] },
    );
  },

  /** The read-only system health panel. */
  adminSystem(): Promise<ApiResult<AdminSystem | null>> {
    return fallback<AdminSystem | null>(
      async () => normalizeAdminSystem(await request<unknown>('/admin/system')),
      () => null,
      { requireApi: true, empty: null, surfaceStatuses: [403] },
    );
  },

  /**
   * Redeem an invitation — public, so it must NOT require an admin session.
   * Reuses the registration path: a new profile is created and signed in.
   */
  acceptInvitation(
    code: string,
    input: { email: string; password: string; displayName: string },
  ): Promise<ApiResult<InvitationAcceptResult | null>> {
    return fallback<InvitationAcceptResult | null>(
      async () =>
        normalizeInvitationAcceptResult(
          await request<unknown>(`/auth/invitations/${encodeURIComponent(code)}/accept`, {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      () => null,
      { allowApiError: true, empty: null },
    );
  },

  programme(): Promise<ApiResult<GeneratedPlan>> {
    return fallback(
      () => request<GeneratedPlan>('/programme'),
      () => demoProgramme(),
    );
  },

  generatePlan(intake: PlanIntake): Promise<ApiResult<GeneratedPlan>> {
    return fallback(
      () => request<GeneratedPlan>('/programme/one-time', { method: 'POST', body: JSON.stringify(intake) }),
      () => demoGeneratedPlan(intake),
    );
  },

  sessions(limit = 20): Promise<ApiResult<SessionSummary[]>> {
    return fallback(
      () => request<SessionSummary[]>(`/sessions?limit=${limit}`),
      () => demoSessions(),
    );
  },

  logSet(sessionId: string, set: LogSetInput): Promise<ApiResult<LogSetResult>> {
    return fallback(
      () =>
        request<LogSetResult>(`/sessions/${encodeURIComponent(sessionId)}/sets`, {
          method: 'POST',
          body: JSON.stringify(set),
        }),
      () => demoLogSet(set),
    );
  },

  stats(): Promise<ApiResult<StatsSummary>> {
    return fallback(
      () => request<StatsSummary>('/stats/summary', {}, { retryOnTimeout: true }),
      () => demoStats(),
      { requireRealAccountData: true },
    );
  },

  /* ----------------------------- blocks & goals ------------------------- */

  /**
   * The block summary is a data-heavy read of the user's own training. On a real
   * account a transport failure must never show the built-in demo block in the
   * user's slots: it resolves to `null` + a `problem` so the screen can say so
   * and offer a Retry. Demo data answers only for the demo persona.
   */
  blocksCurrent(): Promise<ApiResult<CurrentBlock>> {
    return fallback(
      () => request<CurrentBlock>('/blocks/current', {}, { retryOnTimeout: true }),
      () => demoBlocksCurrent(),
      { requireRealAccountData: true },
    );
  },

  blocksHistory(limit = 6): Promise<ApiResult<BlockHistoryPayload>> {
    return fallback(
      () =>
        request<BlockHistoryPayload>(`/blocks/history?limit=${limit}`, {}, { retryOnTimeout: true }),
      () => demoBlocksHistory(limit),
      { requireRealAccountData: true },
    );
  },

  /**
   * A 409 is the documented "5 active goals in this block" cap: it must reach
   * the screen as a friendly message, never be hidden by the demo fallback.
   * Validation problems are surfaced the same way; anything else (an endpoint
   * that is not deployed yet, a dropped connection) resolves to demo data.
   */
  createGoal(input: GoalInput): Promise<ApiResult<Goal | null>> {
    return fallback(
      () => request<Goal>('/goals', { method: 'POST', body: JSON.stringify(input) }),
      () => demoCreateGoal(input),
      { empty: null, surfaceStatuses: [409, 400, 422] },
    );
  },

  updateGoal(goalId: string, patch: GoalPatch): Promise<ApiResult<Goal | null>> {
    return fallback(
      () => request<Goal>(`/goals/${encodeURIComponent(goalId)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      () => demoUpdateGoal(goalId, patch),
      { empty: null, surfaceStatuses: [400, 409, 422] },
    );
  },

  deleteGoal(goalId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/goals/${encodeURIComponent(goalId)}`, { method: 'DELETE' });
        return true;
      },
      () => demoDeleteGoal(goalId),
    );
  },

  /** The most recent logged sets (movement, weight, reps, RPE) for the Grid panel. */
  recentSets(limit = 6): Promise<ApiResult<RecentSet[]>> {
    return fallback(
      async () => {
        const page = await request<SessionListPageWire>('/sessions?limit=3', {}, { retryOnTimeout: true });
        const sessions = Array.isArray(page) ? page : page.data;
        // The detail reads are independent; fetch them together rather than
        // walking three sessions one round trip at a time.
        const details = await Promise.all(
          sessions.map(async (session) => ({
            session,
            detail: await request<SessionDetailWire>(
              `/sessions/${encodeURIComponent(session.sessionId)}`,
              {},
              { retryOnTimeout: true },
            ),
          })),
        );
        const sets: RecentSet[] = [];
        for (const { session, detail } of details) {
          if (sets.length >= limit) break;
          for (const exercise of detail.sessionExercises) {
            for (const set of exercise.loggedSets) {
              if (set.isWarmup) continue;
              sets.push({
                id: set.id,
                movement: exercise.exerciseName,
                variant: null,
                weightKg: typeof set.weightKg === 'number' ? set.weightKg : null,
                reps: typeof set.reps === 'number' ? set.reps : null,
                rpe: typeof set.rpe === 'number' ? set.rpe : null,
                performedAt: set.performedAt,
                sessionLabel: session.label ?? 'Session',
              });
            }
          }
        }
        return sets.sort((a, b) => Date.parse(b.performedAt) - Date.parse(a.performedAt)).slice(0, limit);
      },
      () => demoRecentSets(limit),
      { requireRealAccountData: true },
    );
  },

  /** Exercise options for the `exercise_1rm` goal picker. */
  exercises(): Promise<ApiResult<ExerciseOption[]>> {
    return fallback(
      async () => {
        const page = await request<{ data: ExerciseOption[] }>('/exercises?limit=200');
        return Array.isArray(page) ? page : page.data;
      },
      () => demoExercises(),
    );
  },

  /* --------------------------- programme authoring ---------------------- */

  /** The active plan (or `planId`) plus one flattened row per prescription. */
  programmeOverview(planId?: string | null, clientId?: string | null): Promise<ApiResult<ProgrammeOverview>> {
    const query = planId ? `?planId=${encodeURIComponent(planId)}` : '';
    return fallback(
      async () =>
        normalizeProgrammeOverview(await request<unknown>(withClientId(`/programme${query}`, clientId))),
      () => demoProgrammeOverview(planId ?? null),
    );
  },

  createProgramme(draft: ProgrammeDraft, clientId?: string | null): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(withClientId('/programme', clientId), {
            method: 'POST',
            body: JSON.stringify(draft),
          }),
        ),
      () => demoCreateProgramme(draft),
    );
  },

  /**
   * Create the block's one warm-up session (0019). The movements are then added
   * through `addProgrammeExercises` with the warm-up's `dayId`, so there is one
   * implementation of "add a movement to a session" for days and warm-ups alike.
   */
  createProgrammeWarmup(
    planId: string,
    input: { label?: string; focus?: string[] } = {},
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme/${encodeURIComponent(planId)}/warmup`, clientId),
            { method: 'POST', body: JSON.stringify(input) },
          ),
        ),
      () => demoCreateProgrammeWarmup(planId, input),
    );
  },

  /** Remove the block's warm-up session; the block is otherwise unchanged. */
  deleteProgrammeWarmup(
    planId: string,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () => {
        await request<void>(
          withClientId(`/programme/${encodeURIComponent(planId)}/warmup`, clientId),
          { method: 'DELETE' },
        );
        return normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme?planId=${encodeURIComponent(planId)}`, clientId),
          ),
        );
      },
      () => demoDeleteProgrammeWarmup(planId),
    );
  },

  patchProgrammePlan(
    planId: string,
    patch: ProgrammePlanPatch,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(withClientId(`/programme/${encodeURIComponent(planId)}`, clientId), {
            method: 'PATCH',
            body: JSON.stringify(patch),
          }),
        ),
      () => demoPatchProgramme(planId, patch),
    );
  },

  addProgrammeDay(
    planId: string,
    input: { label: string; focus?: string[]; index?: number },
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeMutationResult>> {
    return fallback(
      async () => {
        const raw = await request<unknown>(withClientId(`/programme/${encodeURIComponent(planId)}/days`, clientId), {
          method: 'POST',
          body: JSON.stringify(input),
        });
        const record = asRecord(raw);
        const nested = asRecord(record.day);
        const dayId =
          asString(nested.dayId) ?? asString(nested.id) ?? (asString(record.dayId) ?? asString(record.id));
        return { overview: normalizeProgrammeOverview(raw), dayId };
      },
      () => demoAddProgrammeDay(planId, input),
    );
  },

  patchProgrammeDay(
    planId: string,
    dayId: string,
    patch: ProgrammeDayPatch,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme/${encodeURIComponent(planId)}/days/${encodeURIComponent(dayId)}`, clientId),
            { method: 'PATCH', body: JSON.stringify(patch) },
          ),
        ),
      () => demoPatchProgrammeDay(planId, dayId, patch),
    );
  },

  deleteProgrammeDay(
    planId: string,
    dayId: string,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () => {
        await request<void>(
          withClientId(`/programme/${encodeURIComponent(planId)}/days/${encodeURIComponent(dayId)}`, clientId),
          { method: 'DELETE' },
        );
        return normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme?planId=${encodeURIComponent(planId)}`, clientId),
          ),
        );
      },
      () => demoDeleteProgrammeDay(planId, dayId),
    );
  },

  addProgrammeExercises(
    planId: string,
    dayId: string,
    input: ProgrammeExerciseInput | ProgrammeExerciseInput[],
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(
              `/programme/${encodeURIComponent(planId)}/days/${encodeURIComponent(dayId)}/exercises`,
              clientId,
            ),
            { method: 'POST', body: JSON.stringify(input) },
          ),
        ),
      () => demoAddProgrammeExercises(planId, dayId, input),
    );
  },

  patchProgrammeExercise(
    planId: string,
    planExerciseId: string,
    patch: ProgrammeExercisePatch,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () => {
        await request<unknown>(
          withClientId(`/programme/exercises/${encodeURIComponent(planExerciseId)}`, clientId),
          { method: 'PATCH', body: JSON.stringify(patch) },
        );
        return normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme?planId=${encodeURIComponent(planId)}`, clientId),
          ),
        );
      },
      () => demoPatchProgrammeExercise(planExerciseId, patch),
    );
  },

  deleteProgrammeExercise(
    planId: string,
    planExerciseId: string,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () => {
        await request<void>(
          withClientId(`/programme/exercises/${encodeURIComponent(planExerciseId)}`, clientId),
          { method: 'DELETE' },
        );
        return normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme?planId=${encodeURIComponent(planId)}`, clientId),
          ),
        );
      },
      () => demoDeleteProgrammeExercise(planExerciseId),
    );
  },

  reorderProgramme(
    planId: string,
    body: { dayIds?: string[]; planExerciseIds?: string[] },
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(withClientId(`/programme/${encodeURIComponent(planId)}/reorder`, clientId), {
            method: 'POST',
            body: JSON.stringify(body),
          }),
        ),
      () => demoReorderProgramme(planId, body),
    );
  },

  /** Group / ungroup exercises of one day (superset or drop set), one transaction. */
  groupProgrammeExercises(
    planId: string,
    dayId: string,
    input: GroupProgrammeInput,
    clientId?: string | null,
  ): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () =>
        normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(
              `/programme/${encodeURIComponent(planId)}/days/${encodeURIComponent(dayId)}/group`,
              clientId,
            ),
            { method: 'PATCH', body: JSON.stringify(input) },
          ),
        ),
      () => demoGroupProgrammeExercises(planId, dayId, input),
    );
  },

  activateProgramme(planId: string, clientId?: string | null): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () => {
        await request<unknown>(withClientId(`/programme/${encodeURIComponent(planId)}/activate`, clientId), {
          method: 'POST',
        });
        return normalizeProgrammeOverview(
          await request<unknown>(
            withClientId(`/programme?planId=${encodeURIComponent(planId)}`, clientId),
          ),
        );
      },
      () => demoActivateProgramme(planId),
      // A real account must never be told "nothing was saved" about a change it
      // actually asked for: surface the API's own refusal instead of a demo swap.
      { surfaceForRealAccount: true, empty: null as unknown as ProgrammeOverview },
    );
  },

  /**
   * Stop the running block without replacing it ("nothing runs for now"). The
   * block stays in All Programmes to start again; a lifter may stop a
   * coach-owned block without editing it (choosing what runs is their call).
   */
  deactivateProgramme(planId: string, clientId?: string | null): Promise<ApiResult<ProgrammeOverview>> {
    return fallback(
      async () => {
        await request<unknown>(
          withClientId(`/programme/${encodeURIComponent(planId)}/deactivate`, clientId),
          { method: 'POST' },
        );
        return normalizeProgrammeOverview(
          await request<unknown>(withClientId('/programme', clientId)),
        );
      },
      () => demoProgrammeOverview(null),
      { surfaceForRealAccount: true, empty: null as unknown as ProgrammeOverview },
    );
  },

  /**
   * Delete an inactive block. The API refuses the active one (409) — deactivate
   * first — and a coach-owned block is the coach's to delete, never its lifter's.
   */
  deleteProgrammePlan(planId: string, clientId?: string | null): Promise<ApiResult<null>> {
    return fallback(
      async () => {
        await request<void>(withClientId(`/programme/${encodeURIComponent(planId)}`, clientId), {
          method: 'DELETE',
        });
        return null;
      },
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * Every block of the caller (or, with `clientId`, of a linked client): dates,
   * counts and lineage. A real account is never shown the demo list as its own.
   */
  programmePlans(clientId?: string): Promise<ApiResult<PlanHistoryEntry[]>> {
    return fallback(
      async () =>
        normalizePlanHistory(await request<unknown>(withClientId('/programme/plans', clientId))),
      () => demoProgrammePlans(),
    );
  },

  /* ------------------------------ live sessions ------------------------- */

  activeSession(): Promise<ApiResult<ActiveSessionPayload>> {
    return fallback(
      async () => normalizeActiveSession(await request<unknown>('/sessions/active')),
      () => demoActiveSessionPayload(),
    );
  },

  /**
   * Start a session. A real account must see the API's own reason when the
   * create is rejected (an invalid `planDayId`, an expired token, …) rather than
   * a transport-failure message: the screen then re-reads `/sessions/active` so
   * a session the server did create is never hidden until a tab switch.
   */
  startSession(input: StartSessionInput = {}): Promise<ApiResult<ActiveSessionPayload | null>> {
    return fallback<ActiveSessionPayload | null>(
      async () =>
        normalizeActiveSession(
          await request<unknown>('/sessions', { method: 'POST', body: JSON.stringify(input) }),
        ),
      () => demoStartSession(input),
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * The account's recent sessions, totals-only, newest first. Used to find the
   * most recent completed session so the Grid can show a Previous column.
   */
  sessionHistory(limit = 10): Promise<ApiResult<SessionTotalsWire[]>> {
    return fallback(
      async () => {
        const page = await request<unknown>(`/sessions?limit=${limit}`);
        const list = Array.isArray(page) ? page : asArray<unknown>(asRecord(page).data);
        return list
          .map((entry) => normalizeSessionTotals(entry))
          .filter((entry): entry is SessionTotalsWire => entry !== null);
      },
      () => [],
    );
  },

  /** One session's exercises and logged sets (the Grid's Previous column). */
  sessionDetail(sessionId: string): Promise<ApiResult<SessionExercise[]>> {
    return fallback(
      async () => {
        const raw = await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}`);
        return asArray<unknown>(asRecord(raw).sessionExercises).map(normalizeSessionExercise);
      },
      () => [],
    );
  },

  /**
   * A completed session read as a table: header facts, the prescription beside
   * every logged set, the server's compliance summary and — only when a coach
   * passes the client's id — the anomaly flags. An own-data read returns no
   * `flags` key at all, so the coach layer cannot leak onto the lifter's screen.
   */
  sessionView(sessionId: string, clientId?: string | null): Promise<ApiResult<SessionViewWire | null>> {
    return fallback<SessionViewWire | null>(
      async () =>
        normalizeSessionView(
          await request<unknown>(
            `/sessions/${encodeURIComponent(sessionId)}${withClientId('', clientId)}`,
            {},
            { retryOnTimeout: true },
          ),
        ),
      () => null,
      { surfaceForRealAccount: true, empty: null },
    );
  },

  /**
   * A rejected set is a validation problem the lifter must be able to read: the
   * API's own `detail` (e.g. `tempo: Invalid`) is surfaced rather than hidden
   * behind the demo fallback, so a failed write is diagnosable in one look.
   * `data` is null exactly when the API answered with a problem.
   */
  logSessionSet(sessionId: string, input: LogSessionSetInput): Promise<ApiResult<SessionSetResponse | null>> {
    const path = `/sessions/${encodeURIComponent(sessionId)}/sets`;
    const body = JSON.stringify(input);
    const clientSetId = typeof input.clientSetId === 'string' && input.clientSetId.length > 0 ? input.clientSetId : null;
    return writeSet<SessionSetResponse | null>({
      path,
      method: 'POST',
      body,
      clientSetId,
      key: clientSetId ?? `${path}:${body}`,
      label: `Set ${input.setIndex}`,
      run: async () => normalizeSessionSetResponse(await request<unknown>(path, { method: 'POST', body })),
      demo: () => demoLogSessionSet(sessionId, input),
    });
  },

  patchSessionSet(setId: string, patch: Partial<LogSessionSetInput>): Promise<ApiResult<SessionSetResponse | null>> {
    const path = `/sets/${encodeURIComponent(setId)}`;
    const body = JSON.stringify(patch);
    return writeSet<SessionSetResponse | null>({
      path,
      method: 'PATCH',
      body,
      clientSetId: null,
      key: `patch:${setId}`,
      label: 'Set update',
      run: async () => normalizeSessionSetResponse(await request<unknown>(path, { method: 'PATCH', body })),
      demo: () => demoPatchSessionSet(setId, patch),
    });
  },

  deleteSessionSet(setId: string): Promise<ApiResult<boolean>> {
    return fallback(
      async () => {
        await request<void>(`/sets/${encodeURIComponent(setId)}`, { method: 'DELETE' });
        return true;
      },
      () => demoDeleteSessionSet(setId),
    );
  },

  completeSession(
    sessionId: string,
    input: { perceivedExertion?: number | null; moodScore?: number | null; notes?: string | null } = {},
  ): Promise<ApiResult<CompleteSessionResponse>> {
    return fallback(
      async () =>
        normalizeCompleteSession(
          await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}/complete`, {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      () => demoCompleteSession(sessionId, input),
    );
  },

  /**
   * Abandon a session. The backend workstream deployed
   * `POST /sessions/:id/abandon` (200, idempotent, 409 once completed); this
   * calls it first and still probes `DELETE /sessions/:id` if that route is ever
   * absent, so a build against an older API degrades honestly instead of
   * pretending the session is gone. Abandoning closes the session and keeps its
   * logged sets in history — the API excludes them from aggregates by status.
   */
  async abandonSession(sessionId: string): Promise<AbandonSessionResult> {
    const id = encodeURIComponent(sessionId);
    const notFound = (error: unknown): boolean =>
      error instanceof ApiError && (error.problem.status === 404 || error.problem.status === 405);

    try {
      const raw = await request<unknown>(`/sessions/${id}/abandon`, { method: 'POST', body: '{}' });
      reportSource('api');
      const status = asString(asRecord(asRecord(raw).session).status) ?? asString(asRecord(raw).status);
      return {
        abandoned: true,
        supported: true,
        detail:
          status === 'abandoned'
            ? 'Session abandoned. Its logged sets stay in your history but stop counting toward volume, streaks and PRs.'
            : 'Session discarded.',
      };
    } catch (error) {
      if (!(error instanceof ApiError)) {
        return {
          abandoned: false,
          supported: false,
          detail: 'The API could not be reached, so the session was NOT discarded.',
        };
      }
      if (!notFound(error)) {
        return {
          abandoned: false,
          supported: true,
          detail: error.problem.detail ?? error.problem.title,
        };
      }
    }

    try {
      await request<void>(`/sessions/${id}`, { method: 'DELETE' });
      reportSource('api');
      return { abandoned: true, supported: true, detail: 'Session discarded.' };
    } catch (error) {
      if (error instanceof ApiError && !notFound(error)) {
        return {
          abandoned: false,
          supported: true,
          detail: error.problem.detail ?? error.problem.title,
        };
      }
      return {
        abandoned: false,
        supported: false,
        detail:
          'This API build has no abandon endpoint yet (POST /sessions/:id/abandon and DELETE /sessions/:id both answered 404), so the session is still in progress.',
      };
    }
  },

  /**
   * Add a movement to a running session. The written contract has start-from-day
   * and log-a-set but no add-exercise step; this calls the natural endpoint and
   * the screen tells the truth when the API does not serve it yet.
   */
  addSessionExercise(sessionId: string, input: AddSessionExerciseInput): Promise<ApiResult<ActiveSessionPayload>> {
    return fallback(
      async () =>
        normalizeActiveSession(
          await request<unknown>(`/sessions/${encodeURIComponent(sessionId)}/exercises`, {
            method: 'POST',
            body: JSON.stringify(input),
          }),
        ),
      () => demoAddSessionExercise(sessionId, input),
    );
  },
};

export type ApiClient = typeof api;
